import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from './api';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import EmailManagement from './components/EmailManagement';
import AdminSmartPro from './components/AdminSmartPro';
import SyncDiagnosticsTab from './components/SyncDiagnosticsTab';
import AWSMonitoringTab from './components/AWSMonitoringTab';

const PlatformAdminDashboard = ({ user, onImpersonate, onExitAdmin }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [hoas, setHOAs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [selectedHOA, setSelectedHOA] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [sendPasswordsModal, setSendPasswordsModal] = useState(null); // Store HOA data for modal
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailData, setBulkEmailData] = useState({
    subject: '',
    message: '',
    target: 'all' // 'all', 'homeowners', 'board_members'
  });
  const [showConfigModal, setShowConfigModal] = useState(false);

  useEffect(() => {
    fetchHOAs();
    fetchEmployees();
    if (activeTab === 'quotes') {
      fetchQuotes();
    }
  }, [activeTab]);

  const fetchHOAs = async () => {
    try {
      const response = await axios.get('/api/platform-admin/hoas');
      setHOAs(response.data.hoas);
    } catch (error) {
      console.error('Failed to fetch HOAs:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/api/platform-admin/employees');
      setEmployees(response.data.employees);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get('/api/platform-admin/quotes');
      setQuotes(response.data.quotes || []);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      setQuotes([]);
    }
  };

  const updateEmployeePermissions = async (employeeId, permissions) => {
    try {
      console.log('🔧 Updating permissions for employee:', employeeId);
      console.log('🔧 New permissions:', permissions);
      
      const response = await axios.put(`/api/platform-admin/employees/${employeeId}/permissions`, {
        permissions: permissions
      });
      
      console.log('✅ Update successful:', response.data);
      setEditingEmployee(null);
      fetchEmployees();
      alert('✅ Permissions updated successfully!');
    } catch (error) {
      console.error('❌ Failed to update permissions:', error);
      console.error('❌ Error response:', error.response?.data);
      alert(`❌ Failed to update permissions: ${error.response?.data?.detail || error.message}`);
    }
  };

  const executeSendPasswords = async (hoaId, action, newEmail = null) => {
    setLoading(true);
    try {
      let response;
      
      if (action === 'reset_board_members') {
        // Reset all board member passwords
        response = await axios.post(`/api/platform-admin/hoa/${hoaId}/send-passwords`);
      } else if (action === 'reset_main_admin') {
        // Reset the main HOA admin password (we'll need to add this endpoint)
        response = await axios.post(`/api/platform-admin/hoa/${hoaId}/reset-admin-password`);
      } else if (action === 'change_email') {
        // Change the setup email (we'll need to add this endpoint)
        response = await axios.put(`/api/platform-admin/hoa/${hoaId}/contact-email`, {
          new_email: newEmail
        });
      } else if (action === 'set_main_admin') {
        // Set/change the main admin
        response = await axios.put(`/api/platform-admin/hoa/${hoaId}/set-main-admin`, {
          admin_email: newEmail
        });
      }
      
      if (response && response.data.success) {
        let message = '';
        
        if (action === 'reset_board_members') {
          const { hoa_name, board_member_credentials } = response.data;
          message = `✅ Generated new passwords for ${board_member_credentials.length} board members\n\n`;
          message += `HOA: ${hoa_name}\n\n`;
          message += `New Board Member Credentials:\n`;
          message += `${'='.repeat(40)}\n`;
          
          board_member_credentials.forEach((cred, index) => {
            message += `${index + 1}. ${cred.name} (${cred.role})\n`;
            message += `   Email: ${cred.email}\n`;
            message += `   Password: ${cred.password}\n\n`;
          });
          
          message += `⚠️ IMPORTANT: Share these credentials securely. These are temporary passwords.`;
        } else if (action === 'reset_main_admin') {
          message = `✅ Main admin password reset successfully\n\n`;
          message += `Email: ${response.data.email}\n`;
          message += `New Password: ${response.data.password}\n\n`;
          message += `⚠️ IMPORTANT: Share this credential securely.`;
        } else if (action === 'change_email') {
          message = `✅ Contact email updated successfully\n\n`;
          message += `Old Email: ${response.data.old_email}\n`;
          message += `New Email: ${response.data.new_email}`;
        } else if (action === 'set_main_admin') {
          message = `✅ Main admin updated successfully\n\n`;
          message += `HOA: ${response.data.hoa_name}\n`;
          message += `New Admin: ${response.data.admin_email}\n`;
          message += `Status: ${response.data.status === 'existing_user_linked' ? 'Existing user linked to HOA' : 'Contact email updated'}\n\n`;
          message += `${response.data.note}`;
        }
        
        alert(message);
        
        // Copy to clipboard for easy sharing
        if (navigator.clipboard && (action === 'reset_board_members' || action === 'reset_main_admin')) {
          try {
            await navigator.clipboard.writeText(message.replace(/\n/g, '\n'));
          } catch (err) {
            console.log('Could not copy to clipboard:', err);
          }
        }
        
        // Refresh HOA data
        fetchHOAs();
      }
    } catch (error) {
      console.error('Operation failed:', error);
      alert(`❌ Operation failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
      setSendPasswordsModal(null);
    }
  };

  const sendBulkEmail = async () => {
    if (!bulkEmailData.subject || !bulkEmailData.message) {
      alert('Please fill in both subject and message');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/platform-admin/send-bulk-email', bulkEmailData);
      
      alert(`✅ Successfully sent bulk email to ${response.data.sent_count} users`);
      setShowBulkEmailModal(false);
      setBulkEmailData({ subject: '', message: '', target: 'all' });
    } catch (error) {
      console.error('Failed to send bulk email:', error);
      alert(`❌ Failed to send bulk email: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊', permission: null }, // Always visible
    { id: 'hoas', name: 'HOA Management', icon: '🏘️', permission: 'hoa_management' },
    { id: 'hoa_creation_logs', name: 'HOA Creation Logs', icon: '📝', permission: null, adminOnly: true },
    { id: 'support_tickets', name: 'Support Tickets', icon: '🎫', permission: null, adminOnly: true },
    { id: 'document_removal', name: 'Document Removal Requests', icon: '🗑️', permission: null, adminOnly: true },
    { id: 'quotes', name: 'Customer Quotes', icon: '📋', permission: 'customer_quotes' },
    { id: 'employees', name: 'Team Management', icon: '👥', permission: 'employee_management', adminOnly: true },
    { id: 'emails', name: 'Emails', icon: '📧', permission: null, adminOnly: true },
    { id: 'event_settings', name: 'Event & Email Settings', icon: '🎫', permission: null, adminOnly: true },
    { id: 'payment_tracker', name: 'Payment Tracker', icon: '🔍', permission: null, adminOnly: true },
    { id: 'login_tracking', name: 'Login Tracking', icon: '🔐', permission: null, adminOnly: true }, // New tab for login/registration tracking
    { id: 'profile_styles', name: 'Profile Styles', icon: '🎨', permission: null, adminOnly: true }, // New tab for profile styling
    { id: 'registration', name: 'Registration Codes', icon: '🔑', permission: 'registration_management' },
    { id: 'discounts', name: 'Discount Codes', icon: '🎟️', permission: 'discount_management' },
    { id: 'marketing', name: 'Marketing', icon: '🎨', permission: 'marketing_management' },
    { id: 'impersonation', name: 'View Toggle', icon: '👤', permission: 'impersonation' },
    { id: 'activity', name: 'Activity & Audit', icon: '📋', permission: 'employee_management' },
    { id: 'smart_pro_admin', name: 'HOA Smart Pro Admin', icon: '🧠', permission: null, adminOnly: true },
    { id: 'evoting_audit', name: 'E-Voting Audit', icon: '🗳️', permission: null, adminOnly: true },
    { id: 'settings', name: 'Settings', icon: '⚙️', permission: null }, // Always visible
  ];

  // Filter tabs based on user role and permissions
  const getVisibleTabs = () => {
    console.log('🔍 Filtering tabs for user:', user); // Debug log
    console.log('🔍 User permissions:', user?.permissions); // Debug log
    
    // Wait for user data to be fully loaded
    if (!user || !user.role) {
      console.log('⏳ User data not loaded yet, showing only overview');
      return [tabs[0]]; // Show only overview while loading
    }
    
    if (user?.role === 'platform_admin') {
      // Platform admin sees all tabs
      console.log('👑 Platform admin - showing all tabs');
      return tabs;
    } else if (user?.role === 'platform_employee') {
      // Employees see tabs based on their permissions
      console.log('👤 Platform employee - filtering by permissions');
      const userPermissions = user?.permissions || [];
      console.log('🔑 Available permissions:', userPermissions);
      
      const filteredTabs = tabs.filter(tab => {
        // Skip admin-only tabs for employees
        if (tab.adminOnly) {
          console.log(`🚫 Skipping admin-only tab: ${tab.name}`);
          return false;
        }
        
        // Show overview always
        if (tab.permission === null) {
          console.log(`✅ Always show: ${tab.name}`);
          return true;
        }
        
        // Check if employee has the required permission
        const hasPermission = userPermissions.includes(tab.permission);
        console.log(`${hasPermission ? '✅' : '❌'} ${tab.name} (requires ${tab.permission}): ${hasPermission}`);
        return hasPermission;
      });
      
      console.log('📋 Final visible tabs:', filteredTabs.map(t => t.name));
      return filteredTabs;
    }
    
    console.log('❓ Unknown user role, showing only overview');
    return [tabs[0]]; // Fallback to overview only
  };

  const visibleTabs = getVisibleTabs();

  // Ensure activeTab is valid for the user's permissions
  React.useEffect(() => {
    if (!visibleTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || 'overview');
    }
  }, [visibleTabs, activeTab]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow border-b-4 border-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                🛠️ {user?.role === 'platform_admin' ? 'Platform Admin Dashboard' : 'Platform Dashboard'}
              </h1>
              <p className="text-sm text-gray-600">
                {user?.role === 'platform_admin' ? 'Master Control Panel' : 'Team Dashboard'} - {user?.first_name || 'Loading'} {user?.last_name || ''}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onExitAdmin}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Exit Admin Mode
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Navigation - MOBILE FRIENDLY: No horizontal scroll needed */}
      <nav className="bg-white shadow-sm border-b hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center lg:justify-start space-x-2 lg:space-x-8 py-2">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-3 my-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap rounded-t-md transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Header with Menu */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {visibleTabs.find(tab => tab.id === activeTab)?.name || 'Dashboard'}
        </h2>
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile Navigation Sidebar */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowMobileMenu(false)}></div>
          <div className="fixed right-0 top-0 h-full w-64 bg-white shadow-lg">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Admin Menu</h2>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto" style={{maxHeight: 'calc(100vh - 80px)'}}>
              <div className="space-y-2">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center px-3 py-2 rounded-lg text-left ${
                      activeTab === tab.id
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="mr-3">{tab.icon}</span>
                    <span>{tab.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content - MOBILE OPTIMIZED: Proper padding and overflow handling */}
      <main className="max-w-7xl mx-auto py-3 sm:py-6 px-2 sm:px-6 lg:px-8">
        <div className="sm:px-0">
          {activeTab === 'overview' && <OverviewTab hoas={hoas} employees={employees} setShowBulkEmailModal={setShowBulkEmailModal} />}
          {activeTab === 'hoas' && <HOAManagementTab hoas={hoas} onRefresh={fetchHOAs} setSendPasswordsModal={setSendPasswordsModal} executeSendPasswords={executeSendPasswords} />}
          {activeTab === 'hoa_creation_logs' && <HOACreationLogsTab />}
          {activeTab === 'support_tickets' && <SupportTicketsTab />}
          {activeTab === 'document_removal' && <DocumentRemovalTab />}
          {activeTab === 'quotes' && <QuotesTab quotes={quotes} onRefresh={fetchQuotes} />}
          {activeTab === 'employees' && <EmployeeManagementTab employees={employees} onRefresh={fetchEmployees} setEditingEmployee={setEditingEmployee} />}
          {activeTab === 'emails' && <EmailManagement user={user} />}
          {activeTab === 'event_settings' && <EventEmailSettingsTab />}
          {activeTab === 'payment_tracker' && <PaymentTrackerTab hoas={hoas} />}
          {activeTab === 'login_tracking' && <LoginTrackingTab hoas={hoas} />}
          {activeTab === 'profile_styles' && <ProfileStylesTab hoas={hoas} />}
          {activeTab === 'registration' && <RegistrationCodesTab hoas={hoas} />}
          {activeTab === 'discounts' && <DiscountCodesTab />}
          {activeTab === 'marketing' && <MarketingTab />}
          {activeTab === 'impersonation' && <ImpersonationTab hoas={hoas} onImpersonate={onImpersonate} />}
          {activeTab === 'activity' && <ActivityAuditTab user={user} />}
          {activeTab === 'smart_pro_admin' && <AdminSmartProTab />}

          {activeTab === 'evoting_audit' && <EVotingAuditTab hoas={hoas} />}
          {activeTab === 'settings' && <SettingsTab user={user} />}
          {activeTab === 'analytics' && <AnalyticsTab hoas={hoas} />}
        </div>
      </main>

      {/* Edit Employee Permissions Modal */}
      {editingEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Permissions: {editingEmployee.first_name} {editingEmployee.last_name}
              </h3>
            </div>
            
            <div className="space-y-3 mb-6">
              <p className="text-sm text-gray-600 mb-3">Select permissions for this employee:</p>
              
              {[
                { key: 'hoa_management', label: 'HOA Management', desc: 'Can view HOAs, send passwords, and help with setup' },
                { key: 'customer_quotes', label: 'Customer Quotes', desc: 'Can view and manage customer quotes from HOA setup process' },
                { key: 'registration_management', label: 'Registration Codes', desc: 'Can create and manage registration codes' },
                { key: 'discount_management', label: 'Discount Codes', desc: 'Can create and manage discount codes' },
                { key: 'analytics', label: 'Analytics & Reports', desc: 'Can access platform analytics and reports' },
                { key: 'impersonation', label: 'View Toggle/Impersonation', desc: 'Can switch to different user views for support' },
                { key: 'employee_management', label: 'Team Management', desc: 'Can add/edit/remove other team members (Admin-level permission)' },
                { key: 'hoa_config_management', label: 'HOA Configuration', desc: 'Can manage comprehensive HOA configurations and overrides' }
              ].map((permission) => (
                <label key={permission.key} className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={editingEmployee.permissions?.includes(permission.key) || false}
                    onChange={(e) => {
                      const updatedPermissions = e.target.checked 
                        ? [...(editingEmployee.permissions || []), permission.key]
                        : (editingEmployee.permissions || []).filter(p => p !== permission.key);
                      setEditingEmployee({...editingEmployee, permissions: updatedPermissions});
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">{permission.label}</span>
                    <p className="text-xs text-gray-500">{permission.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setEditingEmployee(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => updateEmployeePermissions(editingEmployee.id, editingEmployee.permissions)}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Update Permissions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Passwords Options Modal */}
      {sendPasswordsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-96 shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Password & Email Management
              </h3>
              <p className="text-sm text-gray-600 mt-2">
                {sendPasswordsModal.name} ({sendPasswordsModal.code})
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Current contact: {sendPasswordsModal.contact_email || 'Not set'}
              </p>
            </div>
            
            <div className="space-y-4 mb-6">
              <button
                onClick={() => executeSendPasswords(sendPasswordsModal.id, 'reset_board_members')}
                disabled={loading}
                className="w-full text-left p-3 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                <div className="font-medium text-gray-900">🔑 Reset All Board Member Passwords</div>
                <div className="text-sm text-gray-600">Generate new temporary passwords for all board members</div>
              </button>
              
              <button
                onClick={() => executeSendPasswords(sendPasswordsModal.id, 'reset_main_admin')}
                disabled={loading}
                className="w-full text-left p-3 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                <div className="font-medium text-gray-900">👤 Reset Main Admin Password</div>
                <div className="text-sm text-gray-600">Reset password for the primary HOA contact</div>
              </button>
              
              <div className="border border-gray-300 rounded-md p-3">
                <div className="font-medium text-gray-900 mb-2">👑 Set/Change Main Admin</div>
                <div className="text-sm text-gray-600 mb-3">Designate who should be the main admin for this HOA</div>
                <input
                  type="email"
                  placeholder="Enter admin email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  id="set-admin-email-input"
                />
                <button
                  onClick={() => {
                    const adminEmail = document.getElementById('set-admin-email-input').value;
                    if (!adminEmail || !adminEmail.includes('@')) {
                      alert('Please enter a valid email address');
                      return;
                    }
                    executeSendPasswords(sendPasswordsModal.id, 'set_main_admin', adminEmail);
                  }}
                  disabled={loading}
                  className="mt-2 w-full px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 disabled:opacity-50"
                >
                  Set as Main Admin
                </button>
              </div>
              
              <div className="border border-gray-300 rounded-md p-3">
                <div className="font-medium text-gray-900 mb-2">📧 Change Contact Email</div>
                <div className="text-sm text-gray-600 mb-3">Update the primary contact email for this HOA</div>
                <input
                  type="email"
                  placeholder="Enter new email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  id="new-email-input"
                />
                <button
                  onClick={() => {
                    const newEmail = document.getElementById('new-email-input').value;
                    if (!newEmail || !newEmail.includes('@')) {
                      alert('Please enter a valid email address');
                      return;
                    }
                    executeSendPasswords(sendPasswordsModal.id, 'change_email', newEmail);
                  }}
                  disabled={loading}
                  className="mt-2 w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Update Email
                </button>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setSendPasswordsModal(null)}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Setup Checklist Guide */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Setup Support Tools</h3>
        </div>
        <div className="px-6 py-4 space-y-6">
          {/* Return to Checklist */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 002 2m-6 9l2 2 4-4" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900 mb-2">HOA Setup Checklist</h4>
              <p className="text-sm text-gray-600 mb-4">
                Access the setup checklist for any HOA to help them complete their community configuration. 
                Perfect for customer support and onboarding assistance.
              </p>
              <button
                onClick={() => {
                  alert('📋 To access an HOA setup checklist:\n\n1. Go to HOA Management\n2. Select the HOA you want to help\n3. Click "Help Setup" to open their checklist\n\nThis allows you to assist with their onboarding process.');
                }}
                className="inline-flex items-center px-4 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 002 2m-6 9l2 2 4-4" />
                </svg>
                📋 Access HOA Checklists
              </button>
            </div>
          </div>

          {/* Setup Guide */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Setup Checklist Guide</h4>
              <p className="text-sm text-gray-600 mb-4">
                Comprehensive guide explaining what each setup checklist item means and how to help clients complete them.
                Perfect for customer support and onboarding assistance.
              </p>
              <button
                onClick={() => setShowSetupGuide(true)}
                className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                📖 View Setup Guide
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Setup Guide Modal */}
      {showSetupGuide && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mb-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-900">📋 HOA Setup Checklist Guide</h3>
                <button
                  onClick={() => setShowSetupGuide(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Complete guide for helping HOA clients understand and complete their setup checklist items.
              </p>
            </div>
            
            <div className="max-h-96 overflow-y-auto space-y-6">
              {/* Basic Information */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">🏘️ Basic Information</h4>
                <div className="space-y-3 text-sm text-gray-700">
                  <div>
                    <strong>HOA Name:</strong> The official name of the homeowners association as it appears in legal documents.
                  </div>
                  <div>
                    <strong>Address:</strong> Physical address of the HOA community or management office.
                  </div>
                  <div>
                    <strong>Total Properties:</strong> Number of homes/units in the community that pay HOA fees.
                  </div>
                  <div>
                    <strong>Contact Email:</strong> Primary email for HOA communications and system notifications.
                  </div>
                </div>
              </div>

              {/* Board Members */}
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">👥 Board Members</h4>
                <div className="space-y-3 text-sm text-gray-700">
                  <div>
                    <strong>Required Roles:</strong> President, Treasurer, Secretary (minimum). Some HOAs also have Vice President, Directors.
                  </div>
                  <div>
                    <strong>What to collect:</strong> Full name, email address, phone number, role/position.
                  </div>
                  <div>
                    <strong>Why it matters:</strong> Board members get admin access to manage the HOA portal, approve residents, handle communications.
                  </div>
                  <div>
                    <strong>Common issues:</strong> 
                    <ul className="list-disc list-inside ml-4 mt-1">
                      <li>Board member doesn't have email - help them create one</li>
                      <li>Shared email addresses - each person needs their own</li>
                      <li>Outgoing board members - make sure current board is listed</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">📄 HOA Documents</h4>
                <div className="space-y-3 text-sm text-gray-700">
                  <div>
                    <strong>CC&Rs (Covenants, Conditions & Restrictions):</strong> The main governing document with rules and regulations.
                  </div>
                  <div>
                    <strong>Bylaws:</strong> How the HOA operates, meeting procedures, board duties.
                  </div>
                  <div>
                    <strong>Budget/Financial:</strong> Annual budget, reserve study, financial statements.
                  </div>
                  <div>
                    <strong>Architectural Guidelines:</strong> Rules for home modifications, landscaping, etc.
                  </div>
                  <div>
                    <strong>File formats:</strong> PDF preferred. Word docs, images also accepted.
                  </div>
                  <div>
                    <strong>Help them find documents:</strong>
                    <ul className="list-disc list-inside ml-4 mt-1">
                      <li>Check with previous management company</li>
                      <li>Look in HOA files/records</li>
                      <li>Contact county recorder's office for CC&Rs</li>
                      <li>Ask long-time residents</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Resident List */}
              <div className="border-l-4 border-orange-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">🏠 Resident Information</h4>
                <div className="space-y-3 text-sm text-gray-700">
                  <div>
                    <strong>What to collect:</strong> Property address, owner name, email, phone (if available).
                  </div>
                  <div>
                    <strong>CSV format:</strong> Address, Owner Name, Email, Phone (one row per property).
                  </div>
                  <div>
                    <strong>Sources for resident data:</strong>
                    <ul className="list-disc list-inside ml-4 mt-1">
                      <li>Previous management company records</li>
                      <li>HOA membership lists</li>
                      <li>County property records (for addresses/owners)</li>
                      <li>Annual meeting sign-in sheets</li>
                    </ul>
                  </div>
                  <div>
                    <strong>Missing emails:</strong> It's OK if some residents don't have emails initially. They can be added later.
                  </div>
                </div>
              </div>

              {/* Payment Setup */}
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">💳 Payment & Billing</h4>
                <div className="space-y-3 text-sm text-gray-700">
                  <div>
                    <strong>Bank Account:</strong> HOA needs a business checking account for receiving payments.
                  </div>
                  <div>
                    <strong>Monthly Dues Amount:</strong> Standard monthly fee each property pays.
                  </div>
                  <div>
                    <strong>Special Assessments:</strong> Any one-time fees currently being collected.
                  </div>
                  <div>
                    <strong>Late Fees:</strong> Policy for late payment penalties.
                  </div>
                  <div>
                    <strong>Payment Methods:</strong> Online payments, checks, automatic bank transfers.
                  </div>
                </div>
              </div>

              {/* Communication Preferences */}
              <div className="border-l-4 border-indigo-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">📧 Communication Setup</h4>
                <div className="space-y-3 text-sm text-gray-700">
                  <div>
                    <strong>Email Templates:</strong> Welcome messages, payment reminders, violation notices.
                  </div>
                  <div>
                    <strong>Website Content:</strong> Community information, contact details, announcements.
                  </div>
                  <div>
                    <strong>Logo/Branding:</strong> HOA logo for letterhead and website.
                  </div>
                  <div>
                    <strong>Social Media:</strong> Links to community Facebook groups, etc.
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  💡 <strong>Pro Tip:</strong> Don't worry about having everything perfect initially. 
                  Most items can be updated after the HOA is set up and running.
                </div>
                <button
                  onClick={() => setShowSetupGuide(false)}
                  className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ENHANCED: Bulk Email Modal */}
      {showBulkEmailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">📧 Send Newsletter to All Users</h3>
              <p className="text-sm text-gray-600 mt-1">Send an email to all registered users across all HOAs</p>
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Target Audience</label>
                <select
                  value={bulkEmailData.target}
                  onChange={(e) => setBulkEmailData({...bulkEmailData, target: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All Users (Homeowners + Board Members)</option>
                  <option value="homeowners">Homeowners Only</option>
                  <option value="board_members">Board Members Only</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Subject</label>
                <input
                  type="text"
                  value={bulkEmailData.subject}
                  onChange={(e) => setBulkEmailData({...bulkEmailData, subject: e.target.value})}
                  placeholder="Newsletter subject line..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  value={bulkEmailData.message}
                  onChange={(e) => setBulkEmailData({...bulkEmailData, message: e.target.value})}
                  rows={8}
                  placeholder="Write your newsletter message here..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Important Notice</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>This will send an email to ALL users across ALL HOAs. Please review your message carefully before sending.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkEmailModal(false);
                  setBulkEmailData({ subject: '', message: '', target: 'all' });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={sendBulkEmail}
                disabled={loading || !bulkEmailData.subject || !bulkEmailData.message}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Newsletter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Overview Tab Component

// HOA Creation Logs Tab Component
const HOACreationLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, failed, in_progress

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const queryParams = filter !== 'all' ? `?status=${filter}` : '';
      const { data } = await axios.get(`/api/platform-admin/hoa-creation-logs${queryParams}`);
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Failed to fetch HOA creation logs:', error);
      alert('Failed to load HOA creation logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'bg-green-100 text-green-800 border-green-300',
      failed: 'bg-red-100 text-red-800 border-red-300',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      pending: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return badges[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">📝 HOA Creation Logs</h2>
            <p className="text-sm text-gray-600 mt-1">
              Track all HOA signup attempts for debugging and monitoring
            </p>
          </div>
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            🔄 Refresh
          </button>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'completed'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            ✅ Completed
          </button>
          <button
            onClick={() => setFilter('failed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'failed'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            ❌ Failed
          </button>
          <button
            onClick={() => setFilter('in_progress')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'in_progress'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            ⏳ In Progress
          </button>
        </div>

        {/* Logs List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No HOA creation logs found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {logs.map((log) => (
              <div
                key={log._id || log.id}
                className="border-2 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {log.hoa_name || 'Unknown HOA'}
                      </h3>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                          log.status || log.payment_status
                        )}`}
                      >
                        {(log.status || log.payment_status || 'unknown').toUpperCase()}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                      <div>
                        <span className="font-medium">Admin Email:</span>{' '}
                        {log.admin_email || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">HOA Code:</span>{' '}
                        {log.hoa_code || 'Not assigned'}
                      </div>
                      <div>
                        <span className="font-medium">Created:</span>{' '}
                        {formatDate(log.created_at)}
                      </div>
                      <div>
                        <span className="font-medium">Completed:</span>{' '}
                        {formatDate(log.completed_at)}
                      </div>
                    </div>

                    {/* Error Message */}
                    {log.error_message && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                        <p className="text-sm font-medium text-red-800">Error:</p>
                        <p className="text-sm text-red-700 mt-1">{log.error_message}</p>
                      </div>
                    )}

                    {/* Plan Info */}
                    {(log.selected_plan || log.detected_plan) && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Plan:</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                          {(log.selected_plan || log.detected_plan || 'N/A').toUpperCase()}
                        </span>
                      </div>
                    )}

                    {/* Add-ons */}
                    {log.detected_addons && log.detected_addons.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm font-medium text-gray-700">Add-ons:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {log.detected_addons.map((addon, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs"
                            >
                              {addon}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const OverviewTab = ({ hoas, employees, setShowBulkEmailModal }) => {
  const totalHomeowners = hoas.reduce((sum, hoa) => sum + (hoa.total_properties || 0), 0);
  const activeHOAs = hoas.filter(hoa => hoa.is_active).length;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">🏘️</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total HOAs</dt>
                  <dd className="text-lg font-medium text-gray-900">{activeHOAs}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">🏠</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Properties</dt>
                  <dd className="text-lg font-medium text-gray-900">{totalHomeowners}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">👥</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Team Members</dt>
                  <dd className="text-lg font-medium text-gray-900">{employees.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">💰</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Monthly Revenue</dt>
                  <dd className="text-lg font-medium text-gray-900">$12,450</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Platform Actions</h3>
          <p className="mt-1 text-sm text-gray-500">Quick actions for managing all HOAs</p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={() => setShowBulkEmailModal(true)}
              className="relative group bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="text-2xl">📧</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium">Send Bulk Email</p>
                  <p className="text-xs opacity-90">Email all users across HOAs</p>
                </div>
              </div>
            </button>
            
            <button
              onClick={async () => {
                try {
                  // Export all users with authentication
                  const response = await api.get('/api/platform-admin/export-users', {
                    responseType: 'blob'
                  });
                  
                  // Create download link
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `all_users_export_${new Date().toISOString().split('T')[0]}.csv`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  
                  alert('✅ Successfully exported all users!');
                } catch (error) {
                  console.error('Failed to export users:', error);
                  alert(`❌ Failed to export users: ${error.response?.data?.detail || error.message}`);
                }
              }}
              className="relative group bg-gradient-to-r from-green-500 to-teal-600 p-6 text-white rounded-lg hover:from-green-600 hover:to-teal-700 transition-all duration-200 transform hover:scale-105"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="text-2xl">📋</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium">Export All Users</p>
                  <p className="text-xs opacity-90">Download user data CSV</p>
                </div>
              </div>
            </button>
            
            <button
              className="relative group bg-gradient-to-r from-orange-500 to-red-600 p-6 text-white rounded-lg hover:from-orange-600 hover:to-red-700 transition-all duration-200 transform hover:scale-105 opacity-75 cursor-not-allowed"
              disabled
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="text-2xl">📊</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium">Platform Analytics</p>
                  <p className="text-xs opacity-90">Coming Soon</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Recent HOA Activity</h3>
          <div className="mt-5">
            {hoas.slice(0, 5).map((hoa) => (
              <div key={hoa.id} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                <div>
                  <p className="text-sm font-medium text-gray-900">{hoa.name}</p>
                  <p className="text-sm text-gray-500">Code: {hoa.code} • {hoa.total_properties} properties</p>
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(hoa.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// HOA Management Tab Component - ENHANCED: With export and bulk email functionality
const HOAManagementTab = ({ hoas, onRefresh, setSendPasswordsModal, executeSendPasswords }) => {
  const [selectedHOA, setSelectedHOA] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [overrideData, setOverrideData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHOAs, setSelectedHOAs] = useState(new Set());
  const [filteredHOAs, setFilteredHOAs] = useState(hoas);
  const [syncingPublicPages, setSyncingPublicPages] = useState(false);
  const [syncingPermissions, setSyncingPermissions] = useState(false);
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  const [syncingBudgetActuals, setSyncingBudgetActuals] = useState(false);
  // Inline admin actions (scoped to HOA Management tab)
  const [showSetAdminModal, setShowSetAdminModal] = useState(false);
  const [showResetEmailModal, setShowResetEmailModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminRole, setAdminRole] = useState('platform_admin');
  const [resetEmail, setResetEmail] = useState('');
  const [adminActionLoading, setAdminActionLoading] = useState(false);
  // Board members management
  const [showBoardMembersModal, setShowBoardMembersModal] = useState(false);
  const [boardMembers, setBoardMembers] = useState([]);
  const [boardMembersLoading, setBoardMembersLoading] = useState(false);
  const [showAddBoardMemberForm, setShowAddBoardMemberForm] = useState(false);
  const [newBoardMember, setNewBoardMember] = useState({ name: '', email: '', role: 'board_member' });
  const [showResetBalancesModal, setShowResetBalancesModal] = useState(false);
  const [paResetType, setPaResetType] = useState('all');
  const [paResetConfirmation, setPaResetConfirmation] = useState('');
  const [paResetting, setPaResetting] = useState(false);

  const setPlatformAdminByEmail = async () => {
    if (!adminEmail) { alert('Please enter an email'); return; }
    try {
      setAdminActionLoading(true);
      const response = await axios.put('/api/platform-admin/users/role-by-email', { email: adminEmail, role: 'platform_admin' });
      if (response.data?.success) {
        alert(`✅ ${adminEmail} is now Platform Admin`);
        setShowSetAdminModal(false);
        setAdminEmail('');
      } else {
        alert('❌ Failed to update platform admin');
      }
    } catch (error) {
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    } finally {
      setAdminActionLoading(false);
    }
  };

  const resetPasswordByEmail = async () => {
    if (!resetEmail) { alert('Please enter an email'); return; }
    try {
      setAdminActionLoading(true);
      const response = await axios.post('/api/platform-admin/users/reset-password-by-email', { email: resetEmail });
      if (response.data?.success) {
        alert(`✅ Temporary password for ${resetEmail}:\n${response.data.temp_password}`);
        setShowResetEmailModal(false);
        setResetEmail('');
      }
    } catch (error) {
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    } finally {
      setAdminActionLoading(false);
    }
  };


  const assignMissingLedgerIds = async (hoaId) => {
    if (!window.confirm('Assign ledger IDs to all users without one in this HOA?')) return;
    try {
      setLoading(true);
      const response = await axios.post(`/api/platform-admin/hoa/${hoaId}/assign-missing-ledger-ids`);
      if (response.data?.success) {
        alert(`✅ ${response.data.message}\n\nTotal without ledger: ${response.data.total_users_without_ledger}\nAssigned: ${response.data.assigned_count}`);
      } else {
        alert('❌ Failed to assign ledger IDs');
      }
    } catch (error) {
      console.error('Failed to assign ledger IDs:', error);
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const cleanupDuplicateTeamMembers = async (hoaId) => {
    if (!window.confirm('🧹 Cleanup duplicate team members?\n\nThis will remove ALL duplicate team member records (keeping only the oldest for each email).\n\nThis action cannot be undone!')) return;
    try {
      setLoading(true);
      const response = await axios.post(`/api/platform-admin/hoa/${hoaId}/cleanup-duplicate-team-members`);
      if (response.data?.success) {
        const data = response.data;
        let message = `✅ ${data.message}\n\n`;
        message += `📧 Emails Checked: ${data.emails_checked}\n`;
        message += `🗑️ Total Removed: ${data.total_removed}\n`;
        
        if (data.duplicates_found && data.duplicates_found.length > 0) {
          message += `\n📋 Duplicates Found:\n`;
          data.duplicates_found.forEach(dup => {
            message += `  • ${dup.email}: ${dup.count} in ${dup.collection}\n`;
          });
        }
        
        alert(message);
      } else {
        alert('❌ Failed to cleanup duplicates');
      }
    } catch (error) {
      console.error('Failed to cleanup duplicates:', error);
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };


  const checkDuplicatePayments = async (hoaId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/platform-admin/hoa/${hoaId}/duplicate-payments`);
      if (response.data?.success) {
        const data = response.data;
        
        if (data.duplicate_groups_found === 0) {
          alert(`✅ No duplicate payments found\n\nTotal payments checked: ${data.total_payments_checked}`);
        } else {
          let message = `⚠️ DUPLICATE PAYMENTS FOUND\n\n`;
          message += `Total payments checked: ${data.total_payments_checked}\n`;
          message += `Duplicate groups found: ${data.duplicate_groups_found}\n\n`;
          message += `═══════════════════════════════\n\n`;
          
          data.duplicates.forEach((group, idx) => {
            message += `GROUP ${idx + 1}:\n`;
            message += `Payer: ${group.payer_email}\n`;
            message += `Property: ${group.property_identifier}\n`;
            message += `Amount: $${group.primary_payment.amount}\n\n`;
            message += `Primary Payment:\n`;
            message += `  ID: ${group.primary_payment.id.substring(0, 8)}...\n`;
            message += `  Status: ${group.primary_payment.status}\n`;
            message += `  Method: ${group.primary_payment.payment_method}\n`;
            message += `  Time: ${new Date(group.primary_payment.created_at).toLocaleString()}\n\n`;
            message += `Duplicate Payments (${group.duplicate_count}):\n`;
            group.duplicate_payments.forEach((dup, dupIdx) => {
              message += `  ${dupIdx + 1}. ID: ${dup.id.substring(0, 8)}... | `;
              message += `Status: ${dup.status} | `;
              message += `${dup.minutes_apart}min apart\n`;
            });
            message += `\n═══════════════════════════════\n\n`;
          });
          
          message += `\n⚠️ You may need to refund duplicate charges in Stripe Dashboard!`;
          alert(message);
        }
      } else {
        alert('❌ Failed to check duplicate payments');
      }
    } catch (error) {
      console.error('Failed to check duplicate payments:', error);
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };



  const [loading, setLoading] = useState(false);

  // Filter HOAs based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = hoas.filter(hoa => 
        hoa.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hoa.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hoa.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hoa.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredHOAs(filtered);
    } else {
      setFilteredHOAs(hoas);
    }
  }, [searchTerm, hoas]);

  // Load board members for an HOA
  const loadBoardMembers = async (hoa) => {
    setSelectedHOA(hoa);
    setBoardMembersLoading(true);
    setShowBoardMembersModal(true);
    try {
      const response = await axios.get(`/api/platform-admin/hoa/${hoa.id}/board-members`);
      setBoardMembers(response.data.board_members || []);
    } catch (error) {
      console.error('Failed to load board members:', error);
      alert('❌ Failed to load board members');
      setBoardMembers([]);
    } finally {
      setBoardMembersLoading(false);
    }
  };

  // Add a new board member
  const addBoardMember = async () => {
    if (!newBoardMember.name || !newBoardMember.email) {
      alert('Please fill in name and email');
      return;
    }

    try {
      const response = await axios.post(`/api/platform-admin/hoa/${selectedHOA.id}/board-members`, newBoardMember);
      
      if (response.data.success) {
        alert(`✅ ${response.data.message}\n\nTemporary Password: ${response.data.temp_password}\n\nPlease share this password with the board member securely.`);
        setNewBoardMember({ name: '', email: '', role: 'board_member' });
        setShowAddBoardMemberForm(false);
        // Reload board members
        loadBoardMembers(selectedHOA);
      }
    } catch (error) {
      console.error('Failed to add board member:', error);
      alert('❌ ' + (error.response?.data?.detail || 'Failed to add board member'));
    }
  };

  const handleSelectAll = () => {
    if (selectedHOAs.size === filteredHOAs.length) {
      setSelectedHOAs(new Set());
    } else {
      setSelectedHOAs(new Set(filteredHOAs.map(hoa => hoa.id)));
    }
  };

  const handleSelectHOA = (hoaId) => {
    const newSelected = new Set(selectedHOAs);
    if (newSelected.has(hoaId)) {
      newSelected.delete(hoaId);
    } else {
      newSelected.add(hoaId);
    }
    setSelectedHOAs(newSelected);
  };

  const deleteHOA = async (hoaId) => {
    const hoa = hoas.find(h => h.id === hoaId);
    if (!window.confirm(`Are you sure you want to delete "${hoa?.name}" (${hoa?.code})? This will permanently delete all associated data including users, documents, and activities. This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      console.log('Attempting to delete HOA:', hoaId);
      console.log('Auth token exists:', !!localStorage.getItem('hoa_token'));
      console.log('Axios headers:', axios.defaults.headers.common);
      
      const response = await axios.delete(`/api/platform-admin/hoas/${hoaId}`);
      
      console.log('Delete response:', response.data);
      
      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to delete HOA - Full error:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      alert(`❌ Failed to delete HOA: ${error.response?.data?.detail || error.message}\n\nPlease check browser console for details.`);
    } finally {
      setLoading(false);
    }
  };

  const bulkDeleteHOAs = async () => {
    if (selectedHOAs.size === 0) {
      alert('Please select HOAs to delete');
      return;
    }

    const selectedHOAsList = Array.from(selectedHOAs);
    const hoaNames = selectedHOAsList.map(id => {
      const hoa = hoas.find(h => h.id === id);
      return hoa ? `${hoa.name} (${hoa.code})` : id;
    }).join(', ');

    if (!window.confirm(`Are you sure you want to delete ${selectedHOAs.size} HOAs?\n\n${hoaNames}\n\nThis will permanently delete all associated data. This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/platform-admin/hoas/bulk-delete', {
        hoa_ids: selectedHOAsList
      });
      
      if (response.data.success) {
        alert(`✅ Successfully deleted ${response.data.deleted_count} HOAs. ${response.data.failed_count > 0 ? `${response.data.failed_count} failed.` : ''}`);
        setSelectedHOAs(new Set());
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to bulk delete HOAs:', error);
      alert(`❌ Failed to delete HOAs: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const bulkApplyOverride = async () => {
    if (selectedHOAs.size === 0) {
      alert('Please select HOAs to apply overrides to');
      return;
    }

    // Show override modal for bulk operations
    setShowOverrideModal(true);
  };



  const applyOverride = async (hoaId, overrides) => {
    if (selectedHOAs.size > 0) {
      // Bulk override
      const selectedHOAsList = Array.from(selectedHOAs);
      try {
        setLoading(true);
        await axios.post('/api/platform-admin/hoas/bulk-override', {
          hoa_ids: selectedHOAsList,
          override_data: overrides
        });
        alert(`✅ Successfully applied overrides to ${selectedHOAsList.length} HOAs`);
        setSelectedHOAs(new Set());
      } catch (error) {
        console.error('Failed to apply bulk override:', error);
        alert(`❌ Failed to apply overrides: ${error.response?.data?.detail || error.message}`);
      } finally {
        setLoading(false);
      }
    } else {
      // Single override
      try {
        await axios.post(`/api/platform-admin/hoa/${hoaId}/override`, overrides);
        alert('✅ Override applied successfully');
      } catch (error) {
        console.error('Failed to apply override:', error);
        alert(`❌ Failed to apply override: ${error.response?.data?.detail || error.message}`);
      }
    }
    setShowOverrideModal(false);
    setOverrideData({});
    onRefresh();
  };
  
  const sendBoardMemberPasswords = async (hoaId) => {
    // Find the HOA data to show in modal
    const hoa = hoas.find(h => h.id === hoaId);
    if (!hoa) {
      alert('❌ HOA not found');
      return;
    }
    
    // Show the Send Passwords modal with options
    setSendPasswordsModal(hoa);
  };



  const openHelpSetup = (hoa) => {
    // Construct the setup URL with HOA code for direct access
    const setupUrl = `${window.location.origin}/setup-checklist/${hoa.code}`;
    
    // Open in new tab so admin can help customer via screen share
    const newWindow = window.open(setupUrl, '_blank');
    
    if (newWindow) {
      // Show instructions to admin
      alert(`🚀 HOA Setup Help\n\nOpening setup checklist for: ${hoa.name} (${hoa.code})\n\nYou can now:\n• Share your screen with the customer\n• Walk them through the setup process\n• Help them complete any remaining tasks\n\nThe setup page is opening in a new tab.`);
    } else {
      // Fallback if popup blocked
      alert(`🚀 Setup URL for ${hoa.name}:\n\n${setupUrl}\n\nPlease open this URL in a new tab to help the customer with their setup process.`);
    }
  };

  const syncAllPublicPages = async () => {
    if (!window.confirm('🌐 Sync Public Pages for All HOAs\n\nThis will create public pages for all HOAs that don\'t have one yet.\n\nThis is a free feature included for all communities.\n\nContinue?')) {
      return;
    }

    setSyncingPublicPages(true);

    try {
      const { data } = await api.post('/api/platform-admin/sync-all-public-pages');

      if (data.success) {
        const summary = data.summary;
        const results = data.results;

        let message = `✅ Public Pages Sync Complete!\n\n`;
        message += `📊 Summary:\n`;
        message += `• Created: ${summary.created} new public pages\n`;
        message += `• Already Exists: ${summary.already_exists}\n`;
        message += `• Errors: ${summary.errors}\n\n`;

        if (results.created && results.created.length > 0) {
          message += `\n📝 Created Pages:\n`;
          results.created.slice(0, 5).forEach(item => {
            message += `• ${item.name} → ${item.url}\n`;
          });
          if (results.created.length > 5) {
            message += `  ... and ${results.created.length - 5} more\n`;
          }
        }

        if (results.errors && results.errors.length > 0) {
          message += `\n❌ Errors:\n`;
          results.errors.forEach(item => {
            message += `• ${item.name}: ${item.error}\n`;
          });
        }

        alert(message);
        onRefresh(); // Refresh the HOA list
      }
    } catch (error) {
      console.error('Failed to sync public pages:', error);
      alert(`❌ Failed to sync public pages: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSyncingPublicPages(false);
    }
  };

  const syncAllPermissions = async () => {
    if (!window.confirm('🔐 Sync Membership Permissions for All HOAs\n\nThis will synchronize poll, newsletter, and document creation permissions across all HOAs.\n\nFor each HOA, it will:\n• Validate specific user permissions\n• Verify board and committee access\n• Ensure settings match actual permissions\n\nThis ensures everyone who should have access actually has it.\n\nContinue?')) {
      return;
    }

    setSyncingPermissions(true);

    try {
      const { data } = await api.post('/api/vote/sync-all-permissions');
      
      console.log('✅ Permissions synced:', data);
      
      let message = `✅ Permissions Sync Complete!\n\n`;
      message += `📊 Summary:\n`;
      message += `• HOAs Processed: ${data.details.total_hoas_processed}\n`;
      message += `• Poll Permissions Synced: ${data.details.poll_permissions_synced}\n`;
      message += `• Users with Access: ${data.details.users_granted_access.length}\n`;
      
      if (data.details.errors.length > 0) {
        message += `\n⚠️ Errors: ${data.details.errors.length}\n`;
      }
      
      alert(message);
    } catch (error) {
      console.error('Failed to sync permissions:', error);
      alert(`❌ Failed to sync permissions: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSyncingPermissions(false);
    }
  };


  const removeDuplicateChannels = async () => {
    if (!window.confirm('💬 Remove Duplicate Board Channels\n\nThis will scan all HOAs and remove duplicate board channels.\n\nFor each HOA, it will:\n• Keep the OLDEST board channel\n• Remove all newer duplicates\n\nThis action cannot be undone.\n\nContinue?')) {
      return;
    }

    setRemovingDuplicates(true);

    try {
      const { data } = await api.post('/api/platform-admin/remove-duplicate-channels');

      if (data.success) {
        const results = data.results;

        let message = `✅ Duplicate Channels Cleanup Complete!\n\n`;
        message += `📊 Summary:\n`;
        message += `• HOAs Checked: ${results.hoas_checked}\n`;
        message += `• Duplicates Removed: ${results.duplicates_removed}\n`;
        message += `• Errors: ${results.errors.length}\n\n`;

        if (results.errors && results.errors.length > 0) {
          message += `\n❌ Errors:\n`;
          results.errors.forEach(item => {
            message += `• ${item.hoa_name}: ${item.error}\n`;
          });
        }

        alert(message);
      }
    } catch (error) {
      console.error('Failed to remove duplicate channels:', error);
      alert(`❌ Failed to remove duplicate channels: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRemovingDuplicates(false);
    }
  };

  // ONE-TIME DATA FIX: Sync budget actuals for all HOAs
  const syncAllBudgetActuals = async () => {
    if (!window.confirm('🔧 ONE-TIME FIX: Sync Budget Actuals for All HOAs\n\nThis will recalculate budget actuals for ALL HOAs based on their active budget period dates.\n\n⚠️ This is a one-time data fix to correct actuals that were counting transactions outside their budget period.\n\nContinue?')) {
      return;
    }

    setSyncingBudgetActuals(true);

    try {
      const { data } = await api.post('/api/platform-admin/sync-all-budget-actuals');

      if (data.success) {
        let message = `✅ Budget Actuals Sync Complete!\n\n`;
        message += `📊 Summary:\n`;
        message += `• HOAs Processed: ${data.hoas_processed || 0}\n`;
        message += `• Budget Items Adjusted: ${data.total_items_adjusted || 0}\n`;
        message += `• Total Adjustment: $${(data.total_adjustment || 0).toFixed(2)}\n`;
        
        if (data.errors && data.errors.length > 0) {
          message += `\n❌ Errors:\n`;
          data.errors.forEach(err => {
            message += `• ${err.hoa_name || err.hoa_id}: ${err.error}\n`;
          });
        }

        alert(message);
      }
    } catch (error) {
      console.error('Failed to sync budget actuals:', error);
      alert(`❌ Failed to sync budget actuals: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSyncingBudgetActuals(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Search */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">HOA Management</h2>
          <p className="text-sm text-gray-500">{filteredHOAs.length} of {hoas.length} communities shown</p>
        </div>
        
        {/* Search Bar */}
        <div className="w-full sm:w-80">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name, code, contact, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>



      {/* Global Action Buttons - Always Visible */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-sm font-semibold text-blue-900">🌐 Public Pages Sync</h3>
            <p className="text-xs text-blue-700 mt-1">
              Create public pages for all HOAs that don't have one. This is a free feature for all communities.
            </p>
          </div>
          <button
            onClick={syncAllPublicPages}
            disabled={syncingPublicPages}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {syncingPublicPages ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing...
              </>
            ) : (
              '🔄 Sync Public Pages for All HOAs'
            )}
          </button>
        </div>
      </div>

      {/* Permissions Sync */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-sm font-semibold text-green-900">🔐 Membership Permissions Sync</h3>
            <p className="text-xs text-green-700 mt-1">
              Sync poll, newsletter, and document creation permissions across all HOAs. Ensures users who should have access actually get it.
            </p>
          </div>
          <button
            onClick={syncAllPermissions}
            disabled={syncingPermissions}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {syncingPermissions ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing...
              </>
            ) : (
              '🔄 Sync Permissions for All HOAs'
            )}
          </button>
        </div>
      </div>

      {/* Communication Channels Cleanup - Always Visible */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-sm font-semibold text-purple-900">💬 Communication Channels Cleanup</h3>
            <p className="text-xs text-purple-700 mt-1">
              Remove duplicate board channels across all HOAs. Keeps the oldest channel and removes duplicates.
            </p>
          </div>
          <button
            onClick={removeDuplicateChannels}
            disabled={removingDuplicates}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {removingDuplicates ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Removing...
              </>
            ) : (
              '🧹 Remove Duplicate Channels'
            )}
          </button>
        </div>

        {/* ONE-TIME FIX: Sync Budget Actuals */}
        <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
          <div>
            <p className="text-sm font-medium text-amber-900">🔧 One-Time Fix: Sync Budget Actuals</p>
            <p className="text-xs text-amber-700 mt-1">
              Recalculate budget actuals for all HOAs to only include transactions within their budget period dates.
            </p>
          </div>
          <button
            onClick={syncAllBudgetActuals}
            disabled={syncingBudgetActuals}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            data-testid="sync-budget-actuals-btn"
          >
            {syncingBudgetActuals ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing...
              </>
            ) : (
              '🔧 Sync Budget Actuals'
            )}
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      {filteredHOAs.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedHOAs.size === filteredHOAs.length && filteredHOAs.length > 0}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {selectedHOAs.size === filteredHOAs.length && filteredHOAs.length > 0 ? 'Deselect All' : 'Select All'}
                </span>
              </label>
              
              {selectedHOAs.size > 0 && (
                <span className="text-sm text-indigo-600">
                  {selectedHOAs.size} selected
                </span>
              )}
            </div>
            
            {/* Bulk Action Buttons */}
            {selectedHOAs.size > 0 && (
              <div className="flex space-x-2">
                <button
                  onClick={bulkApplyOverride}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50"
                >
                  Apply Override to Selected
                </button>
                <button
                  onClick={bulkDeleteHOAs}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                >
                  {loading ? 'Deleting...' : `Delete Selected (${selectedHOAs.size})`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* HOA List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredHOAs.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-3l-7 4-7-4m14 6H5" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm ? 'No HOAs match your search' : 'No HOAs found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search terms' : 'HOAs will appear here once created'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredHOAs.map((hoa) => (
              <li key={hoa.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedHOAs.has(hoa.id)}
                      onChange={() => handleSelectHOA(hoa.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {hoa.name}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>Code: <span className="font-mono">{hoa.code}</span></span>
                        <span>{hoa.total_properties || 0} properties</span>
                        <span>{hoa.contact_email}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{hoa.address}</p>
                    </div>
                  </div>
                  
                  {/* MOBILE FRIENDLY: Action buttons wrap on small screens */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 min-w-0">
                    <div className="flex flex-wrap gap-1 sm:gap-2 justify-end sm:justify-start">
                      <button
                        onClick={() => sendBoardMemberPasswords(hoa.id)}
                        disabled={loading}
                        className="inline-flex items-center px-2 py-1 border border-green-300 text-xs font-medium rounded text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50 whitespace-nowrap"
                        title="Generate and get new temporary passwords for board members"
                      >
                        📧 Send Passwords
                      </button>

                      <button
                        onClick={() => openHelpSetup(hoa)}
                        className="inline-flex items-center px-2 py-1 border border-blue-300 text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 whitespace-nowrap"
                        title="Open HOA setup checklist to help customer complete their setup"
                      >
                        🚀 Help Setup
                      </button>
                      <button
                        onClick={() => {
                          setSelectedHOA(hoa);
                          setShowConfigModal(true);
                        }}
                        className="inline-flex items-center px-2 py-1 border border-purple-300 text-xs font-medium rounded text-purple-700 bg-purple-50 hover:bg-purple-100 whitespace-nowrap"
                      >
                        Manage Config
                      </button>
                      <button
                        onClick={() => loadBoardMembers(hoa)}
                        className="inline-flex items-center px-2 py-1 border border-indigo-300 text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 whitespace-nowrap"
                        title="View and manage board members for this HOA"
                      >
                        👥 Board Members
                      </button>
                      <button
                        onClick={() => assignMissingLedgerIds(hoa.id)}
                        className="inline-flex items-center px-2 py-1 border border-green-300 text-xs font-medium rounded text-green-700 bg-green-50 hover:bg-green-100 whitespace-nowrap"
                        title="Assign ledger IDs to users who don't have one"
                      >
                        🆔 Assign Ledger IDs
                      </button>
                      <button
                        onClick={() => cleanupDuplicateTeamMembers(hoa.id)}
                        className="inline-flex items-center px-2 py-1 border border-red-300 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 whitespace-nowrap"
                        title="Remove duplicate team members from all collections"
                      >
                        🧹 Cleanup Duplicates
                      </button>
                      <button
                        onClick={() => checkDuplicatePayments(hoa.id)}
                        className="inline-flex items-center px-2 py-1 border border-orange-300 text-xs font-medium rounded text-orange-700 bg-orange-50 hover:bg-orange-100 whitespace-nowrap"
                        title="Check for duplicate external payments"
                      >
                        💳 Check Duplicate Payments
                      </button>
                      <button
                        data-testid={`reset-balances-btn-${hoa.id}`}
                        onClick={() => {
                          setSelectedHOA(hoa);
                          setShowResetBalancesModal(true);
                        }}
                        className="inline-flex items-center px-2 py-1 border border-red-400 text-xs font-medium rounded text-red-800 bg-red-50 hover:bg-red-100 whitespace-nowrap"
                        title="Reset ledger balances for this HOA"
                      >
                        Reset Balances
                      </button>
      {/* Board Members Modal */}
      {showBoardMembersModal && selectedHOA && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Board Members</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedHOA.name} ({selectedHOA.code})
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowBoardMembersModal(false);
                    setShowAddBoardMemberForm(false);
                    setSelectedHOA(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {boardMembersLoading ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading board members...</p>
                </div>
              ) : (
                <>
                  {/* Board Members List */}
                  <div className="space-y-3 mb-6">
                    {boardMembers.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">No board members found for this HOA</p>
                      </div>
                    ) : (
                      boardMembers.map((member) => (
                        <div key={member.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="font-medium text-gray-900">{member.name}</h4>
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  member.role === 'president' ? 'bg-blue-100 text-blue-800' :
                                  member.role === 'vice_president' ? 'bg-indigo-100 text-indigo-800' :
                                  member.role === 'treasurer' ? 'bg-green-100 text-green-800' :
                                  member.role === 'secretary' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {member.role.replace('_', ' ').toUpperCase()}
                                </span>
                              </div>
                              <div className="mt-2 text-sm text-gray-600 space-y-1">
                                <div className="flex items-center">
                                  <span className="font-semibold w-24">Email:</span>
                                  <span>{member.email}</span>
                                </div>
                                {member.phone && (
                                  <div className="flex items-center">
                                    <span className="font-semibold w-24">Phone:</span>
                                    <span>{member.phone}</span>
                                  </div>
                                )}
                                {member.property_address && (
                                  <div className="flex items-center">
                                    <span className="font-semibold w-24">Address:</span>
                                    <span>{member.property_address}</span>
                                  </div>
                                )}
                                <div className="flex items-center text-xs text-gray-500">
                                  <span className="font-semibold w-24">Added:</span>
                                  <span>{new Date(member.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Board Member Section */}
                  {!showAddBoardMemberForm ? (
                    <button
                      onClick={() => setShowAddBoardMemberForm(true)}
                      className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
                    >
                      + Add New Board Member
                    </button>
                  ) : (
                    <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                      <h4 className="font-medium text-gray-900 mb-3">Add New Board Member</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            placeholder="John Smith"
                            value={newBoardMember.name}
                            onChange={(e) => setNewBoardMember({...newBoardMember, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email *
                          </label>
                          <input
                            type="email"
                            placeholder="john@example.com"
                            value={newBoardMember.email}
                            onChange={(e) => setNewBoardMember({...newBoardMember, email: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Role *
                          </label>
                          <select
                            value={newBoardMember.role}
                            onChange={(e) => setNewBoardMember({...newBoardMember, role: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="president">President</option>
                            <option value="vice_president">Vice President</option>
                            <option value="treasurer">Treasurer</option>
                            <option value="secretary">Secretary</option>
                            <option value="board_member">Board Member</option>
                          </select>
                        </div>
                        <div className="flex gap-3 justify-end">
                          <button
                            onClick={() => {
                              setShowAddBoardMemberForm(false);
                              setNewBoardMember({ name: '', email: '', role: 'board_member' });
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={addBoardMember}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                          >
                            Add Board Member
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inline Admin Modals */}
      {showSetAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Set Platform Admin</h3>
            <input
              type="email"
              value={adminEmail}
              onChange={(e)=>setAdminEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full border px-3 py-2 rounded mb-3"
            />
            <div className="flex justify-end space-x-2">
              <button onClick={()=>setShowSetAdminModal(false)} className="px-3 py-2 text-sm rounded border">Cancel</button>
              <button onClick={setPlatformAdminByEmail} disabled={adminActionLoading} className="px-3 py-2 text-sm rounded bg-indigo-600 text-white disabled:opacity-50">{adminActionLoading? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {showResetEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Reset Password by Email</h3>
            <input
              type="email"
              value={resetEmail}
              onChange={(e)=>setResetEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full border px-3 py-2 rounded mb-3"
            />
            <div className="flex justify-end space-x-2">
              <button onClick={()=>setShowResetEmailModal(false)} className="px-3 py-2 text-sm rounded border">Cancel</button>
              <button onClick={resetPasswordByEmail} disabled={adminActionLoading} className="px-3 py-2 text-sm rounded bg-indigo-600 text-white disabled:opacity-50">{adminActionLoading? 'Resetting...' : 'Reset'}</button>
            </div>
          </div>
        </div>
      )}

                      <button
                        onClick={() => {
                          setSelectedHOA(hoa);
                          setShowOverrideModal(true);
                        }}
                        className="inline-flex items-center px-2 py-1 border border-indigo-300 text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 whitespace-nowrap"
                      >
                        Override
                      </button>
                      <button
                        onClick={() => deleteHOA(hoa.id)}
                        disabled={loading}
                        className="inline-flex items-center px-2 py-1 border border-red-300 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 whitespace-nowrap"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="flex items-center justify-end sm:justify-start">
                      <button
                        onClick={() => setShowSetAdminModal(true)}
                        className="inline-flex items-center px-2 py-1 border border-yellow-300 text-xs font-medium rounded text-yellow-700 bg-yellow-50 hover:bg-yellow-100 whitespace-nowrap"
                      >
                        Set Platform Admin
                      </button>
                      <button
                        onClick={() => setShowResetEmailModal(true)}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-gray-50 hover:bg-gray-100 whitespace-nowrap"
                      >
                        Reset Password (by Email)
                      </button>

                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        hoa.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {hoa.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* HOA Configuration Management Modal */}
      {showConfigModal && selectedHOA && (
        <HOAConfigurationModal
          hoa={selectedHOA}
          onClose={() => {
            setShowConfigModal(false);
            setSelectedHOA(null);
          }}
          onSave={() => {
            setShowConfigModal(false);
            setSelectedHOA(null);
            onRefresh();
          }}
        />
      )}

      {/* Override Modal */}
      {showOverrideModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedHOAs.size > 0 
                  ? `Apply Override to ${selectedHOAs.size} HOAs`
                  : `Apply Override to ${selectedHOA?.name || 'HOA'}`
                }
              </h3>
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Extra Storage (GB)</label>
                <input
                  type="number"
                  value={overrideData.extra_storage || ''}
                  onChange={(e) => setOverrideData({...overrideData, extra_storage: parseInt(e.target.value) || 0})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Extra Accounts</label>
                <input
                  type="number"
                  value={overrideData.extra_accounts || ''}
                  onChange={(e) => setOverrideData({...overrideData, extra_accounts: parseInt(e.target.value) || 0})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={overrideData.guest_management || false}
                  onChange={(e) => setOverrideData({...overrideData, guest_management: e.target.checked})}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Enable Guest Management</label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={overrideData.custom_pages || false}
                  onChange={(e) => setOverrideData({...overrideData, custom_pages: e.target.checked})}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Enable Custom Pages</label>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => applyOverride(selectedHOAs.size > 0 ? null : selectedHOA?.id, overrideData)}
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Applying...' : 'Apply Override'}
              </button>
              <button
                onClick={() => {
                  setShowOverrideModal(false);
                  setOverrideData({});
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Balances Modal (Platform Admin) */}
      {showResetBalancesModal && selectedHOA && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-red-800 mb-2">Reset Ledger Balances</h3>
            <p className="text-sm text-gray-600 mb-4">HOA: <strong>{selectedHOA.name}</strong></p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700 font-medium">
                This removes ledger entries for all properties. A backup is created first so this can be undone.
              </p>
              <p className="text-xs text-red-600 mt-1">Budget transactions will NOT be affected.</p>
            </div>

            <div className="space-y-3 mb-4">
              <label className="text-sm font-medium text-gray-700">What to reset:</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="paResetType" value="all" checked={paResetType === 'all'} onChange={() => setPaResetType('all')} />
                  <div>
                    <span className="font-medium text-sm">Remove All Balances</span>
                    <p className="text-xs text-gray-500">Removes all ledger entries — dues, balance forward, payments, fees, everything</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="paResetType" value="dues_only" checked={paResetType === 'dues_only'} onChange={() => setPaResetType('dues_only')} />
                  <div>
                    <span className="font-medium text-sm">Remove Dues Only</span>
                    <p className="text-xs text-gray-500">Only removes dues charges — keeps balance forward, payments, fines, etc.</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="paResetType" value="balance_forward_only" checked={paResetType === 'balance_forward_only'} onChange={() => setPaResetType('balance_forward_only')} />
                  <div>
                    <span className="font-medium text-sm">Remove Balance Forward Only</span>
                    <p className="text-xs text-gray-500">Only removes balance forward entries — keeps dues, payments, fines, etc.</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 block mb-1">Type RESET to confirm:</label>
              <input
                type="text"
                placeholder="Type RESET"
                value={paResetConfirmation}
                onChange={(e) => setPaResetConfirmation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => { setShowResetBalancesModal(false); setPaResetConfirmation(''); setPaResetType('all'); }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                disabled={paResetConfirmation !== 'RESET' || paResetting}
                onClick={async () => {
                  try {
                    setPaResetting(true);
                    const resp = await axios.post(`/api/platform-admin/hoa/${selectedHOA.id}/reset-balances`, {
                      reset_type: paResetType,
                      confirmation: 'RESET',
                    });
                    const d = resp.data;
                    alert(`${d.message}\n\nBackup ID: ${d.backup_id}\n(Save this in case you need to undo)`);
                    setShowResetBalancesModal(false);
                    setPaResetConfirmation('');
                    setPaResetType('all');
                  } catch (err) {
                    alert(`Reset failed: ${err.response?.data?.detail || err.message}`);
                  } finally {
                    setPaResetting(false);
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {paResetting ? 'Resetting...' : 'Confirm Reset'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
const RoleUpdater = ({ hoa, onClose }) => {
  const [email, setEmail] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [role, setRole] = React.useState('board_member');
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');

  const save = async () => {
    if (!email) { setMessage('Please enter an email'); return; }
    setLoading(true);
    setMessage('');
    try {
      const payload = { email, first_name: firstName || 'Board', last_name: lastName || 'Member', role };
      const res = await axios.post(`/api/platform-admin/hoa/${hoa.id}/board-members`, payload);
      if (res.data?.success) {
        setMessage('✅ Role updated successfully');
      } else {
        setMessage('❌ Failed to update');
      }
    } catch (e) {
      setMessage(`❌ ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="space-y-3">
        <div>
          <label className="block text-sm text-gray-700">Board Member Email</label>
          <input value={email} onChange={e=>setEmail(e.target.value)} className="w-full border px-3 py-2 rounded" placeholder="user@example.com" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-700">First Name</label>
            <input value={firstName} onChange={e=>setFirstName(e.target.value)} className="w-full border px-3 py-2 rounded" />
          </div>
          <div>
            <label className="block text-sm text-gray-700">Last Name</label>
            <input value={lastName} onChange={e=>setLastName(e.target.value)} className="w-full border px-3 py-2 rounded" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-gray-700">Role</label>
          <select value={role} onChange={e=>setRole(e.target.value)} className="w-full border px-3 py-2 rounded capitalize">
            <option value="board_member">Board Member (access)</option>
            <option value="president">President</option>
            <option value="vice_president">Vice President</option>
            <option value="treasurer">Treasurer</option>
            <option value="secretary">Secretary</option>
          </select>
        </div>
      </div>
      {message && <p className="text-sm mt-3">{message}</p>}
      <div className="flex justify-end space-x-2 mt-4">
        <button onClick={onClose} className="px-3 py-2 text-sm rounded border">Close</button>
        <button onClick={save} disabled={loading} className="px-3 py-2 text-sm rounded bg-indigo-600 text-white disabled:opacity-50">{loading? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
};

// Employee Management Tab Component
const EmployeeManagementTab = ({ employees, onRefresh, setEditingEmployee }) => {
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    permissions: []
  });

  const createEmployee = async () => {
    try {
      await axios.post('/api/platform-admin/employees', newEmployee);
      setShowAddEmployee(false);
      setNewEmployee({ email: '', first_name: '', last_name: '', password: '', permissions: [] });
      onRefresh();
    } catch (error) {
      console.error('Failed to create employee:', error);
    }
  };

  const removeEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to remove this employee? This action will deactivate their access.')) {
      return;
    }
    
    try {
      console.log('Attempting to remove employee:', employeeId);
      console.log('Auth token exists:', !!localStorage.getItem('hoa_token'));
      console.log('Axios headers:', axios.defaults.headers.common);
      
      const response = await axios.delete(`/api/platform-admin/employees/${employeeId}`);
      
      console.log('Remove employee response:', response.data);
      
      if (response.data.success) {
        alert(`✅ Employee removed successfully`);
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to remove employee - Full error:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      alert(`❌ Failed to remove employee: ${error.response?.data?.detail || error.message}\n\nPlease check browser console for details.`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Team Management</h2>
        <button
          onClick={() => setShowAddEmployee(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Add Team Member
        </button>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {employees.map((employee) => (
            <li key={employee.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {employee.first_name} {employee.last_name}
                  </p>
                  <p className="text-sm text-gray-500">{employee.email}</p>
                  <p className="text-xs text-gray-400">
                    Created: {new Date(employee.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEditingEmployee(employee)}
                    className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Edit Permissions
                  </button>
                  <button
                    onClick={() => removeEmployee(employee.id)}
                    className="inline-flex items-center px-2 py-1 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50"
                  >
                    Remove
                  </button>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Add Employee Modal */}
      {showAddEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Team Member</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  value={newEmployee.first_name}
                  onChange={(e) => setNewEmployee({...newEmployee, first_name: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  value={newEmployee.last_name}
                  onChange={(e) => setNewEmployee({...newEmployee, last_name: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  value={newEmployee.password}
                  onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={createEmployee}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Add Employee
              </button>
              <button
                onClick={() => setShowAddEmployee(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Impersonation Tab Component
const ImpersonationTab = ({ hoas, onImpersonate }) => {
  const [selectedHOA, setSelectedHOA] = useState('');
  const [selectedRole, setSelectedRole] = useState('homeowner');
  const [reason, setReason] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  
  // Audit trail state
  const [auditRecords, setAuditRecords] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditFilter, setAuditFilter] = useState('');
  const [showAuditTrail, setShowAuditTrail] = useState(false);

  // Fetch audit trail
  const fetchAuditTrail = async () => {
    setLoadingAudit(true);
    try {
      const params = new URLSearchParams();
      if (auditFilter) {
        params.append('hoa_id', auditFilter);
      }
      params.append('limit', '100'); // Get more records for audit
      
      const response = await axios.get(`/api/platform-admin/impersonation-audit?${params}`);
      if (response.data.success) {
        setAuditRecords(response.data.audit_records);
      }
    } catch (error) {
      console.error('Failed to fetch audit trail:', error);
      alert('Failed to load audit trail. You may not have permission to view this data.');
    } finally {
      setLoadingAudit(false);
    }
  };

  // Load audit trail when component mounts or filter changes
  React.useEffect(() => {
    if (showAuditTrail) {
      fetchAuditTrail();
    }
  }, [showAuditTrail, auditFilter]);

  // Load users list for selected HOA and role
  const loadUsers = async (hoaId, role, q='') => {
    if (!hoaId) return;
    try {
      setLoadingUsers(true);
      const params = new URLSearchParams();
      if (role) params.append('role', role);
      if (q) params.append('q', q);
      const res = await axios.get(`/api/platform-admin/hoa/${hoaId}/users?${params.toString()}`);
      if (res.data?.success) {
        setAvailableUsers(res.data.users);
        if (res.data.users.length > 0) setSelectedUser(res.data.users[0].id);
      }
    } catch (e) {
      console.error('Failed to load users', e);
      alert('Failed to load users for impersonation.');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (selectedHOA) {
      loadUsers(selectedHOA, selectedRole, userQuery);
    } else {
      setAvailableUsers([]);
      setSelectedUser('');
    }
  }, [selectedHOA, selectedRole, userQuery]);

  // Fetch users when HOA is selected
  const fetchHOAUsers = async (hoaId) => {
    if (!hoaId) {
      setAvailableUsers([]);
      setSelectedUser('');
      return;
    }

    setLoadingUsers(true);
    try {
      // Try different endpoints to get users
      let response;
      try {
        response = await axios.get(`/api/platform-admin/hoas/${hoaId}/users`);
      } catch (error) {
        // If that fails, try alternative endpoint
        response = await axios.get(`/api/platform-admin/hoa/${hoaId}/users`);
      }
      
      if (response.data && response.data.users) {
        setAvailableUsers(response.data.users);
        // Auto-select first user with the selected role
        const usersWithRole = response.data.users.filter(user => user.role === selectedRole);
        if (usersWithRole.length > 0) {
          setSelectedUser(usersWithRole[0].id);
        } else if (response.data.users.length > 0) {
          setSelectedUser(response.data.users[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch HOA users:', error);
      // If we can't fetch users, we'll try without target_user_id and let backend handle it
      setAvailableUsers([]);
      setSelectedUser('');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleHOAChange = (hoaId) => {
    setSelectedHOA(hoaId);
    fetchHOAUsers(hoaId);
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    // Auto-select user with this role if available
    const usersWithRole = availableUsers.filter(user => user.role === role);
    if (usersWithRole.length > 0) {
      setSelectedUser(usersWithRole[0].id);
    }
  };

  const handleImpersonate = async () => {
    if (!selectedHOA) {
      alert('⚠️ Please select an HOA community first');
      return;
    }
    
    if (!reason.trim()) {
      alert('⚠️ Please provide a reason for impersonation');
      return;
    }

    try {
      console.log('Starting impersonation with:', { selectedHOA, selectedRole, reason, selectedUser });
      
      // Use selectedUser if available, otherwise let backend pick one
      const result = await onImpersonate(selectedHOA, selectedRole, reason, selectedUser || null);
      
      if (result && result.success) {
        alert(`✅ Successfully switched to ${selectedRole.replace('_', ' ').toUpperCase()} view`);
        // The user should now be redirected to the main dashboard
      } else {
        alert(`❌ Impersonation failed: ${result?.error || 'Unknown error'}`);
        console.error('Impersonation failed:', result);
      }
    } catch (error) {
      console.error('Impersonation error:', error);
      alert(`❌ Impersonation failed: ${error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">View Toggle (Impersonation)</h2>
          <p className="text-sm text-gray-600 mt-1">
            Switch to different user views to help troubleshoot issues and guide HOA board members.
          </p>
        </div>
        <button
          onClick={() => setShowAuditTrail(!showAuditTrail)}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
        >
          {showAuditTrail ? '🚀 Show Impersonation' : '📊 View Audit Trail'}
        </button>
      </div>

      {!showAuditTrail ? (
        // Impersonation interface
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Select HOA Community</label>
              <select
                value={selectedHOA}
                onChange={(e) => handleHOAChange(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Choose a community...</option>
                {hoas.map((hoa) => (
                  <option key={hoa.id} value={hoa.id}>
                    {hoa.name} ({hoa.code})
            <div>
              <label className="block text-sm font-medium text-gray-700">Select User (optional)</label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={userQuery}
                  onChange={(e) => setUserQuery(e.target.value)}
                  placeholder="Search name or email"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm w-64"
                />
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1"
                >
                  <option value="">Auto-select first available</option>
                  {availableUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name} - {u.email} ({u.role})</option>
                  ))}
                </select>
              </div>
              {loadingUsers && (
                <p className="text-xs text-gray-500 mt-1">Loading users…</p>
              )}
            </div>

                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">View As</label>
              <select
                value={selectedRole}
                onChange={(e) => handleRoleChange(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="homeowner">Homeowner</option>
                <option value="board_member">Board Member</option>
                <option value="president">HOA President</option>
                <option value="treasurer">HOA Treasurer</option>
                <option value="secretary">HOA Secretary</option>
              </select>
            </div>

            {selectedHOA && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Target User {loadingUsers && <span className="text-sm text-gray-500">(Loading...)</span>}
                </label>
                {availableUsers.length > 0 ? (
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Auto-select user with role</option>
                    {availableUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email}) - {user.role}
                      </option>
                    ))}
                  </select>
                ) : !loadingUsers ? (
                  <div className="mt-1 block w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-600">
                    {selectedHOA ? 'No users found. Will try to auto-select a user with the specified role.' : 'Select an HOA first'}
                  </div>
                ) : (
                  <div className="mt-1 block w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-600">
                    Loading users...
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">Reason for Access</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows="3"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g., Helping board member troubleshoot user approval issue"
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> All impersonation sessions are logged for audit purposes.
                    Only use this feature to provide support and troubleshoot issues.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleImpersonate}
              disabled={!selectedHOA || !reason.trim()}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Switch to {selectedRole.replace('_', ' ').toUpperCase()} View
            </button>
          </div>
        </div>
      </div>
      ) : (
        // Audit Trail interface
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Impersonation Audit Trail</h3>
                <div className="flex items-center space-x-3">
                  <select
                    value={auditFilter}
                    onChange={(e) => setAuditFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">All HOAs</option>
                    {hoas.map(hoa => (
                      <option key={hoa.id} value={hoa.id}>{hoa.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={fetchAuditTrail}
                    disabled={loadingAudit}
                    className="px-3 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 disabled:opacity-50"
                  >
                    🔄 Refresh
                  </button>
                </div>
              </div>

              {loadingAudit ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading audit trail...</p>
                </div>
              ) : auditRecords.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No impersonation records found.</p>
                  <p className="text-sm text-gray-400 mt-1">Audit trail will appear here when staff members use impersonation.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HOA</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {auditRecords.map((record, index) => (
                        <tr key={record.admin_id + record.started_at + index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div>
                              <div className="font-medium text-gray-900">{record.admin_name}</div>
                              <div className="text-gray-500 text-xs">{record.admin_email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div>
                              <div className="font-medium text-gray-900">{record.target_user_name}</div>
                              <div className="text-gray-500 text-xs">{record.target_user_email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {record.hoa_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {record.target_role.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                            <div className="truncate" title={record.reason}>
                              {record.reason}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(record.started_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              record.duration === 'Active' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {record.duration}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab = ({ hoas }) => {
  const totalRevenue = hoas.length * 499; // Simplified calculation
  const avgPropertiesPerHOA = hoas.length > 0 ? Math.round(hoas.reduce((sum, hoa) => sum + hoa.total_properties, 0) / hoas.length) : 0;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Platform Analytics</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                <span className="text-white text-sm">💰</span>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Annual Revenue</dt>
                <dd className="text-lg font-medium text-gray-900">${totalRevenue.toLocaleString()}</dd>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                <span className="text-white text-sm">📊</span>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Avg Properties/HOA</dt>
                <dd className="text-lg font-medium text-gray-900">{avgPropertiesPerHOA}</dd>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mr-3">
                <span className="text-white text-sm">📈</span>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Growth Rate</dt>
                <dd className="text-lg font-medium text-gray-900">+12.5%</dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">HOA Distribution by Size</h3>
          <div className="mt-5">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Small (1-100 homes)</span>
                <span className="text-sm font-medium">{hoas.filter(h => h.total_properties <= 100).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Medium (101-300 homes)</span>
                <span className="text-sm font-medium">{hoas.filter(h => h.total_properties > 100 && h.total_properties <= 300).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Large (300+ homes)</span>
                <span className="text-sm font-medium">{hoas.filter(h => h.total_properties > 300).length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// HOA Configuration Management Modal Component
const HOAConfigurationModal = ({ hoa, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchHOAConfiguration();
  }, [hoa.id]);

  const fetchHOAConfiguration = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/platform-admin/hoa/${hoa.id}/configuration`);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch HOA configuration:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to access HOA Configuration Management. Please contact your administrator.');
        onClose();
      } else {
        alert('Failed to load HOA configuration. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async (updates) => {
    try {
      setSaving(true);
      const response = await axios.put(`/api/platform-admin/hoa/${hoa.id}/configuration`, updates);
      if (response.data.success) {
        alert(`✅ ${response.data.message}${response.data.notification_sent ? '\n📧 Primary board member has been notified of changes.' : ''}`);
        onSave();
      }
    } catch (error) {
      console.error('Failed to save configuration:', error);
      alert(`❌ Failed to save configuration: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const configTabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'subscription', name: 'Subscription', icon: '💳' },
    { id: 'sync', name: 'Sync & Diagnostics', icon: '🔧' },
    { id: 'aws', name: 'AWS Monitoring', icon: '☁️' },
    { id: 'amenities', name: 'Amenities', icon: '🏊' },
    { id: 'payments', name: 'Payments', icon: '💰' },
    { id: 'board', name: 'Board Members', icon: '👥' },
    { id: 'settings', name: 'Settings', icon: '⚙️' }
  ];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white min-h-[80vh] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-6 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🔧 HOA Configuration Management</h2>
            <p className="text-sm text-gray-600 mt-1">
              {config.hoa?.name} ({config.hoa?.code}) • {config.statistics?.active_users || 0} members
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Tabs - Mobile Friendly with Horizontal Scroll */}
        <div className="border-b border-gray-200 mb-6 flex-shrink-0 overflow-x-auto">
          <nav className="-mb-px flex space-x-4 md:space-x-8 min-w-max px-1">
            {configTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-2 md:px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="text-base">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.name}</span>
                <span className="sm:hidden text-xs">{tab.name.split(' ')[0]}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content with Scrolling */}
        <div className="flex-1 overflow-y-auto pr-2">
          {activeTab === 'overview' && <ConfigOverviewTab config={config} />}
          {activeTab === 'subscription' && <ConfigSubscriptionTab config={config} onSave={saveConfiguration} saving={saving} />}
          {activeTab === 'sync' && <SyncDiagnosticsTab config={config} onUpdate={fetchHOAConfiguration} />}
          {activeTab === 'aws' && <AWSMonitoringTab />}
          {activeTab === 'amenities' && <ConfigAmenitiesTab config={config} onSave={saveConfiguration} saving={saving} />}
          {activeTab === 'payments' && <ConfigPaymentsTab config={config} onSave={saveConfiguration} saving={saving} />}
          {activeTab === 'board' && <ConfigBoardTab config={config} onSave={saveConfiguration} />}
          {activeTab === 'settings' && <ConfigSettingsTab config={config} />}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 pt-4 mt-6 flex-shrink-0">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Configuration Tab Components - ENHANCED: With seat/storage tracking and real data
const ConfigOverviewTab = ({ config }) => {
  const [exporting, setExporting] = useState(false);
  const [exportingConsents, setExportingConsents] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationData, setNotificationData] = useState({
    subject: '',
    message: '',
    target: 'all'
  });

  // Export HOA Users functionality  
  const handleExportUsers = async () => {
    try {
      setExporting(true);
      
      // Get auth token
      const platformToken = localStorage.getItem('platform_token');
      const hoaToken = localStorage.getItem('hoa_token');
      const token = platformToken || hoaToken;
      
      if (!token) {
        alert('❌ No authentication token found. Please log in again.');
        return;
      }
      
      const response = await api.get(`/api/platform-admin/hoa/${config.hoa?.id}/export-users`, {
        responseType: 'blob'
      });
      
      const fileName = `${config.hoa?.name || 'HOA'}_users_export_${new Date().toISOString().split('T')[0]}.csv`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert(`✅ Successfully exported users for ${config.hoa?.name}`);
    } catch (error) {
      console.error('Failed to export HOA users:', error);
      alert(`❌ Failed to export users: ${error.response?.data?.detail || error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Export Electronic Consents functionality
  const handleExportConsents = async () => {
    try {
      setExportingConsents(true);
      
      // Get auth token
      const platformToken = localStorage.getItem('platform_token');
      const hoaToken = localStorage.getItem('hoa_token');
      const token = platformToken || hoaToken;
      
      if (!token) {
        alert('❌ No authentication token found. Please log in again.');
        return;
      }
      
      const response = await api.get(`/api/platform-admin/hoa/${config.hoa?.id}/electronic-consents/export?format=csv`, {
        responseType: 'blob'
      });
      
      const fileName = `${config.hoa?.name || 'HOA'}_electronic_consents_${new Date().toISOString().split('T')[0]}.csv`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert(`✅ Successfully exported electronic consents for ${config.hoa?.name}`);
    } catch (error) {
      console.error('Failed to export electronic consents:', error);
      alert(`❌ Failed to export consents: ${error.response?.data?.detail || error.message}`);
    } finally {
      setExportingConsents(false);
    }
  };

  // Send Notification functionality
  const handleSendNotification = async () => {
    if (!notificationData.subject || !notificationData.message) {
      alert('Please fill in both subject and message');
      return;
    }

    try {
      setSendingNotification(true);
      const response = await axios.post(`/api/platform-admin/hoa/${config.hoa?.id}/send-notification`, notificationData);
      
      alert(`✅ Successfully sent notification to ${response.data.sent_count} users`);
      setShowNotificationModal(false);
      setNotificationData({ subject: '', message: '', target: 'all' });
    } catch (error) {
      console.error('Failed to send notification:', error);
      alert(`❌ Failed to send notification: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSendingNotification(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ENHANCED: Statistics Cards with Seat/Storage Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-sm">👥</span>
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900">User Seats</p>
              <p className="text-lg font-bold text-blue-900">
                {config.statistics?.active_users || 0}/{config.subscription?.plan?.max_accounts || 600}
              </p>
              <p className="text-xs text-blue-600">
                {(((config.statistics?.active_users || 0) / (config.subscription?.plan?.max_accounts || 600)) * 100).toFixed(1)}% used
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-sm">💾</span>
            </div>
            <div>
              <p className="text-sm font-medium text-green-900">Storage Used</p>
              <p className="text-lg font-bold text-green-900">
                {((config.statistics?.storage_used || 0) / (1024 * 1024 * 1024)).toFixed(1)}GB/{((config.subscription?.plan?.storage_gb || 5) + (config.subscription?.overrides?.extra_storage || 0))}GB
              </p>
              <p className="text-xs text-green-600">
                {(((config.statistics?.storage_used || 0) / ((config.subscription?.plan?.storage_gb || 5) * 1024 * 1024 * 1024)) * 100).toFixed(1)}% used
              </p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-sm">⏳</span>
            </div>
            <div>
              <p className="text-sm font-medium text-yellow-900">Pending Users</p>
              <p className="text-lg font-bold text-yellow-900">{config.statistics?.pending_users || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-sm">💳</span>
            </div>
            <div>
              <p className="text-sm font-medium text-purple-900">Subscription</p>
              <p className="text-lg font-bold text-purple-900 capitalize">
                {config.subscription?.plan?.tier || 'Basic'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ENHANCED: User Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-sm">👔</span>
            </div>
            <div>
              <p className="text-sm font-medium text-indigo-900">Board Members</p>
              <p className="text-lg font-bold text-indigo-900">{config.statistics?.board_members_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-sm">🏠</span>
            </div>
            <div>
              <p className="text-sm font-medium text-teal-900">Homeowners</p>
              <p className="text-lg font-bold text-teal-900">{config.statistics?.homeowners_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-sm">🔧</span>
            </div>
            <div>
              <p className="text-sm font-medium text-orange-900">Team Members</p>
              <p className="text-lg font-bold text-orange-900">{config.statistics?.team_members_count || 0}</p>
              <p className="text-xs text-orange-600">Maintenance & Security</p>
            </div>
          </div>
        </div>
      </div>

      {/* ENHANCED: HOA Information with Real Data */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">HOA Information</h3>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Name</dt>
            <dd className="text-sm text-gray-900">{config.hoa?.name}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Code</dt>
            <dd className="text-sm text-gray-900 font-mono">{config.hoa?.code}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Address</dt>
            <dd className="text-sm text-gray-900">{config.hoa?.address}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Contact Email</dt>
            <dd className="text-sm text-gray-900">{config.hoa?.contact_email}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Total Properties</dt>
            <dd className="text-sm text-gray-900">
              {config.hoa?.total_properties || config.statistics?.total_properties || 0}
              {config.subscription?.overrides?.extra_accounts && (
                <span className="text-xs text-indigo-600 ml-1">
                  (+{config.subscription.overrides.extra_accounts} override)
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Established Date</dt>
            <dd className="text-sm text-gray-900">
              {config.hoa?.established_date ? new Date(config.hoa.established_date).toLocaleDateString() : 'Not specified'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Setup Status</dt>
            <dd className="text-sm text-gray-900">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  config.hoa?.setup_completed 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {config.hoa?.setup_completed ? '✅ Complete' : '⏳ In Progress'}
                </span>
                {!config.hoa?.setup_completed && (
                  <span className="text-xs text-gray-500" title="HOA has not completed all onboarding checklist items yet">
                    (Onboarding checklist incomplete)
                  </span>
                )}
              </div>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Created Date</dt>
            <dd className="text-sm text-gray-900">
              {config.hoa?.created_at ? new Date(config.hoa.created_at).toLocaleDateString() : 'Unknown'}
            </dd>
          </div>
        </dl>
      </div>

      {/* ENHANCED: Quick Actions with Real Functionality */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button 
            onClick={handleExportUsers}
            disabled={exporting}
            className="inline-flex items-center justify-center px-4 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            {exporting ? 'Exporting...' : 'Export Users'}
          </button>

          <button 
            onClick={handleExportConsents}
            disabled={exportingConsents}
            className="inline-flex items-center justify-center px-4 py-2 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {exportingConsents ? 'Exporting...' : 'Export E-Consents'}
          </button>
          
          <button 
            onClick={() => setShowNotificationModal(true)}
            className="inline-flex items-center justify-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Send Notification
          </button>
          
          <button className="inline-flex items-center justify-center px-4 py-2 border border-purple-300 text-sm font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100">
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H9a2 2 0 01-2-2z" />
            </svg>
            Generate Report
          </button>
        </div>
      </div>

      {/* Send Notification Modal */}
      {showNotificationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">📧 Send Notification to HOA Members</h3>
              <p className="text-sm text-gray-600 mt-1">Send notification to members of {config.hoa?.name}</p>
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Target Audience</label>
                <select
                  value={notificationData.target}
                  onChange={(e) => setNotificationData({...notificationData, target: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All Users ({config.statistics?.active_users || 0} users)</option>
                  <option value="homeowners">Homeowners Only ({config.statistics?.homeowners_count || 0} users)</option>
                  <option value="board_members">Board Members Only ({config.statistics?.board_members_count || 0} users)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Subject</label>
                <input
                  type="text"
                  value={notificationData.subject}
                  onChange={(e) => setNotificationData({...notificationData, subject: e.target.value})}
                  placeholder="Notification subject line..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  value={notificationData.message}
                  onChange={(e) => setNotificationData({...notificationData, message: e.target.value})}
                  rows={6}
                  placeholder="Write your notification message here..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNotificationModal(false);
                  setNotificationData({ subject: '', message: '', target: 'all' });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSendNotification}
                disabled={sendingNotification || !notificationData.subject || !notificationData.message}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingNotification ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ConfigSubscriptionTab = ({ config, onSave, saving }) => {
  const [subscriptionConfig, setSubscriptionConfig] = useState(config.subscription || {});
  const [stripeAddons, setStripeAddons] = useState([]);
  const [loadingStripeData, setLoadingStripeData] = useState(true);
  const [processingAddon, setProcessingAddon] = useState(null);

  // Fetch real Stripe add-on data on mount
  useEffect(() => {
    fetchStripeAddons();
  }, [config.hoa?.id]);

  const fetchStripeAddons = async () => {
    if (!config.hoa?.id) {
      console.warn('ConfigSubscriptionTab: No HOA ID found in config');
      setLoadingStripeData(false);
      return;
    }
    
    try {
      setLoadingStripeData(true);
      console.log(`Fetching Stripe addons for HOA: ${config.hoa.id}`);
      const { data } = await api.get(`/api/platform-admin/hoa/${config.hoa.id}/stripe-addons`);
      console.log('Stripe addons response:', data);
      setStripeAddons(data.active_addons || []);
    } catch (error) {
      console.error('Failed to fetch Stripe addons:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoadingStripeData(false);
    }
  };

  // Handle add-on addition (actually updates Stripe!)
  const handleAddAddon = async (addonId, addon, quantity = 1) => {
    setProcessingAddon(addonId);
    try {
      await api.post(`/api/platform-admin/hoa/${config.hoa.id}/add-addon`, {
        addon_id: addonId,
        quantity: quantity
      });
      
      alert(`✅ Added ${quantity}x ${addon.name} to Stripe subscription\n\nFeatures will be enabled in the HOA.`);
      await fetchStripeAddons(); // Refresh
    } catch (error) {
      console.error('Error adding add-on:', error);
      alert(`❌ Failed to add ${addon.name}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcessingAddon(null);
    }
  };

  // Handle add-on removal (actually updates Stripe!)
  const handleRemoveAddon = async (addonId, addon) => {
    if (!window.confirm(`Remove ${addon.name} from Stripe subscription?\n\nThis will also disable features in the HOA.`)) return;
    
    setProcessingAddon(addonId);
    try {
      await api.post(`/api/platform-admin/hoa/${config.hoa.id}/remove-addon`, {
        addon_id: addonId
      });
      
      alert(`✅ Removed ${addon.name} from Stripe subscription`);
      await fetchStripeAddons(); // Refresh
    } catch (error) {
      console.error('Error removing add-on:', error);
      alert(`❌ Failed to remove ${addon.name}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcessingAddon(null);
    }
  };

  // Define all available add-ons with their properties
  const planTier = config.subscription?.plan?.tier || 'basic';
  
  const availableAddons = {
    voting_platform: {
      name: "E-Voting Platform",
      description: "Special issues & annual elections",
      monthly_price: 10,
      yearly_price: 100,
      included: planTier === 'premium', // Premium includes voting
      quantityBased: false
    },
    amenity_management: {
      name: "Amenity Management",
      description: "Full booking & management system",
      monthly_price: 10,
      yearly_price: 100,
      included: false,
      quantityBased: false
    },
    budget_finance: {
      name: "Advanced Budgeting & Financials",
      description: "Rollover, reports, balance sheet",
      monthly_price: 10,
      yearly_price: 100,
      included: planTier === 'standard' || planTier === 'premium', // Included in Standard+
      quantityBased: false
    },
    extra_homes: {
      name: "Additional Homes (+150 capacity)",
      description: "Expand community capacity",
      monthly_price: 4,
      yearly_price: 35,
      included: false,
      quantityBased: true
    },
    extra_storage: {
      name: "Additional Storage (+10GB)",
      description: "Extra document storage",
      monthly_price: 3,
      yearly_price: 30,
      included: false,
      quantityBased: true
    }
  };

  // Get active add-ons from Stripe
  const activeAddonIds = stripeAddons.map(a => a.addon_id);

  return (
    <div className="space-y-6">
      {/* Current Subscription */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Current Subscription</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Plan Tier</label>
            <p className="text-sm text-gray-900 capitalize font-medium">
              {planTier}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Annual Price</label>
            <p className="text-sm text-gray-900">${config.subscription?.plan?.price_per_year || 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Max Homes</label>
            <p className="text-sm text-gray-900">{config.subscription?.plan?.max_homes || 100}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Storage</label>
            <p className="text-sm text-gray-900">{config.subscription?.plan?.storage_gb || 15} GB</p>
          </div>
          {config.subscription?.stripe_subscription_id && (
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-500">Stripe Subscription ID</label>
              <p className="text-xs text-gray-600 font-mono">{config.subscription.stripe_subscription_id}</p>
            </div>
          )}
          {config.subscription?.end_date && (
            <div className="col-span-2 mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs font-medium text-blue-600 mb-1">Next Billing Date</div>
              <div className="text-sm font-bold text-blue-900">
                {new Date(config.subscription.end_date).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Active Add-ons (Live Stripe Data) */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-green-900 mb-4">✅ Active Add-ons (Live Stripe Data)</h3>
        {loadingStripeData ? (
          <p className="text-sm text-green-700">Loading Stripe data...</p>
        ) : stripeAddons.length === 0 ? (
          <p className="text-sm text-green-700">No active add-ons</p>
        ) : (
          <div className="space-y-2">
            {stripeAddons.map((addon) => {
              const addonInfo = availableAddons[addon.addon_id];
              return (
                <div key={addon.addon_id} className="flex items-center justify-between py-2 px-3 bg-white rounded border border-green-300">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{addonInfo?.name || addon.addon_id}</p>
                    {addon.quantity > 1 && (
                      <p className="text-xs text-gray-600">Quantity: {addon.quantity}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveAddon(addon.addon_id, addonInfo)}
                    disabled={processingAddon === addon.addon_id}
                    className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                  >
                    {processingAddon === addon.addon_id ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Available Add-ons */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Available Add-ons</h3>
        <div className="space-y-3">
          {Object.entries(availableAddons).map(([addonId, addon]) => {
            const isActive = activeAddonIds.includes(addonId);
            const isIncluded = addon.included;
            
            return (
              <div key={addonId} className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded border border-gray-200">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">{addon.name}</p>
                    {isIncluded && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                        ✅ Included in {planTier}
                      </span>
                    )}
                    {isActive && !isIncluded && (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                        ✅ Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{addon.description}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    ${addon.monthly_price}/month or ${addon.yearly_price}/year
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isIncluded ? (
                    <span className="text-xs text-gray-500 italic">Included</span>
                  ) : isActive && addon.quantityBased ? (
                    // Show +/- buttons for active quantity-based add-ons
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleAddAddon(addonId, addon, 1)}
                        disabled={processingAddon === addonId}
                        className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 font-bold"
                        title="Add 1 more"
                      >
                        +1
                      </button>
                      <button
                        onClick={() => handleRemoveAddon(addonId, addon)}
                        disabled={processingAddon === addonId}
                        className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                      >
                        {processingAddon === addonId ? 'Removing...' : 'Remove All'}
                      </button>
                    </div>
                  ) : isActive ? (
                    <button
                      onClick={() => handleRemoveAddon(addonId, addon)}
                      disabled={processingAddon === addonId}
                      className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                    >
                      {processingAddon === addonId ? 'Removing...' : 'Remove'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAddAddon(addonId, addon, 1)}
                      disabled={processingAddon === addonId}
                      className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {processingAddon === addonId ? 'Adding...' : addon.quantityBased ? 'Add (+1)' : 'Add'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>⚠️ Important:</strong> Add/Remove buttons update the Stripe subscription immediately.
          Features will be enabled/disabled in the HOA, and billing will be adjusted at the next billing cycle.
        </p>
      </div>
    </div>
  );
};

const ConfigAmenitiesTab = ({ config, onSave, saving }) => {
  const [amenities, setAmenities] = useState(config.community_resources?.amenities || []);

  const availableAmenities = [
    'Swimming Pool', 'Tennis Court', 'Fitness Center/Gym', 'Clubhouse', 'Playground',
    'Walking Trails', 'BBQ/Picnic Area', 'Dog Park', 'Basketball Court', 'Volleyball Court',
    'Hot Tub/Spa', 'Business Center', 'Library', 'Community Garden', 'Concierge Service',
    'Valet Parking', 'Car Wash Station', 'EV Charging Stations', 'Rooftop Deck',
    'Game Room', 'Theater Room', 'Guest Parking', 'Storage Units', 'Mailroom'
  ];

  const handleToggleAmenity = (amenity) => {
    setAmenities(prev => 
      prev.includes(amenity) 
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity]
    );
  };

  const handleSaveAmenities = () => {
    onSave({ amenities });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">HOA Amenities</h3>
          <button
            onClick={handleSaveAmenities}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Amenities'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {availableAmenities.map(amenity => (
            <label key={amenity} className="flex items-center">
              <input
                type="checkbox"
                checked={amenities.includes(amenity)}
                onChange={() => handleToggleAmenity(amenity)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">{amenity}</span>
            </label>
          ))}
        </div>

        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-sm text-green-700">
            ✅ <strong>Immediately Editable:</strong> Amenity changes take effect immediately and will notify the primary board member.
          </p>
        </div>
      </div>
    </div>
  );
};

const ConfigPaymentsTab = ({ config, onSave, saving }) => {
  const [paymentConfig, setPaymentConfig] = useState(config.payment_configuration || {
    dues_amount: config.hoa?.annual_dues || 300,
    dues_frequency: config.hoa?.dues_frequency || 'annually', 
    due_day: config.hoa?.due_day || 1,
    due_month: config.hoa?.due_month || 1,
    late_fee_amount: config.hoa?.late_fee_amount || 25,
    late_fee_days: config.hoa?.late_fee_days || 30,
    payment_methods_enabled: config.hoa?.payment_methods_enabled || ['online'],
    online_payment_enabled: config.hoa?.online_payment_enabled !== false,
    check_payment_enabled: config.hoa?.check_payment_enabled || false,
    money_order_enabled: config.hoa?.money_order_enabled || false
  });

  const [paymentAccess, setPaymentAccess] = useState(config.payment_access || {
    access_level: 'treasurer_only', // 'treasurer_only', 'president_treasurer', 'all_board'
    allow_override: false
  });

  // Helper functions
  const getMonthName = (monthNum) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNum - 1] || 'January';
  };

  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const handleSavePaymentConfig = () => {
    onSave({ 
      payment_configuration: paymentConfig,
      payment_access: paymentAccess
    });
  };

  return (
    <div className="space-y-6">
      {/* ENHANCED: Current Payment Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Payment Configuration</h3>
          <button
            onClick={handleSavePaymentConfig}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              HOA Dues Amount (${paymentConfig.dues_frequency === 'annually' ? 'Annual' : 'Monthly'})
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                value={paymentConfig.dues_amount}
                onChange={(e) => setPaymentConfig({...paymentConfig, dues_amount: parseInt(e.target.value) || 0})}
                className="pl-8 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {paymentConfig.dues_frequency === 'annually' 
                ? 'Total annual dues amount'
                : `$${(paymentConfig.dues_amount * 12).toLocaleString()} annually`
              }
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Frequency</label>
            <select
              value={paymentConfig.dues_frequency}
              onChange={(e) => setPaymentConfig({...paymentConfig, dues_frequency: e.target.value})}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annually">Annually</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {paymentConfig.dues_frequency === 'annually' ? 'Annual Due Date' : 'Due Day of Month'}
            </label>
            {paymentConfig.dues_frequency === 'annually' ? (
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={paymentConfig.due_month || 1}
                  onChange={(e) => setPaymentConfig({...paymentConfig, due_month: parseInt(e.target.value)})}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value={1}>January</option>
                  <option value={2}>February</option>
                  <option value={3}>March</option>
                  <option value={4}>April</option>
                  <option value={5}>May</option>
                  <option value={6}>June</option>
                  <option value={7}>July</option>
                  <option value={8}>August</option>
                  <option value={9}>September</option>
                  <option value={10}>October</option>
                  <option value={11}>November</option>
                  <option value={12}>December</option>
                </select>
                <select
                  value={paymentConfig.due_day || 1}
                  onChange={(e) => setPaymentConfig({...paymentConfig, due_day: parseInt(e.target.value)})}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
            ) : (
              <select
                value={paymentConfig.due_day}
                onChange={(e) => setPaymentConfig({...paymentConfig, due_day: parseInt(e.target.value)})}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {paymentConfig.dues_frequency === 'annually' 
                ? `Annual dues due on ${getMonthName(paymentConfig.due_month || 1)} ${paymentConfig.due_day || 1}`
                : `Monthly dues due on the ${paymentConfig.due_day || 1}${getOrdinalSuffix(paymentConfig.due_day || 1)} of each month`
              }
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Late Fee Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                value={paymentConfig.late_fee_amount}
                onChange={(e) => setPaymentConfig({...paymentConfig, late_fee_amount: parseInt(e.target.value) || 0})}
                className="pl-8 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Late Fee Grace Period (Days)</label>
            <input
              type="number"
              value={paymentConfig.late_fee_days}
              onChange={(e) => setPaymentConfig({...paymentConfig, late_fee_days: parseInt(e.target.value) || 0})}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-md font-medium text-gray-900 mb-3">Accepted Payment Methods</h4>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={paymentConfig.online_payment_enabled}
                onChange={(e) => setPaymentConfig({...paymentConfig, online_payment_enabled: e.target.checked})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">💳 Online Payments (Credit/Debit Cards)</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={paymentConfig.check_payment_enabled}
                onChange={(e) => setPaymentConfig({...paymentConfig, check_payment_enabled: e.target.checked})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">📄 Personal Checks</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={paymentConfig.money_order_enabled}
                onChange={(e) => setPaymentConfig({...paymentConfig, money_order_enabled: e.target.checked})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">💰 Money Orders</span>
            </label>
          </div>
        </div>
      </div>

      {/* ENHANCED: Payment Access Permissions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">💼 Payment Management Access</h3>
        <p className="text-sm text-gray-600 mb-4">
          Control who can manage payment settings, view payment reports, and handle payment-related issues.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Access Level</label>
            <div className="space-y-2">
              <label className="flex items-start">
                <input
                  type="radio"
                  name="access_level"
                  value="treasurer_only"
                  checked={paymentAccess.access_level === 'treasurer_only'}
                  onChange={(e) => setPaymentAccess({...paymentAccess, access_level: e.target.value})}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 mt-0.5"
                />
                <div className="ml-2">
                  <span className="text-sm font-medium text-gray-900">Treasurer Only</span>
                  <p className="text-xs text-gray-500">Only the treasurer can manage payments and view financial reports</p>
                </div>
              </label>
              
              <label className="flex items-start">
                <input
                  type="radio"
                  name="access_level"
                  value="president_treasurer"
                  checked={paymentAccess.access_level === 'president_treasurer'}
                  onChange={(e) => setPaymentAccess({...paymentAccess, access_level: e.target.value})}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 mt-0.5"
                />
                <div className="ml-2">
                  <span className="text-sm font-medium text-gray-900">President & Treasurer</span>
                  <p className="text-xs text-gray-500">Both president and treasurer can manage payments</p>
                </div>
              </label>
              
              <label className="flex items-start">
                <input
                  type="radio"
                  name="access_level"
                  value="all_board"
                  checked={paymentAccess.access_level === 'all_board'}
                  onChange={(e) => setPaymentAccess({...paymentAccess, access_level: e.target.value})}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 mt-0.5"
                />
                <div className="ml-2">
                  <span className="text-sm font-medium text-gray-900">All Board Members</span>
                  <p className="text-xs text-gray-500">All board members can manage payments and view reports</p>
                </div>
              </label>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={paymentAccess.allow_override}
                onChange={(e) => setPaymentAccess({...paymentAccess, allow_override: e.target.checked})}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">🔓 Allow Platform Admin Override</span>
            </label>
            <p className="text-xs text-gray-500 ml-6">Platform administrators can always access payment settings regardless of this setting</p>
          </div>

          {/* FIXED: Add Save Button for Payment Access */}
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={handleSavePaymentConfig}
              disabled={saving}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : '💾 Save Payment Access Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* ENHANCED: Payment Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-4">📊 Payment Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm font-medium text-blue-800">Current Dues</p>
            <p className="text-lg font-bold text-blue-900">
              ${paymentConfig.dues_amount} {paymentConfig.dues_frequency === 'annually' ? '/year' : '/month'}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-800">Late Fee</p>
            <p className="text-lg font-bold text-blue-900">
              ${paymentConfig.late_fee_amount} after {paymentConfig.late_fee_days} days
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-800">Due Date</p>
            <p className="text-sm text-blue-800">
              {paymentConfig.dues_frequency === 'annually' 
                ? `${getMonthName(paymentConfig.due_month || 1)} ${paymentConfig.due_day || 1} annually`
                : `${paymentConfig.due_day || 1}${getOrdinalSuffix(paymentConfig.due_day || 1)} of each month`
              }
            </p>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-sm font-medium text-blue-800">Payment Methods</p>
          <p className="text-sm text-blue-800">
            {[
              paymentConfig.online_payment_enabled && 'Online',
              paymentConfig.check_payment_enabled && 'Check',
              paymentConfig.money_order_enabled && 'Money Order'
            ].filter(Boolean).join(', ') || 'None enabled'}
          </p>
        </div>
      </div>
    </div>
  );
};
const ConfigBoardTab = ({ config, onSave }) => {
  const boardMembers = config.board_members || [];
  const [editingMember, setEditingMember] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMember, setNewMember] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'board_member'
  });

  const handleAddMember = async () => {
    try {
      const response = await axios.post(`/api/platform-admin/hoa/${config.hoa.id}/board-members`, newMember);
      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        setNewMember({ email: '', first_name: '', last_name: '', role: 'board_member' });
        setShowAddForm(false);
        // Refresh the configuration
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to add board member:', error);
      alert(`❌ Failed to add board member: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleRemoveMember = async (memberId) => {
    const member = boardMembers.find(m => m.id === memberId);
    if (!window.confirm(`Are you sure you want to remove ${member?.first_name} ${member?.last_name} from the board? This will change their role to regular homeowner.`)) {
      return;
    }

    try {
      const response = await axios.delete(`/api/platform-admin/hoa/${config.hoa.id}/board-members/${memberId}`);
      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        // Refresh the configuration
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to remove board member:', error);
      alert(`❌ Failed to remove board member: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const response = await axios.put(`/api/platform-admin/hoa/${config.hoa.id}/board-members/${memberId}`, {
        role: newRole
      });
      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        // Refresh the configuration
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to update member role:', error);
      alert(`❌ Failed to update member role: ${error.response?.data?.detail || error.message}`);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'president': return 'bg-red-100 text-red-800';
      case 'vice_president': return 'bg-orange-100 text-orange-800';
      case 'treasurer': return 'bg-green-100 text-green-800';
      case 'secretary': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const setPlatformAdminByEmail = async () => {
    const email = prompt('Enter user email to set as Platform Admin:');
    if (!email) return;
    try {
      const response = await axios.put('/api/platform-admin/users/role-by-email', { email, role: 'platform_admin' });
      if (response.data?.success) {
        alert(`✅ ${email} is now Platform Admin`);
      } else {
        alert('❌ Failed to update platform admin');
      }
    } catch (error) {
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    }
  };

  const resetPasswordByEmail = async () => {
    const email = prompt('Enter user email to reset password:');
    if (!email) return;
    try {
      const response = await axios.post('/api/platform-admin/users/reset-password-by-email', { email });
      if (response.data?.success) {
        alert(`✅ Temporary password for ${email}:\n${response.data.temp_password}`);
      }
    } catch (error) {
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'vice_president': return 'Vice President';
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };
  const handleResetPassword = async (memberId, email) => {
    if (!window.confirm(`Generate a new temporary password for ${email}?`)) return;
    try {
      const response = await axios.post(`/api/platform-admin/hoa/${config.hoa.id}/board-members/${memberId}/reset-password`);
      if (response.data.success) {
        alert(`✅ Temporary password generated for ${email}:\n${response.data.temp_password}`);
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert(`❌ Failed to reset password: ${error.response?.data?.detail || error.message}`);
    }
  };


  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Board Members ({boardMembers.length})</h3>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Board Member
          </button>
        </div>

        {/* Add Member Form */}
        {showAddForm && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="text-md font-medium text-gray-900 mb-3">Add New Board Member</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="member@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={newMember.role}
                  onChange={(e) => setNewMember({...newMember, role: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="board_member">Board Member</option>
                  <option value="president">President</option>
                  <option value="vice_president">Vice President</option>
                  <option value="treasurer">Treasurer</option>
                  <option value="secretary">Secretary</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  value={newMember.first_name}
                  onChange={(e) => setNewMember({...newMember, first_name: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  value={newMember.last_name}
                  onChange={(e) => setNewMember({...newMember, last_name: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="mt-4 flex space-x-3">
              <button
                onClick={handleAddMember}
                disabled={!newMember.email || !newMember.first_name || !newMember.last_name}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                Add Member
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewMember({ email: '', first_name: '', last_name: '', role: 'board_member' });
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
        {boardMembers.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No board members configured yet.</p>
            <p className="text-sm text-gray-400 mt-1">Click "Add Board Member" to get started.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {boardMembers.map((member) => (
              <div key={member.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {member.first_name?.charAt(0)}{member.last_name?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {member.first_name} {member.last_name}
                      </h4>
                      <p className="text-sm text-gray-600">{member.email}</p>
                    </div>
                    <button
                      onClick={() => handleResetPassword(member.id, member.email)}
                      className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                    >
                      Reset Password
                    </button>

                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <select
                      value={member.role}
                      onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                      className="text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="board_member">Board Member</option>
                      <option value="president">President</option>
                      <option value="vice_president">Vice President</option>
                      <option value="treasurer">Treasurer</option>
                      <option value="secretary">Secretary</option>
                    </select>
                    
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-sm text-blue-700">
            ✅ <strong>Full Management:</strong> You can now add, remove, and change roles of board members. Changes are applied immediately and board members are notified.
          </p>
        </div>
      </div>
    </div>
  );
};

const ConfigSettingsTab = ({ config }) => {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">System Settings</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Communication Preferences</h4>
            <p className="text-sm text-gray-600 mb-2">Configure how the HOA prefers to communicate with members.</p>
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-700">
                ✅ <strong>Immediately Editable:</strong> Communication settings can be updated instantly.
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">HOA Maintenance Responsibilities</h4>
            <p className="text-sm text-gray-600 mb-2">Define what maintenance the HOA is responsible for.</p>
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-700">
                ✅ <strong>Immediately Editable:</strong> Maintenance responsibility settings can be updated instantly.
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Governing Documents</h4>
            <p className="text-sm text-gray-600 mb-2">Official HOA documents, bylaws, and CC&Rs.</p>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-700">
                ⚠️ <strong>Requires Ticket:</strong> Changes to governing documents require legal review and approval.
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Legal Structure</h4>
            <p className="text-sm text-gray-600 mb-2">HOA incorporation details and legal status.</p>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-700">
                ⚠️ <strong>Requires Ticket:</strong> Legal structure changes require administrative approval.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Registration Codes Tab Component
const RegistrationCodesTab = ({ hoas }) => {
  const [selectedHOA, setSelectedHOA] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Individual codes state
  const [individualEmails, setIndividualEmails] = useState('');
  const [individualExpiry, setIndividualExpiry] = useState(7);
  const [sendEmails, setSendEmails] = useState(true);
  
  // Shared code state
  const [sharedMaxUses, setSharedMaxUses] = useState('');
  const [sharedExpiry, setSharedExpiry] = useState(7);
  
  // File upload state
  const [uploadFile, setUploadFile] = useState(null);

  const fetchHOACodes = async (hoaId) => {
    if (!hoaId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`/api/platform-admin/registration-codes/${hoaId}`);
      setCodes(response.data.codes || []);
    } catch (error) {
      console.error('Failed to fetch codes:', error);
      alert('Failed to fetch registration codes');
    } finally {
      setLoading(false);
    }
  };

  const createIndividualCodes = async () => {
    if (!selectedHOA) {
      alert('Please select an HOA first');
      return;
    }
    
    const emails = individualEmails.split('\n').map(e => e.trim()).filter(e => e && e.includes('@'));
    
    if (emails.length === 0) {
      alert('Please enter at least one valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/platform-admin/registration-codes/individual', {
        hoa_id: selectedHOA,
        emails: emails,
        expires_in_days: individualExpiry,
        send_emails: sendEmails,
        registration_base_url: "https://login-tracking-hub.preview.emergentagent.com/register"
      });

      alert(`✅ Created ${emails.length} individual codes${sendEmails ? ' and sent welcome emails' : ''}`);
      setIndividualEmails('');
      fetchHOACodes(selectedHOA);
    } catch (error) {
      console.error('Failed to create codes:', error);
      alert(`❌ Failed to create codes: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createSharedCode = async () => {
    if (!selectedHOA) {
      alert('Please select an HOA first');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/platform-admin/registration-codes/shared', {
        hoa_id: selectedHOA,
        max_uses: sharedMaxUses ? parseInt(sharedMaxUses) : null,
        expires_in_days: sharedExpiry
      });

      alert(`✅ Created shared code: ${response.data.code.code}`);
      setSharedMaxUses('');
      fetchHOACodes(selectedHOA);
    } catch (error) {
      console.error('Failed to create shared code:', error);
      alert(`❌ Failed to create shared code: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const uploadCSVFile = async () => {
    if (!selectedHOA) {
      alert('Please select an HOA first');
      return;
    }
    
    if (!uploadFile) {
      alert('Please select a CSV file');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const response = await axios.post(
        `/api/platform-admin/registration-codes/bulk-upload?hoa_id=${selectedHOA}&expires_in_days=${individualExpiry}&send_emails=${sendEmails}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      alert(`✅ Processed ${response.data.emails_processed} emails and created ${response.data.codes_created} codes`);
      setUploadFile(null);
      document.getElementById('csvFile').value = '';
      fetchHOACodes(selectedHOA);
    } catch (error) {
      console.error('Failed to upload CSV:', error);
      alert(`❌ Failed to process CSV: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const cancelCodes = async (codeIds) => {
    if (!window.confirm(`Are you sure you want to cancel ${codeIds.length} registration codes? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/platform-admin/registration-codes/cancel', {
        code_ids: codeIds
      });

      alert(`✅ Cancelled ${response.data.cancelled_count} codes`);
      fetchHOACodes(selectedHOA);
    } catch (error) {
      console.error('Failed to cancel codes:', error);
      alert(`❌ Failed to cancel codes: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    return new Date() > new Date(expiresAt);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🔑 Registration Code Management</h2>
          
          {/* HOA Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select HOA Community</label>
            <select
              value={selectedHOA}
              onChange={(e) => {
                setSelectedHOA(e.target.value);
                if (e.target.value) fetchHOACodes(e.target.value);
              }}
              className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Choose a community...</option>
              {hoas.map((hoa) => (
                <option key={hoa.id} value={hoa.id}>
                  {hoa.name} ({hoa.code})
                </option>
              ))}
            </select>
          </div>

          {/* Sub-tabs */}
          {selectedHOA && (
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'overview', name: 'Active Codes', icon: '📋' },
                  { id: 'individual', name: 'Individual Codes', icon: '👤' },
                  { id: 'shared', name: 'Shared Code', icon: '🏘️' },
                  { id: 'bulk', name: 'Bulk Upload', icon: '📤' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSubTab(tab.id)}
                    className={`${
                      activeSubTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                  >
                    {tab.icon} {tab.name}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* Tab Content */}
      {selectedHOA && (
        <>
          {/* Overview Tab */}
          {activeSubTab === 'overview' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Active Registration Codes</h3>
                  <button
                    onClick={() => fetchHOACodes(selectedHOA)}
                    disabled={loading}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    🔄 Refresh
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="mt-2 text-sm text-gray-600">Loading codes...</p>
                  </div>
                ) : codes.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No registration codes found.</p>
                    <p className="text-sm text-gray-400 mt-1">Create individual or shared codes using the tabs above.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {codes.map((code) => (
                      <div key={code.id} className={`border rounded-lg p-4 ${isExpired(code.expires_at) ? 'bg-red-50 border-red-200' : code.is_active ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <span className="text-lg font-mono font-bold text-gray-900">{code.code}</span>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                code.code_type === 'individual' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                              }`}>
                                {code.code_type === 'individual' ? '👤 Individual' : '🏘️ Shared'}
                              </span>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                isExpired(code.expires_at) ? 'bg-red-100 text-red-800' :
                                code.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {isExpired(code.expires_at) ? '⏰ Expired' : code.is_active ? '✅ Active' : '❌ Cancelled'}
                              </span>
                            </div>
                            
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Created:</span> {formatDate(code.created_at)}
                              </div>
                              <div>
                                <span className="font-medium">Expires:</span> {formatDate(code.expires_at)}
                              </div>
                              {code.code_type === 'individual' ? (
                                <div>
                                  <span className="font-medium">Email:</span> {code.email}
                                </div>
                              ) : (
                                <div>
                                  <span className="font-medium">Uses:</span> {code.current_uses || 0} 
                                  {code.max_uses ? ` / ${code.max_uses}` : ' (unlimited)'}
                                </div>
                              )}
                            </div>

                            {code.code_type === 'individual' && code.is_used && (
                              <div className="mt-2 text-sm text-gray-500">
                                Used by: {code.used_by} on {formatDate(code.used_at)}
                              </div>
                            )}
                          </div>
                          
                          <div className="ml-4">
                            {code.is_active && !isExpired(code.expires_at) && (
                              <button
                                onClick={() => cancelCodes([code.id])}
                                disabled={loading}
                                className="text-red-600 hover:text-red-900 text-sm font-medium"
                              >
                                Cancel Code
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Individual Codes Tab */}
          {activeSubTab === 'individual' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create Individual Registration Codes</h3>
                <p className="text-sm text-gray-600 mb-6">Create unique codes for specific email addresses. Each code can only be used once by the assigned email.</p>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Addresses (one per line)</label>
                    <textarea
                      value={individualEmails}
                      onChange={(e) => setIndividualEmails(e.target.value)}
                      placeholder="homeowner1@email.com&#10;homeowner2@email.com&#10;homeowner3@email.com"
                      rows="6"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="mt-1 text-sm text-gray-500">Enter one email address per line</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Code Expiry (days)</label>
                      <input
                        type="number"
                        value={individualExpiry}
                        onChange={(e) => setIndividualExpiry(parseInt(e.target.value))}
                        min="1"
                        max="30"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="sendEmails"
                        checked={sendEmails}
                        onChange={(e) => setSendEmails(e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="sendEmails" className="ml-2 block text-sm text-gray-700">
                        Send welcome emails with registration codes
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={createIndividualCodes}
                    disabled={loading || !individualEmails.trim()}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-3 rounded-md font-medium"
                  >
                    {loading ? 'Creating Codes...' : 'Create Individual Codes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Shared Code Tab */}
          {activeSubTab === 'shared' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create Shared Registration Code</h3>
                <p className="text-sm text-gray-600 mb-6">Create a single code that can be shared with multiple homeowners. Address limits still apply.</p>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Uses (optional)</label>
                      <input
                        type="number"
                        value={sharedMaxUses}
                        onChange={(e) => setSharedMaxUses(e.target.value)}
                        placeholder="Leave empty for unlimited"
                        min="1"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Code Expiry (days)</label>
                      <input
                        type="number"
                        value={sharedExpiry}
                        onChange={(e) => setSharedExpiry(parseInt(e.target.value))}
                        min="1"
                        max="30"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={createSharedCode}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-3 rounded-md font-medium"
                  >
                    {loading ? 'Creating Code...' : 'Create Shared Code'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Upload Tab */}
          {activeSubTab === 'bulk' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Bulk Upload Email List</h3>
                <p className="text-sm text-gray-600 mb-6">Upload a CSV file with email addresses to create individual codes for all emails at once.</p>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CSV File</label>
                    <input
                      type="file"
                      id="csvFile"
                      accept=".csv"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                    <p className="mt-1 text-sm text-gray-500">CSV should have email addresses in the first column</p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">CSV Format Example:</h4>
                    <pre className="text-sm text-blue-800 bg-white p-2 rounded border">
{`email
homeowner1@email.com
homeowner2@email.com
homeowner3@email.com`}
                    </pre>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Code Expiry (days)</label>
                      <input
                        type="number"
                        value={individualExpiry}
                        onChange={(e) => setIndividualExpiry(parseInt(e.target.value))}
                        min="1"
                        max="30"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="sendEmailsBulk"
                        checked={sendEmails}
                        onChange={(e) => setSendEmails(e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="sendEmailsBulk" className="ml-2 block text-sm text-gray-700">
                        Send welcome emails automatically
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={uploadCSVFile}
                    disabled={loading || !uploadFile}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-3 rounded-md font-medium"
                  >
                    {loading ? 'Processing CSV...' : 'Upload and Create Codes'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Discount Codes Tab Component  
const DiscountCodesTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('discount'); // 'discount' or 'freetrial'
  const [discountCodes, setDiscountCodes] = useState([]);
  const [freeTrialCodes, setFreeTrialCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showTrialForm, setShowTrialForm] = useState(false);
  
  // Create discount code form state
  const [newCode, setNewCode] = useState({
    code: '',
    type: 'percentage',
    discount: '',
    description: '',
    max_uses: '',
    expires_in_days: '30'
  });

  // Create free trial code form state
  const [newTrialCode, setNewTrialCode] = useState({
    trial_duration_months: 12,
    is_single_use: true,
    max_uses: 1,
    expires_in_days: 90,
    description: ''
  });

  const fetchDiscountCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/platform-admin/discount-codes');
      setDiscountCodes(response.data.codes || []);
    } catch (error) {
      console.error('Failed to fetch discount codes:', error);
      alert('Failed to fetch discount codes');
    } finally {
      setLoading(false);
    }
  };

  const fetchFreeTrialCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/platform-admin/registration-codes/free-trial/list');
      setFreeTrialCodes(response.data.codes || []);
    } catch (error) {
      console.error('Failed to fetch free trial codes:', error);
      alert('Failed to fetch free trial codes');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (activeSubTab === 'discount') {
      fetchDiscountCodes();
    } else {
      fetchFreeTrialCodes();
    }
  }, [activeSubTab]);

  const createDiscountCode = async () => {
    if (!newCode.code.trim()) {
      alert('Please enter a discount code');
      return;
    }
    
    // For 'free' codes, automatically set discount to 100
    // For other types, validate that discount is provided
    if (newCode.type !== 'free' && (!newCode.discount || parseFloat(newCode.discount) <= 0)) {
      alert('Please enter a valid discount amount');
      return;
    }

    setLoading(true);
    try {
      const expires_at = newCode.expires_in_days ? 
        new Date(Date.now() + parseInt(newCode.expires_in_days) * 24 * 60 * 60 * 1000).toISOString() : 
        null;

      // Set discount value based on type
      const discountValue = newCode.type === 'free' ? 100 : parseFloat(newCode.discount);
      
      const codeData = {
        code: newCode.code.toUpperCase(),
        type: newCode.type,
        discount: discountValue,
        description: newCode.description || (newCode.type === 'free' ? 'Free (100% off)' : `${newCode.discount}${newCode.type === 'percentage' ? '%' : '$'} discount`),
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        expires_at: expires_at,
        is_active: true
      };

      const response = await axios.post('/api/platform-admin/discount-codes', codeData);
      
      alert(`✅ Discount code "${newCode.code.toUpperCase()}" created successfully!`);
      
      // Reset form
      setNewCode({
        code: '',
        type: 'percentage',
        discount: '',
        description: '',
        max_uses: '',
        expires_in_days: '30'
      });
      setShowCreateForm(false);
      
      fetchDiscountCodes();
    } catch (error) {
      console.error('Failed to create discount code:', error);
      alert(`❌ Failed to create discount code: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createFreeTrialCode = async () => {
    setLoading(true);
    try {
      const codeData = {
        trial_duration_months: parseInt(newTrialCode.trial_duration_months),
        is_single_use: newTrialCode.is_single_use,
        max_uses: newTrialCode.is_single_use ? 1 : (newTrialCode.max_uses ? parseInt(newTrialCode.max_uses) : null),
        expires_in_days: parseInt(newTrialCode.expires_in_days),
        description: newTrialCode.description || `${newTrialCode.trial_duration_months} Month Free Trial`
      };

      const response = await axios.post('/api/platform-admin/registration-codes/free-trial', codeData);
      
      alert(`✅ Free Trial code "${response.data.code.code}" created successfully!\n\n` +
            `Trial Duration: ${response.data.code.trial_duration}\n` +
            `Usage Type: ${response.data.code.usage_type}\n` +
            `Expires: ${response.data.code.expires_at}`);
      
      // Reset form
      setNewTrialCode({
        trial_duration_months: 12,
        is_single_use: true,
        max_uses: 1,
        expires_in_days: 90,
        description: ''
      });
      setShowTrialForm(false);
      
      fetchFreeTrialCodes();
    } catch (error) {
      console.error('Failed to create free trial code:', error);
      alert(`❌ Failed to create free trial code: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleCodeStatus = async (codeId, currentStatus) => {
    try {
      await axios.put(`/api/platform-admin/discount-codes/${codeId}`, {
        is_active: !currentStatus
      });
      
      alert('✅ Discount code status updated');
      fetchDiscountCodes();
    } catch (error) {
      console.error('Failed to update code status:', error);
      alert('❌ Failed to update code status');
    }
  };

  const deleteCode = async (codeId, codeName) => {
    if (!window.confirm(`Are you sure you want to delete discount code "${codeName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/api/platform-admin/discount-codes/${codeId}`);
      alert('✅ Discount code deleted successfully');
      fetchDiscountCodes();
    } catch (error) {
      console.error('Failed to delete discount code:', error);
      alert('❌ Failed to delete discount code');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (expiresAt) => {
    return expiresAt && new Date() > new Date(expiresAt);
  };

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🎟️ Code Management</h2>
          <p className="text-sm text-gray-600 mb-6">
            Manage discount codes for payments and free trial codes for new HOA registrations.
          </p>

          {/* Sub-Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => {
                  setActiveSubTab('discount');
                  setShowCreateForm(false);
                  setShowTrialForm(false);
                }}
                className={`${
                  activeSubTab === 'discount'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                💳 Discount Codes
              </button>
              <button
                onClick={() => {
                  setActiveSubTab('freetrial');
                  setShowCreateForm(false);
                  setShowTrialForm(false);
                }}
                className={`${
                  activeSubTab === 'freetrial'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                🎁 Free Trial Codes
              </button>
            </nav>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={activeSubTab === 'discount' ? fetchDiscountCodes : fetchFreeTrialCodes}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              🔄 Refresh
            </button>
            {activeSubTab === 'discount' ? (
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {showCreateForm ? 'Cancel' : '+ Create Discount Code'}
              </button>
            ) : (
              <button
                onClick={() => setShowTrialForm(!showTrialForm)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                {showTrialForm ? 'Cancel' : '+ Create Free Trial Code'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Discount Code</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Discount Code</label>
                <input
                  type="text"
                  value={newCode.code}
                  onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., SAVE50, WELCOME100, FREEHOA"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Discount Type</label>
                <select
                  value={newCode.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    if (newType === 'free') {
                      // Automatically set discount to 100 for free codes
                      setNewCode({...newCode, type: newType, discount: '100'});
                    } else {
                      // Clear discount when switching from free to other types
                      setNewCode({...newCode, type: newType, discount: newCode.type === 'free' ? '' : newCode.discount});
                    }
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="percentage">Percentage Discount (%)</option>
                  <option value="fixed">Fixed Amount Discount ($)</option>
                  <option value="free">Free (100% off)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Value {newCode.type === 'percentage' ? '(%)' : newCode.type === 'fixed' ? '($)' : ''}
                </label>
                <input
                  type="number"
                  value={newCode.discount}
                  onChange={(e) => setNewCode({...newCode, discount: e.target.value})}
                  placeholder={newCode.type === 'percentage' ? '50' : newCode.type === 'fixed' ? '100' : '100'}
                  min="0"
                  max={newCode.type === 'percentage' ? '100' : undefined}
                  disabled={newCode.type === 'free'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-600"
                />
                {newCode.type === 'free' && (
                  <p className="mt-1 text-sm text-green-600">✓ Free codes are automatically set to 100% off (discount value is set automatically)</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  value={newCode.description}
                  onChange={(e) => setNewCode({...newCode, description: e.target.value})}
                  placeholder="e.g., 50% off first year, $100 discount, Free trial"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Uses (optional)</label>
                <input
                  type="number"
                  value={newCode.max_uses}
                  onChange={(e) => setNewCode({...newCode, max_uses: e.target.value})}
                  placeholder="Leave empty for unlimited"
                  min="1"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expires in (days)</label>
                <input
                  type="number"
                  value={newCode.expires_in_days}
                  onChange={(e) => setNewCode({...newCode, expires_in_days: e.target.value})}
                  placeholder="30"
                  min="1"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createDiscountCode}
                disabled={loading}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Discount Code'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FREE TRIAL CODE FORM - THIS IS THE NEW SECTION FOR FREE TRIALS */}
      {showTrialForm && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 shadow rounded-lg border-2 border-green-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-green-900">🎁 Create Free Trial Code</h3>
              <p className="mt-1 text-sm text-green-700">
                Create codes for free trial periods. Single-use codes cannot be shared (one person only). Reusable codes can be used by multiple people.
              </p>
            </div>

            <div className="space-y-4">
              {/* Trial Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trial Duration *
                </label>
                <select
                  value={newTrialCode.trial_duration_months}
                  onChange={(e) => setNewTrialCode({...newTrialCode, trial_duration_months: e.target.value})}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                >
                  <option value={1}>1 Month Free Trial</option>
                  <option value={3}>3 Months Free Trial</option>
                  <option value={6}>6 Months Free Trial</option>
                  <option value={12}>1 Year (12 Months) Free Trial</option>
                  <option value={999}>Lifetime Free (999 Months)</option>
                </select>
              </div>

              {/* Usage Type - THIS IS THE KEY SETTING */}
              <div className="bg-white p-4 rounded-lg border border-green-300">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Usage Type * (IMPORTANT)
                </label>
                <div className="space-y-3">
                  {/* Single-Use Option */}
                  <div 
                    onClick={() => setNewTrialCode({...newTrialCode, is_single_use: true, max_uses: 1})}
                    className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      newTrialCode.is_single_use 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={newTrialCode.is_single_use}
                      onChange={() => setNewTrialCode({...newTrialCode, is_single_use: true, max_uses: 1})}
                      className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500"
                    />
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-semibold text-gray-900">
                        🔒 Single-Use (One-Time Only) - RECOMMENDED
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Code can only be used ONCE. Perfect for preventing sharing. After first use, code is automatically deactivated.
                      </p>
                      <div className="mt-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded inline-block">
                        ✓ Best for conference giveaways & individual promotions
                      </div>
                    </div>
                  </div>

                  {/* Reusable Option */}
                  <div 
                    onClick={() => setNewTrialCode({...newTrialCode, is_single_use: false})}
                    className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      !newTrialCode.is_single_use 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={!newTrialCode.is_single_use}
                      onChange={() => setNewTrialCode({...newTrialCode, is_single_use: false})}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-semibold text-gray-900">
                        ♻️ Reusable (Multiple Uses Until Expiry)
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Code can be used multiple times by different people until it expires or reaches max uses.
                      </p>
                      <div className="mt-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded inline-block">
                        ✓ Good for marketing campaigns & partner programs
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Max Uses (only show if reusable) */}
              {!newTrialCode.is_single_use && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Uses (Optional)
                  </label>
                  <input
                    type="number"
                    value={newTrialCode.max_uses || ''}
                    onChange={(e) => setNewTrialCode({...newTrialCode, max_uses: e.target.value})}
                    placeholder="Leave empty for unlimited uses"
                    min="1"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-blue-700">
                    Leave empty for unlimited uses until expiry. Or set a limit like 100 uses.
                  </p>
                </div>
              )}

              {/* Expiry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code Expires In (Days) *
                </label>
                <input
                  type="number"
                  value={newTrialCode.expires_in_days}
                  onChange={(e) => setNewTrialCode({...newTrialCode, expires_in_days: e.target.value})}
                  min="1"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  How long this code is valid for. Common: 30 days, 90 days, 365 days.
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={newTrialCode.description}
                  onChange={(e) => setNewTrialCode({...newTrialCode, description: e.target.value})}
                  placeholder="e.g., HOA Summit 2025 - Conference Giveaway"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Summary Box */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">📋 Summary:</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Trial: <strong>{newTrialCode.trial_duration_months === '12' ? '1 Year' : `${newTrialCode.trial_duration_months} Months`}</strong></li>
                  <li>• Type: <strong>{newTrialCode.is_single_use ? 'Single-Use (One-Time)' : `Reusable${newTrialCode.max_uses ? ` (Max ${newTrialCode.max_uses} uses)` : ' (Unlimited)'}`}</strong></li>
                  <li>• Valid For: <strong>{newTrialCode.expires_in_days} days</strong></li>
                </ul>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowTrialForm(false)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createFreeTrialCode}
                disabled={loading}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Free Trial Code'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Codes List - Show either Discount Codes or Free Trial Codes */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {activeSubTab === 'discount' ? '💳 Active Discount Codes' : '🎁 Free Trial Codes'}
          </h3>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading codes...</p>
            </div>
          ) : activeSubTab === 'discount' ? (
            // DISCOUNT CODES LIST
            discountCodes.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No discount codes found.</p>
                <p className="text-sm text-gray-400 mt-1">Create your first discount code using the button above.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {discountCodes.map((code) => (
                <div key={code.id} className={`border rounded-lg p-4 ${
                  isExpired(code.expires_at) ? 'bg-red-50 border-red-200' :
                  !code.is_active ? 'bg-gray-50 border-gray-200' : 
                  'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-xl font-mono font-bold text-gray-900">{code.code}</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          code.type === 'free' ? 'bg-green-100 text-green-800' :
                          code.type === 'percentage' ? 'bg-blue-100 text-blue-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {code.type === 'free' ? '🆓 FREE' : 
                           code.type === 'percentage' ? `📊 ${code.discount}%` : 
                           `💰 $${code.discount}`}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          isExpired(code.expires_at) ? 'bg-red-100 text-red-800' :
                          code.is_active ? 'bg-green-100 text-green-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {isExpired(code.expires_at) ? '⏰ Expired' : 
                           code.is_active ? '✅ Active' : '❌ Inactive'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-2">{code.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Created:</span> {formatDate(code.created_at)}
                        </div>
                        <div>
                          <span className="font-medium">Expires:</span> {code.expires_at ? formatDate(code.expires_at) : 'Never'}
                        </div>
                        <div>
                          <span className="font-medium">Uses:</span> {code.current_uses || 0}
                          {code.max_uses ? ` / ${code.max_uses}` : ' (unlimited)'}
                        </div>
                        <div>
                          <span className="font-medium">By:</span> {code.created_by}
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-4 flex space-x-2">
                      <button
                        onClick={() => toggleCodeStatus(code.id, code.is_active)}
                        className={`px-3 py-1 text-sm font-medium rounded ${
                          code.is_active ? 
                          'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 
                          'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {code.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => deleteCode(code.id, code.code)}
                        className="px-3 py-1 text-sm font-medium rounded bg-red-100 text-red-800 hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )
          ) : (
            // FREE TRIAL CODES LIST - THIS IS THE NEW SECTION
            freeTrialCodes.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No free trial codes found.</p>
                <p className="text-sm text-gray-400 mt-1">Create your first free trial code using the button above.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {freeTrialCodes.map((code) => (
                  <div key={code.id} className={`border-2 rounded-lg p-4 ${
                    isExpired(code.expires_at) ? 'bg-red-50 border-red-300' :
                    code.current_uses >= (code.max_uses || 999999) ? 'bg-gray-50 border-gray-300' :
                    'bg-green-50 border-green-300'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Code & Type */}
                        <div className="flex items-center space-x-3 mb-3">
                          <span className="text-xl font-mono font-bold text-gray-900 bg-white px-3 py-1 rounded border-2 border-gray-300">
                            {code.code}
                          </span>
                          {code.is_single_use ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                              🔒 SINGLE-USE
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                              ♻️ REUSABLE
                            </span>
                          )}
                          {code.current_uses >= 1 && code.is_single_use && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                              ✓ USED
                            </span>
                          )}
                        </div>

                        {/* Trial Duration */}
                        <div className="mb-2">
                          <span className="text-sm font-medium text-gray-700">Trial Duration: </span>
                          <span className="text-sm font-bold text-green-700">
                            {code.trial_duration_display || `${code.trial_duration_months} Months`}
                          </span>
                        </div>

                        {/* Description */}
                        {code.description && (
                          <p className="text-sm text-gray-600 mb-2">
                            📝 {code.description}
                          </p>
                        )}

                        {/* Usage Stats */}
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-700 mb-2">
                          <div>
                            <span className="font-medium">Uses: </span>
                            <span className="font-semibold text-indigo-600">
                              {code.current_uses || 0}
                              {code.max_uses && ` / ${code.max_uses}`}
                              {!code.is_single_use && !code.max_uses && ' / ∞'}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">Expires: </span>
                            <span className={isExpired(code.expires_at) ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                              {formatDate(code.expires_at)}
                              {isExpired(code.expires_at) && ' ⚠️'}
                            </span>
                          </div>
                        </div>

                        {/* Used By (if any) */}
                        {code.used_by && code.used_by.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-700 mb-1">Used by:</p>
                            <div className="flex flex-wrap gap-1">
                              {code.used_by.slice(0, 5).map((email, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-800">
                                  {email}
                                </span>
                              ))}
                              {code.used_by.length > 5 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                                  +{code.used_by.length - 5} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Created By */}
                        <div className="mt-2 text-xs text-gray-500">
                          Created by {code.created_by_email} on {formatDate(code.created_at)}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="ml-4 flex flex-col gap-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(code.code);
                            alert('✅ Code copied to clipboard!');
                          }}
                          className="px-3 py-1 text-sm font-medium rounded bg-green-100 text-green-800 hover:bg-green-200 w-full"
                        >
                          📋 Copy Code
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete trial code "${code.code}"?\n\nThis action cannot be undone.`)) {
                              return;
                            }
                            try {
                              await axios.delete(`/api/platform-admin/registration-codes/${code.id}`);
                              alert('✅ Trial code deleted successfully!');
                              // Refresh codes list
                              const response = await axios.get('/api/platform-admin/registration-codes/free-trial/list');
                              setFreeTrialCodes(response.data.codes || []);
                            } catch (error) {
                              console.error('Failed to delete code:', error);
                              alert(`❌ Failed to delete code: ${error.response?.data?.detail || error.message}`);
                            }
                          }}
                          className="px-3 py-1 text-sm font-medium rounded bg-red-100 text-red-800 hover:bg-red-200 w-full"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// Quotes Tab Component
const QuotesTab = ({ quotes, onRefresh }) => {
  const [loading, setLoading] = useState(false);

  // Auto-refresh quotes when tab is opened
  useEffect(() => {
    onRefresh();
  }, [onRefresh]);

  const deleteQuote = async (quoteId, quoteCode) => {
    if (!window.confirm(`Are you sure you want to delete quote ${quoteCode}?`)) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`/api/platform-admin/quotes/${quoteId}`);
      alert('✅ Quote deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Failed to delete quote:', error);
      alert('❌ Failed to delete quote');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const getDaysUntilExpiry = (createdDate) => {
    const created = new Date(createdDate);
    const expiry = new Date(created.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days
    const now = new Date();
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isExpired = (createdDate) => {
    return getDaysUntilExpiry(createdDate) <= 0;
  };

  const activeQuotes = quotes.filter(quote => !isExpired(quote.created_at));
  const expiredQuotes = quotes.filter(quote => isExpired(quote.created_at));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Customer Quotes</h2>
              <p className="mt-1 text-sm text-gray-500">
                View and manage customer quotes. Quotes automatically expire after 30 days.
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onRefresh()}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {loading ? '🔄' : '🔄'} Refresh
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Active Quotes</p>
                  <p className="text-2xl font-bold text-blue-900">{activeQuotes.length}</p>
                </div>
                <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  📋
                </div>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Expired Quotes</p>
                  <p className="text-2xl font-bold text-red-900">{expiredQuotes.length}</p>
                </div>
                <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center text-red-600">
                  ⏰
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Total Quotes</p>
                  <p className="text-2xl font-bold text-green-900">{quotes.length}</p>
                </div>
                <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                  📊
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Quotes */}
      {activeQuotes.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Active Quotes (Expire in 30 days)</h3>
            <div className="space-y-4">
              {activeQuotes.map((quote) => (
                <div key={quote.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Quote: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{quote.quote_code}</span>
                          </p>
                          <p className="text-sm text-gray-600">HOA: {quote.hoa_name || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Contact: {quote.contact_email}</p>
                          <p className="text-sm text-gray-600">Properties: {quote.total_properties || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Created: {formatDate(quote.created_at)}</p>
                          <p className="text-sm text-green-600">
                            Expires in: {getDaysUntilExpiry(quote.created_at)} days
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => deleteQuote(quote.id, quote.quote_code)}
                        disabled={loading}
                        className="px-3 py-1 text-sm font-medium rounded bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Expired Quotes */}
      {expiredQuotes.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Expired Quotes</h3>
            <div className="space-y-4">
              {expiredQuotes.map((quote) => (
                <div key={quote.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Quote: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{quote.quote_code}</span>
                          </p>
                          <p className="text-sm text-gray-600">HOA: {quote.hoa_name || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Contact: {quote.contact_email}</p>
                          <p className="text-sm text-gray-600">Properties: {quote.total_properties || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Created: {formatDate(quote.created_at)}</p>
                          <p className="text-sm text-red-600">
                            Expired {Math.abs(getDaysUntilExpiry(quote.created_at))} days ago
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => deleteQuote(quote.id, quote.quote_code)}
                        disabled={loading}
                        className="px-3 py-1 text-sm font-medium rounded bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {quotes.length === 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-12 sm:px-6 text-center">
            <div className="text-4xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Quotes Found</h3>
            <p className="text-sm text-gray-500">Customer quotes will appear here when they save quotes during the HOA setup process.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Activity Audit Tab Component
const ActivityAuditTab = ({ user }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/platform-admin/activity-audit', {
        params: { filter }
      });
      if (response.data.success) {
        setActivities(response.data.activities || []);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [filter]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Activity & Audit Trail</h2>
          <p className="text-sm text-gray-600 mt-1">
            Monitor platform activities and administrative actions
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Activities</option>
            <option value="admin">Admin Actions</option>
            <option value="hoa">HOA Management</option>
            <option value="user">User Management</option>
          </select>
          <button
            onClick={fetchActivities}
            disabled={loading}
            className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading activities...</p>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Activities Found</h3>
              <p className="text-sm text-gray-500">Activity logs will appear here as actions are performed.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activities.map((activity, index) => (
                <div key={index} className="border-l-4 border-indigo-400 pl-4 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                      <p className="text-sm text-gray-600">{activity.description}</p>
                      <p className="text-xs text-gray-400">
                        {activity.user_name} • {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      activity.type === 'admin' ? 'bg-red-100 text-red-800' :
                      activity.type === 'hoa' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {activity.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ user }) => {
  const [settings, setSettings] = useState({
    notifications: {
      email_alerts: true,
      push_notifications: false,
      system_updates: true,
      security_alerts: true
    }
  });
  const [profileData, setProfileData] = useState({
    name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [nameChangeRequest, setNameChangeRequest] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  const isGodAdmin = user.role === 'platform_admin';

  // Load current settings
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/platform-admin/settings');
        if (response.data.success) {
          setSettings(response.data.settings);
        }
      } catch (error) {
        console.log('Settings not found, using defaults');
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  // Load email activity (currently limited to the selected HOA in configuration modal)
  // For now, this will show empty until we have an HOA context
  useEffect(() => {
    const loadEmailActivity = async () => {
      try {
        setLoadingActivity(true);
        // We need HOA ID to fetch activity - for now we'll skip this in the global settings tab
        // This would work better in the HOA-specific configuration modal
        setEmailActivity([]);
      } catch (error) {
        console.error('Error loading email activity:', error);
        setEmailActivity([]);
      } finally {
        setLoadingActivity(false);
      }
    };
    
    // loadEmailActivity(); // Disabled for now - need HOA context
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put('/api/platform-admin/settings', settings);
      if (response.data.success) {
        alert('✅ Settings saved successfully');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('❌ Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!profileData.email || profileData.email === user.email) {
      alert('Please enter a new email address');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put('/api/platform-admin/update-email', {
        new_email: profileData.email
      });
      if (response.data.success) {
        alert('✅ Email updated successfully. Please check your new email for verification.');
      }
    } catch (error) {
      console.error('Failed to update email:', error);
      alert('❌ Failed to update email: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!profileData.currentPassword || !profileData.newPassword || !profileData.confirmPassword) {
      alert('Please fill in all password fields');
      return;
    }

    if (profileData.newPassword !== profileData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (profileData.newPassword.length < 8) {
      alert('New password must be at least 8 characters long');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put('/api/platform-admin/reset-password', {
        current_password: profileData.currentPassword,
        new_password: profileData.newPassword
      });
      if (response.data.success) {
        alert('✅ Password reset successfully');
        setProfileData({
          ...profileData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert('❌ Failed to reset password: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleNameChange = async () => {
    if (isGodAdmin) {
      // God admin can change name directly
      if (!profileData.name.trim()) {
        alert('Please enter a valid name');
        return;
      }

      setSaving(true);
      try {
        const response = await axios.put('/api/platform-admin/update-name', {
          new_name: profileData.name
        });
        if (response.data.success) {
          alert('✅ Name updated successfully');
        }
      } catch (error) {
        console.error('Failed to update name:', error);
        alert('❌ Failed to update name: ' + (error.response?.data?.detail || 'Unknown error'));
      } finally {
        setSaving(false);
      }
    } else {
      // Employee submits name change request
      if (!nameChangeRequest.trim()) {
        alert('Please enter the requested name change');
        return;
      }

      setSaving(true);
      try {
        const response = await axios.post('/api/platform-admin/request-name-change', {
          requested_name: nameChangeRequest,
          current_name: profileData.name,
          reason: 'Employee name change request'
        });
        if (response.data.success) {
          alert('✅ Name change request submitted successfully. Admin will review your request.');
          setNameChangeRequest('');
        }
      } catch (error) {
        console.error('Failed to submit name change request:', error);
        alert('❌ Failed to submit request: ' + (error.response?.data?.detail || 'Unknown error'));
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <p className="mt-2 text-sm text-gray-600">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-sm text-gray-600 mt-1">
          Manage your profile, preferences, and notification settings
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Settings */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Profile Settings</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            {/* Name Management */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isGodAdmin ? 'Change Name' : 'Current Name'}
              </label>
              {isGodAdmin ? (
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter your name"
                  />
                  <button
                    onClick={handleNameChange}
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Update
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={profileData.name}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Request Name Change
                    </label>
                    <div className="flex space-x-3">
                      <input
                        type="text"
                        value={nameChangeRequest}
                        onChange={(e) => setNameChangeRequest(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="What should your new name be?"
                      /> 
                      <button
                        onClick={handleNameChange}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        Request
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Email Update */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Update Email</label>
              <div className="flex space-x-3">
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter new email address"
                />
                <button
                  onClick={handleUpdateEmail}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  Update
                </button>
              </div>
            </div>

            {/* Password Reset */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reset Password</label>
              <div className="space-y-3">
                <input
                  type="password"
                  value={profileData.currentPassword}
                  onChange={(e) => setProfileData({...profileData, currentPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Current password"
                />
                <input
                  type="password"
                  value={profileData.newPassword}
                  onChange={(e) => setProfileData({...profileData, newPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="New password (min 8 characters)"
                />
                <input
                  type="password"
                  value={profileData.confirmPassword}
                  onChange={(e) => setProfileData({...profileData, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Confirm new password"
                />
                <button
                  onClick={handleResetPassword}
                  disabled={saving}
                  className="w-full px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notification Preferences</h3>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                <p className="text-xs text-gray-500">Receive notifications via email</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.email_alerts}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, email_alerts: e.target.checked }
                })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Push Notifications</label>
                <p className="text-xs text-gray-500">Receive browser push notifications</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.push_notifications}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, push_notifications: e.target.checked }
                })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">System Updates</label>
                <p className="text-xs text-gray-500">Platform updates and maintenance notifications</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.system_updates}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, system_updates: e.target.checked }
                })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Security Alerts</label>
                <p className="text-xs text-gray-500">Important security notifications</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.security_alerts}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, security_alerts: e.target.checked }
                })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
            </div>

            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="w-full px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Notification Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Role:</span>
              <p className="text-gray-600 capitalize">{user.role.replace('_', ' ')}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Current Email:</span>
              <p className="text-gray-600">{user.email}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Account Status:</span>
              <p className="text-green-600">Active</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Marketing Management Tab Component
const MarketingTab = () => {
  const [currentYear] = useState(new Date().getFullYear());
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [monthlyHeaders, setMonthlyHeaders] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const monthColors = [
    '#3B82F6', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4',
    '#EF4444', '#F97316', '#6366F1', '#84CC16', '#8B4513', '#059669'
  ];

  const loadMonthlyHeaders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/platform-admin/marketing/monthly-headers/${selectedYear}`);
      setMonthlyHeaders(response.data.headers || {});
    } catch (error) {
      console.error('Failed to load monthly headers:', error);
      alert('Failed to load monthly headers');
    } finally {
      setLoading(false);
    }
  };

  const saveMonthlyHeaders = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/platform-admin/marketing/monthly-headers/${selectedYear}`, {
        headers: monthlyHeaders
      });
      alert(`Monthly headers updated for ${selectedYear}`);
    } catch (error) {
      console.error('Failed to save monthly headers:', error);
      alert('Failed to save monthly headers');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (monthIndex, file) => {
    if (!file) return;

    // Create a preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setMonthlyHeaders(prev => ({
        ...prev,
        [monthIndex]: {
          ...prev[monthIndex],
          image: e.target.result,
          theme: prev[monthIndex]?.theme || `${monthNames[monthIndex]} Theme`
        }
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleThemeChange = (monthIndex, theme) => {
    setMonthlyHeaders(prev => ({
      ...prev,
      [monthIndex]: {
        ...prev[monthIndex],
        theme: theme
      }
    }));
  };

  const resetMonth = (monthIndex) => {
    if (confirm(`Reset ${monthNames[monthIndex]} to default settings?`)) {
      setMonthlyHeaders(prev => {
        const updated = { ...prev };
        delete updated[monthIndex];
        return updated;
      });
    }
  };

  useEffect(() => {
    loadMonthlyHeaders();
  }, [selectedYear]);

  const yearOptions = [];
  for (let year = currentYear - 1; year <= currentYear + 2; year++) {
    yearOptions.push(year);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Marketing Management</h2>
        <p className="text-gray-600 mb-6">
          Customize monthly calendar headers for all HOA communities. Changes affect future months while preserving historical views.
        </p>

        {/* Year Selection */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Managing Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              {previewMode ? 'Edit Mode' : 'Preview Mode'}
            </button>
            <button
              onClick={saveMonthlyHeaders}
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Monthly Headers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {monthNames.map((monthName, monthIndex) => (
          <div key={monthIndex} className="bg-white shadow rounded-lg overflow-hidden">
            {/* Month Header Preview */}
            <div 
              className="relative h-32"
              style={{ backgroundColor: monthColors[monthIndex] }}
            >
              {monthlyHeaders[monthIndex]?.image && (
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${monthlyHeaders[monthIndex].image})`,
                    opacity: 0.7
                  }}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
              <div className="relative z-10 h-full flex items-center justify-between p-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">
                    {monthName} {selectedYear}
                  </h3>
                  <p className="text-white/90 text-sm">
                    {monthlyHeaders[monthIndex]?.theme || `${monthName} Theme`}
                  </p>
                </div>
              </div>
            </div>

            {/* Controls */}
            {!previewMode && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Header Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(monthIndex, e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Theme Text</label>
                  <input
                    type="text"
                    value={monthlyHeaders[monthIndex]?.theme || `${monthName} Theme`}
                    onChange={(e) => handleThemeChange(monthIndex, e.target.value)}
                    placeholder={`${monthName} Theme`}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => resetMonth(monthIndex)}
                    className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Marketing System Information</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Changes apply to all HOA communities globally</li>
                <li>Historical months preserve their original headers after year-end</li>
                <li>Images are automatically optimized for performance</li>
                <li>Changes to current/future months take effect immediately</li>
                <li>Past months maintain their appearance for historical viewing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Event Stock Photos Management */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Stock Photos</h3>
        <p className="text-gray-600 mb-6">
          Manage default stock photos used for community events. These photos are used as default images when creating events.
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { id: 'board_meeting', name: 'Board Meeting', path: '/assets/events/stock/board_meeting.png' },
            { id: 'maintenance', name: 'Maintenance', path: '/assets/events/stock/maintenance.png' },
            { id: 'arc_meeting', name: 'ARC Meeting', path: '/assets/events/stock/arc_meeting.png' },
            { id: 'pool_party', name: 'Pool Party', path: '/assets/events/stock/pool_party.png' },
            { id: 'community_event', name: 'Community Event', path: '/assets/events/stock/community_event.png' },
            { id: 'social_meeting', name: 'Social Meeting', path: '/assets/events/stock/social_meeting.png' },
            { id: 'holiday_event', name: 'Holiday Event', path: '/assets/events/stock/holiday_event.png' },
            { id: 'emergency_meeting', name: 'Emergency Meeting', path: '/assets/events/stock/emergency_meeting.png' },
            { id: 'workshop', name: 'Workshop', path: '/assets/events/stock/workshop.png' },
            { id: 'fundraiser', name: 'Fundraiser', path: '/assets/events/stock/fundraiser.png' },
            { id: 'other', name: 'Other', path: '/assets/events/stock/other.png' }
          ].map(photo => (
            <div key={photo.id} className="text-center border border-gray-200 rounded-lg p-3">
              <img 
                src={photo.path} 
                alt={photo.name}
                className="w-full h-20 object-cover rounded-lg mb-2"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <p className="text-xs text-gray-600 font-medium">{photo.name}</p>
              <button className="mt-2 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors">
                Replace
              </button>
            </div>
          ))}
        </div>
        
        <div className="flex justify-between items-center">
          <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
            Upload New Stock Photo
          </button>
          <button className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors">
            Reset All to Defaults
          </button>
        </div>
        
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>💡 Note:</strong> These stock photos are preserved per HOA in the God Mode admin dashboard. 
            Each HOA can customize their event photos while maintaining the ability to reset to original defaults.
            The new Workshop and Fundraiser images have been added to the system.
          </p>
        </div>
      </div>
    </div>
  );
};

// Profile Styles Tab Component - Global Settings for ALL HOAs
const ProfileStylesTab = ({ hoas }) => {
  const [profileStyles, setProfileStyles] = useState({
    board_member: { color: '#FFD100', icon: '⭐', name: 'Board Member' },
    committee_member: { color: '#9B59B6', icon: '🪄', name: 'Committee Member' },
    homeowner: { color: '#9B59B6', icon: '🏠', name: 'Homeowner' },
    maintenance_team: { color: '#E74C3C', icon: '🔧', name: 'Maintenance Team' },
    security_team: { color: '#2ECC71', icon: '🛡️', name: 'Security Team' },
    platform_admin: { color: '#E67E22', icon: '👑', name: 'Platform Admin' }
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load global profile styles
  const loadGlobalProfileStyles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/platform/global-profile-styles');
      if (response.data.success && response.data.settings) {
        setProfileStyles(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to load global profile styles:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save global profile styles to ALL HOAs
  const saveGlobalProfileStyles = async () => {
    setSaving(true);
    try {
      // Apply to all HOAs at once
      const response = await api.put('/api/platform/global-profile-styles', {
        profile_styles: profileStyles,
        apply_to_all_hoas: true
      });
      if (response.data.success) {
        alert(`✅ Profile styles saved successfully to ALL ${response.data.updated_hoas || hoas.length} HOAs!`);
      }
    } catch (error) {
      console.error('Failed to save global profile styles:', error);
      alert('❌ Failed to save profile styles. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Load on component mount
  React.useEffect(() => {
    loadGlobalProfileStyles();
  }, []);

  // Update role styling
  const updateRoleStyle = (roleKey, field, value) => {
    setProfileStyles(prev => ({
      ...prev,
      [roleKey]: {
        ...prev[roleKey],
        [field]: value
      }
    }));
  };

  const availableIcons = ['🏠', '🪄', '🔧', '🛡️', '👑', '⭐', '💎', '🎯', '🌟', '🎖️', '🏆', '🎪', '🎨', '📋', '💼', '🔑'];
  const colorPresets = ['#FFD100', '#9B59B6', '#3498DB', '#E74C3C', '#2ECC71', '#E67E22', '#F39C12', '#8E44AD', '#27AE60'];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">🎨 Global Profile Styles Management</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure profile picture colors and icons for ALL HOAs at once. Changes apply globally to all {hoas.length} HOAs.
        </p>
      </div>

      {/* Global Settings Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">⚠️</span>
          <h3 className="font-semibold text-yellow-800">Global Settings</h3>
        </div>
        <p className="text-sm text-yellow-700">
          These settings will apply to <strong>ALL {hoas.length} HOAs</strong> in the system. 
          This will override any individual HOA profile style customizations.
        </p>
      </div>

      {/* Profile Styles Configuration */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Role-Based Profile Styling (Global)</h3>
          <button
            onClick={saveGlobalProfileStyles}
            disabled={saving}
            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {saving ? '💾 Applying to All HOAs...' : `💾 Apply to All ${hoas.length} HOAs`}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading global profile styles...</div>
        ) : (
          <div className="space-y-6">
            {Object.entries(profileStyles).map(([roleKey, roleStyle]) => (
              <div key={roleKey} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-4 mb-4">
                  {/* Preview */}
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg relative border-2 border-white shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${roleStyle.color}, ${roleStyle.color}CC)` }}
                  >
                    AB
                    {roleStyle.icon && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center border border-gray-300 text-xs">
                        {roleStyle.icon}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{roleStyle.name}</h4>
                    <p className="text-sm text-gray-500">{roleKey.replace('_', ' ')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Color Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={roleStyle.color}
                        onChange={(e) => updateRoleStyle(roleKey, 'color', e.target.value)}
                        className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={roleStyle.color}
                        onChange={(e) => updateRoleStyle(roleKey, 'color', e.target.value)}
                        className="block flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="#FFD100"
                      />
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {colorPresets.map(color => (
                        <button
                          key={color}
                          onClick={() => updateRoleStyle(roleKey, 'color', color)}
                          className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Icon Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Corner Icon</label>
                    <input
                      type="text"
                      value={roleStyle.icon}
                      onChange={(e) => updateRoleStyle(roleKey, 'icon', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-2"
                      placeholder="🏠"
                    />
                    <div className="flex flex-wrap gap-1">
                      {availableIcons.map(icon => (
                        <button
                          key={icon}
                          onClick={() => updateRoleStyle(roleKey, 'icon', icon)}
                          className="w-8 h-8 border border-gray-300 rounded hover:bg-gray-100 flex items-center justify-center text-lg"
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Display Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
                    <input
                      type="text"
                      value={roleStyle.name}
                      onChange={(e) => updateRoleStyle(roleKey, 'name', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="Role Name"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          💾 Applying settings to all {hoas.length} HOAs...
        </div>
      )}
    </div>
  );
};

// Event & Email Settings Tab Component
const EventEmailSettingsTab = () => {
  const [settings, setSettings] = useState({
    enable_event_emails: true,
    use_in_app_composer: false,
    default_bcc_protection: true,
    event_email_templates: {
      event_reminder: {
        enabled: true,
        template: 'Dear {name}, this is a reminder about the upcoming event: {event_title} on {event_date} at {event_time}.'
      },
      rsvp_confirmation: {
        enabled: true,
        template: 'Thank you for your RSVP to {event_title}. Event details: {event_date} at {event_time}.'
      }
    },
    fine_email_settings: {
      enabled: true,
      template: 'Dear {name}, this is regarding a violation fine. Amount: ${amount}. Due date: {due_date}.'
    }
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailActivity, setEmailActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Load settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Try both token types for authentication
        const platformToken = localStorage.getItem('platform_token');
        const hoaToken = localStorage.getItem('hoa_token');
        const token = platformToken || hoaToken;

        if (!token) {
          console.warn('No authentication token found for loading settings');
          return;
        }

        const response = await fetch('/api/platform-admin/event-email-settings', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) {
            // Ensure the loaded settings have the correct structure
            const loadedSettings = {
              enable_event_emails: data.settings.enable_event_emails ?? true,
              use_in_app_composer: data.settings.use_in_app_composer ?? false,
              default_bcc_protection: data.settings.default_bcc_protection ?? true,
              event_email_templates: data.settings.event_email_templates ?? {
                event_reminder: {
                  enabled: true,
                  template: 'Dear {name}, this is a reminder about the upcoming event: {event_title} on {event_date} at {event_time}.'
                },
                rsvp_confirmation: {
                  enabled: true,
                  template: 'Thank you for your RSVP to {event_title}. Event details: {event_date} at {event_time}.'
                }
              },
              fine_email_settings: data.settings.fine_email_settings ?? {
                enabled: true,
                template: 'Dear {name}, this is regarding a violation fine. Amount: ${amount}. Due date: {due_date}.'
              }
            };
            setSettings(loadedSettings);
          }
        } else {
          console.error('Error loading settings:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Try both token types for authentication
      const platformToken = localStorage.getItem('platform_token');
      const hoaToken = localStorage.getItem('hoa_token');
      const token = platformToken || hoaToken;
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Save settings via API
      const response = await fetch('/api/platform-admin/event-email-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        alert('Event email settings saved successfully!');
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to save settings: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              🎫 Event & Email Settings
            </h3>
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            🎫 Event & Email Settings
          </h3>
          
          {/* Email Composer Settings */}
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-3">Event Email Functionality</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Enable Event Emails</label>
                    <p className="text-xs text-gray-500">Allow organizers to email event attendees</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enable_event_emails}
                      onChange={(e) => setSettings({...settings, enable_event_emails: e.target.checked})}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${
                      settings.enable_event_emails ? 'bg-blue-600' : 'bg-gray-200'
                    }`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                        settings.enable_event_emails ? 'translate-x-5' : 'translate-x-0.5'
                      } mt-0.5`}></div>
                    </div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Use In-App Email Composer</label>
                    <p className="text-xs text-gray-500">Toggle between mailto: links and in-app composer modal</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.use_in_app_composer}
                      onChange={(e) => setSettings({...settings, use_in_app_composer: e.target.checked})}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${
                      settings.use_in_app_composer ? 'bg-blue-600' : 'bg-gray-200'
                    }`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                        settings.use_in_app_composer ? 'translate-x-5' : 'translate-x-0.5'
                      } mt-0.5`}></div>
                    </div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Default BCC Protection</label>
                    <p className="text-xs text-gray-500">Always use BCC to protect attendee privacy</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.default_bcc_protection}
                      onChange={(e) => setSettings({...settings, default_bcc_protection: e.target.checked})}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${
                      settings.default_bcc_protection ? 'bg-blue-600' : 'bg-gray-200'
                    }`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                        settings.default_bcc_protection ? 'translate-x-5' : 'translate-x-0.5'
                      } mt-0.5`}></div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Fine Email Settings */}
            <div className="bg-red-50 p-4 rounded-lg">
              <h4 className="font-medium text-red-900 mb-3">Fine & Violation Email Settings</h4>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="text-sm font-medium text-gray-900">Enable Fine Emails</label>
                  <p className="text-xs text-gray-500">Send email notifications for fines and violations</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.fine_email_settings?.enabled ?? true}
                    onChange={(e) => setSettings({
                      ...settings, 
                      fine_email_settings: {
                        ...settings.fine_email_settings,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${
                    (settings.fine_email_settings?.enabled ?? true) ? 'bg-blue-600' : 'bg-gray-200'
                  }`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                      (settings.fine_email_settings?.enabled ?? true) ? 'translate-x-5' : 'translate-x-0.5'
                    } mt-0.5`}></div>
                  </div>
                </label>
              </div>
              
              {(settings.fine_email_settings?.enabled ?? true) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fine Email Template</label>
                  <textarea
                    rows="3"
                    value={settings.fine_email_settings?.template ?? ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      fine_email_settings: {
                        ...settings.fine_email_settings,
                        template: e.target.value
                      }
                    })}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter email template..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available variables: {'{name}'}, {'{amount}'}, {'{due_date}'}, {'{violation_type}'}
                  </p>
                </div>
              )}
            </div>

            {/* Recent Emails Log */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Recent Email Activity</h4>
              <p className="text-sm text-gray-600 mb-3">Email activity tracking across all HOAs. View HOA-specific activity in Configuration Management.</p>
              <div className="space-y-2">
                {loadingActivity ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="text-xs text-gray-500 mt-2">Loading activity...</p>
                  </div>
                ) : emailActivity.length > 0 ? (
                  emailActivity.map((activity, index) => (
                    <div key={index} className="flex justify-between items-center py-2 px-3 bg-white rounded border">
                      <div>
                        <p className="text-sm font-medium">{activity.subject}</p>
                        <p className="text-xs text-gray-500">
                          Sent to {activity.recipient_count} {activity.recipient_count === 1 ? 'recipient' : 'recipients'} • {activity.time_ago}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        activity.status === 'sent' || activity.status === 'delivered' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {activity.status === 'sent' ? 'Delivered' : activity.status}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6">
                    <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-600 mt-2">No recent email activity</p>
                    <p className="text-xs text-gray-500 mt-1">Email logs from event notifications and other system emails will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// Login Tracking Tab Component (for debugging user login/registration issues)
const LoginTrackingTab = ({ hoas }) => {
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [registrationAttempts, setRegistrationAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailFilter, setEmailFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState('all'); // 'all', 'success', 'failed'
  const [viewMode, setViewMode] = useState('login'); // 'login' or 'registration'
  const [legend, setLegend] = useState({});

  const loadLoginAttempts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      if (emailFilter) params.append('email', emailFilter);
      if (successFilter !== 'all') params.append('success', successFilter === 'success');
      
      const response = await axios.get(`/api/platform-admin/login-attempts?${params}`);
      if (response.data?.success) {
        setLoginAttempts(response.data.attempts || []);
        setLegend(response.data.failure_reasons_legend || {});
        setViewMode('login');
      }
    } catch (error) {
      console.error('Failed to load login attempts:', error);
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadRegistrationAttempts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      if (emailFilter) params.append('email', emailFilter);
      if (successFilter !== 'all') params.append('success', successFilter === 'success');
      
      const response = await axios.get(`/api/platform-admin/registration-attempts?${params}`);
      if (response.data?.success) {
        setRegistrationAttempts(response.data.attempts || []);
        setLegend(response.data.failure_reasons_legend || {});
        setViewMode('registration');
      }
    } catch (error) {
      console.error('Failed to load registration attempts:', error);
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getReasonBadge = (reason) => {
    const colors = {
      wrong_password: 'bg-red-100 text-red-800',
      email_not_found: 'bg-yellow-100 text-yellow-800',
      email_in_property_not_registered: 'bg-orange-100 text-orange-800',
      no_password_set: 'bg-purple-100 text-purple-800',
      not_approved: 'bg-blue-100 text-blue-800',
      account_inactive: 'bg-gray-100 text-gray-800',
      account_suspended: 'bg-red-100 text-red-800',
      email_already_registered: 'bg-yellow-100 text-yellow-800',
      invalid_registration_code: 'bg-red-100 text-red-800',
      invalid_hoa_code: 'bg-red-100 text-red-800',
      no_code_provided: 'bg-gray-100 text-gray-800'
    };
    return colors[reason] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">🔐 Login & Registration Tracking</h2>
        <p className="text-sm text-gray-600 mb-4">
          Track and debug user login and registration attempts. Use this to help users who are having trouble accessing their accounts.
        </p>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Filter</label>
            <input
              type="text"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="Search by email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={successFilter}
              onChange={(e) => setSuccessFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="all">All</option>
              <option value="success">Successful</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={loadLoginAttempts}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm"
            >
              {loading && viewMode === 'login' ? 'Loading...' : '🔑 Login Attempts'}
            </button>
            <button
              onClick={loadRegistrationAttempts}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
            >
              {loading && viewMode === 'registration' ? 'Loading...' : '📝 Registration Attempts'}
            </button>
          </div>
        </div>

        {/* Results */}
        {viewMode === 'login' && loginAttempts.length > 0 && (
          <div className="overflow-x-auto">
            <h3 className="text-md font-medium text-gray-900 mb-2">🔑 Login Attempts ({loginAttempts.length})</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Issue</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">User/HOA</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">What Went Wrong / Suggestion</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loginAttempts.map((attempt, idx) => (
                  <tr key={attempt.id || idx} className={attempt.success ? 'bg-green-50' : 'bg-red-50'}>
                    <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                      {new Date(attempt.timestamp).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{attempt.email}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${attempt.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {attempt.success ? '✅ Success' : '❌ Failed'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {attempt.failure_reason && (
                        <div>
                          <span className={`px-2 py-1 text-xs rounded-full ${getReasonBadge(attempt.failure_reason)}`} title={legend[attempt.failure_reason]}>
                            {attempt.failure_reason.replace(/_/g, ' ')}
                          </span>
                          {attempt.password_hint && (
                            <div className="mt-1">
                              <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800">
                                💡 {attempt.password_hint.replace(/_/g, ' ')}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {attempt.user_name && <div>{attempt.user_name}</div>}
                      {attempt.hoa_name && <div className="text-gray-400">{attempt.hoa_name}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs text-blue-600 max-w-sm" title={attempt.suggestion}>
                      {attempt.suggestion || (attempt.success ? '—' : 'Try password reset')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'registration' && registrationAttempts.length > 0 && (
          <div className="overflow-x-auto">
            <h3 className="text-md font-medium text-gray-900 mb-2">📝 Registration Attempts ({registrationAttempts.length})</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code Used</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">HOA</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Suggestion</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {registrationAttempts.map((attempt, idx) => (
                  <tr key={attempt.id || idx} className={attempt.success ? 'bg-green-50' : 'bg-red-50'}>
                    <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                      {new Date(attempt.timestamp).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{attempt.email}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${attempt.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {attempt.success ? '✅ Success' : '❌ Failed'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {attempt.failure_reason && (
                        <span className={`px-2 py-1 text-xs rounded-full ${getReasonBadge(attempt.failure_reason)}`} title={legend[attempt.failure_reason]}>
                          {attempt.failure_reason.replace(/_/g, ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-600">
                      {attempt.registration_code || attempt.hoa_code || '-'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{attempt.hoa_name || '-'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate" title={attempt.suggestion}>
                      {attempt.suggestion}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty state */}
        {((viewMode === 'login' && loginAttempts.length === 0) || (viewMode === 'registration' && registrationAttempts.length === 0)) && !loading && (
          <div className="text-center py-8 text-gray-500">
            <p>Click one of the buttons above to load attempts.</p>
          </div>
        )}

        {/* Legend */}
        {Object.keys(legend).length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Failure Reasons Legend:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {Object.entries(legend).map(([key, value]) => (
                <div key={key} className="flex items-start">
                  <span className={`px-2 py-0.5 rounded ${getReasonBadge(key)} mr-2`}>{key.replace(/_/g, ' ')}</span>
                  <span className="text-gray-600">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



// Payment Tracker Tab Component (for tracking external payments and duplicates)
const PaymentTrackerTab = ({ hoas }) => {
  const [selectedHoa, setSelectedHoa] = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [refunding, setRefunding] = useState({});
  const [resending, setResending] = useState({});
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'duplicates'

  const loadAllPayments = async () => {
    if (!selectedHoa) {
      alert('Please select an HOA first');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`/api/external-payments/list/${selectedHoa}`);
      if (response.data?.success) {
        setAllPayments(response.data.payments || []);
        setViewMode('all');
      }
    } catch (error) {
      console.error('Failed to load payments:', error);
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadDuplicates = async () => {
    if (!selectedHoa) {
      alert('Please select an HOA first');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`/api/platform-admin/hoa/${selectedHoa}/duplicate-payments`);
      if (response.data?.success) {
        setDuplicates(response.data.duplicates || []);
        setStats({
          total_checked: response.data.total_payments_checked,
          groups_found: response.data.duplicate_groups_found
        });
        setViewMode('duplicates');
      }
    } catch (error) {
      console.error('Failed to load duplicates:', error);
      alert(`❌ ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resendReceipt = async (paymentId, payerEmail) => {
    if (!window.confirm(`📧 RESEND RECEIPT\n\nSend receipt email to: ${payerEmail}\n\nConfirm?`)) {
      return;
    }

    try {
      setResending(prev => ({...prev, [paymentId]: true}));
      const response = await axios.post(`/api/external-payments/resend-receipt/${paymentId}`);
      
      if (response.data?.success) {
        alert(`✅ Receipt Sent!\n\nEmail sent to: ${response.data.sent_to}`);
      }
    } catch (error) {
      console.error('Failed to resend receipt:', error);
      alert(`❌ Failed to Send Receipt\n\n${error.response?.data?.detail || error.message}`);
    } finally {
      setResending(prev => ({...prev, [paymentId]: false}));
    }
  };

  const refundPayment = async (paymentId, paymentInfo) => {
    if (!window.confirm(`💰 REFUND PAYMENT\n\nAre you sure you want to refund this payment?\n\nAmount: $${paymentInfo.amount}\nPayer: ${paymentInfo.email}\n\nThis action cannot be undone!`)) {
      return;
    }

    try {
      setRefunding(prev => ({...prev, [paymentId]: true}));
      const response = await axios.post('/api/external-payments/refund-payment', null, {
        params: { payment_id: paymentId }
      });
      
      if (response.data?.success) {
        alert(`✅ Refund Successful\n\nRefund ID: ${response.data.refund_id}\nAmount: $${response.data.refund_amount}\n\nThe payment has been refunded and ledger updated.`);
        // Reload duplicates to refresh status
        loadDuplicates();
      } else if (response.data?.already_refunded) {
        alert('ℹ️ This payment has already been refunded.');
      }
    } catch (error) {
      console.error('Failed to refund payment:', error);
      alert(`❌ Refund Failed\n\n${error.response?.data?.detail || error.message}`);
    } finally {
      setRefunding(prev => ({...prev, [paymentId]: false}));
    }
  };

  const selectedHoaData = hoas.find(h => h.id === selectedHoa);

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">💳 Payment Duplicate Tracker</h2>
        <p className="text-gray-600 mb-6">
          Track and identify potential duplicate external payments. Useful for detecting accidental double-charges from bank payments.
        </p>

        {/* HOA Selector */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select HOA</label>
            <select
              value={selectedHoa}
              onChange={(e) => setSelectedHoa(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">-- Choose HOA --</option>
              {hoas.map(hoa => (
                <option key={hoa.id} value={hoa.id}>{hoa.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={loadAllPayments}
              disabled={loading || !selectedHoa}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex-1"
            >
              {loading && viewMode === 'all' ? 'Loading...' : '📋 View All Payments'}
            </button>
          </div>
          <div className="flex items-end">
            <button
              onClick={loadDuplicates}
              disabled={loading || !selectedHoa}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed w-full"
            >
              {loading && viewMode === 'duplicates' ? 'Scanning...' : '🔍 Find Duplicates'}
            </button>
          </div>
          <div className="flex items-end">
            <button
              data-testid="reconcile-payments-btn"
              onClick={async () => {
                if (!selectedHoa) { alert('Please select an HOA first'); return; }
                try {
                  setLoading(true);
                  const resp = await axios.post(`/api/platform-admin/reconcile-payments/${selectedHoa}`);
                  const d = resp.data;
                  if (d.recovered > 0) {
                    alert(`Recovered ${d.recovered} payment(s) from Stripe!\n\n${(d.recovered_details || []).map(p => `• $${p.amount.toFixed(2)} — ${p.user_email}`).join('\n')}\n\nRefresh payments to see them.`);
                    await loadAllPayments();
                  } else {
                    alert(d.message || 'All payments are in sync with Stripe.');
                  }
                } catch (err) {
                  console.error('Reconcile error:', err);
                  alert(`Reconciliation failed: ${err.response?.data?.detail || err.message}`);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading || !selectedHoa}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed w-full"
            >
              {loading ? 'Reconciling...' : '🔄 Reconcile Stripe'}
            </button>
          </div>
        </div>

        {/* Stats - All Payments View */}
        {viewMode === 'all' && allPayments.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-900">{allPayments.length}</div>
                <div className="text-sm text-blue-700">Total Payments</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-900">{allPayments.filter(p => p.status === 'completed').length}</div>
                <div className="text-sm text-green-700">Completed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-900">{allPayments.filter(p => p.status === 'pending').length}</div>
                <div className="text-sm text-yellow-700">Pending</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{allPayments.filter(p => p.status === 'refunded').length}</div>
                <div className="text-sm text-gray-700">Refunded</div>
              </div>
            </div>
          </div>
        )}

        {/* Stats - Duplicates View */}
        {viewMode === 'duplicates' && stats && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-orange-900">{stats.total_checked}</div>
                <div className="text-sm text-orange-700">Payments Scanned</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-900">{stats.groups_found}</div>
                <div className="text-sm text-red-700">Duplicate Groups</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">Last 30 Days</div>
                <div className="text-sm text-gray-700">Time Range</div>
              </div>
            </div>
          </div>
        )}

        {/* All Payments View */}
        {viewMode === 'all' && allPayments.length > 0 && (
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">All External Payments ({allPayments.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Property</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allPayments.map((payment) => (
                    <React.Fragment key={payment.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono">{payment.id.substring(0, 12)}...</td>
                        <td className="px-4 py-3 text-sm">{new Date(payment.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-sm">{payment.payer_email}</td>
                        <td className="px-4 py-3 text-sm">{payment.property_identifier || '-'}</td>
                        <td className="px-4 py-3 text-sm font-semibold">${payment.total_amount}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                            payment.status === 'refunded' ? 'bg-gray-100 text-gray-800' :
                            payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            payment.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{payment.payment_method}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setExpandedPayment(expandedPayment === payment.id ? null : payment.id)}
                              className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                            >
                              {expandedPayment === payment.id ? '▼ Hide' : '▶ Details'}
                            </button>
                            <button
                              onClick={() => resendReceipt(payment.id, payment.payer_email)}
                              disabled={resending[payment.id]}
                              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              {resending[payment.id] ? 'Sending...' : '📧'}
                            </button>
                            {payment.status !== 'refunded' && (
                              <button
                                onClick={() => refundPayment(payment.id, {
                                  amount: payment.amount,
                                  email: payment.payer_email
                                })}
                                disabled={refunding[payment.id]}
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                              >
                                {refunding[payment.id] ? 'Refunding...' : '💳'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedPayment === payment.id && (
                        <tr className="bg-blue-50">
                          <td colSpan="8" className="px-4 py-4">
                            <div className="bg-white border border-blue-200 rounded-lg p-4">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <span>📋</span> Complete Payment Details - FOR REFUND DECISIONS
                              </h4>
                              
                              <div className="grid grid-cols-2 gap-4">
                                {/* Left Column */}
                                <div className="space-y-3">
                                  <div className="bg-gray-50 p-3 rounded">
                                    <div className="text-xs text-gray-500 mb-1">TRANSACTION ID (Full)</div>
                                    <div className="text-sm font-mono font-semibold text-gray-900 break-all">{payment.id}</div>
                                  </div>
                                  
                                  <div className="bg-gray-50 p-3 rounded">
                                    <div className="text-xs text-gray-500 mb-1">STRIPE PAYMENT INTENT ID</div>
                                    <div className="text-sm font-mono text-gray-900 break-all">{payment.stripe_payment_intent_id || 'N/A'}</div>
                                    {payment.stripe_payment_intent_id && (
                                      <a 
                                        href={`https://dashboard.stripe.com/payments/${payment.stripe_payment_intent_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 mt-1 inline-block"
                                      >
                                        → View in Stripe Dashboard
                                      </a>
                                    )}
                                  </div>
                                  
                                  <div className="bg-gray-50 p-3 rounded">
                                    <div className="text-xs text-gray-500 mb-1">PAYMENT METHOD</div>
                                    <div className="text-sm font-semibold text-gray-900">{payment.payment_method}</div>
                                    {payment.payment_method === 'ACH' && (
                                      <div className="text-xs text-yellow-700 mt-1">⏱ ACH takes 2-5 business days to clear</div>
                                    )}
                                  </div>
                                  
                                  <div className="bg-gray-50 p-3 rounded">
                                    <div className="text-xs text-gray-500 mb-1">STATUS</div>
                                    <div className="text-sm font-semibold text-gray-900">{payment.status.toUpperCase()}</div>
                                    <div className="text-xs text-gray-600 mt-1">
                                      {payment.status === 'pending' && '⏳ Waiting for bank to clear (ACH)'}
                                      {payment.status === 'processing' && '⚙️ Being processed by Stripe'}
                                      {payment.status === 'completed' && '✅ Funds captured successfully'}
                                      {payment.status === 'refunded' && '↩️ Full refund issued'}
                                    </div>
                                  </div>
                                  
                                  <div className="bg-gray-50 p-3 rounded">
                                    <div className="text-xs text-gray-500 mb-1">CREATED DATE</div>
                                    <div className="text-sm text-gray-900">{new Date(payment.created_at).toLocaleString()}</div>
                                  </div>
                                </div>
                                
                                {/* Right Column */}
                                <div className="space-y-3">
                                  <div className="bg-green-50 border border-green-200 p-3 rounded">
                                    <div className="text-xs text-gray-500 mb-1">CUSTOMER INFO</div>
                                    <div className="text-sm font-semibold text-gray-900">{payment.payer_name || 'N/A'}</div>
                                    <div className="text-sm text-gray-700">{payment.payer_email}</div>
                                    <div className="text-sm text-gray-600 mt-1">Property: {payment.property_identifier || 'Not specified'}</div>
                                  </div>
                                  
                                  <div className="bg-green-50 border border-green-200 p-3 rounded">
                                    <div className="text-xs text-gray-500 mb-1">AMOUNT BREAKDOWN</div>
                                    <div className="space-y-1">
                                      <div className="flex justify-between text-sm">
                                        <span>Amount:</span>
                                        <span className="font-semibold">${payment.amount || payment.total_amount}</span>
                                      </div>
                                      {payment.processing_fee && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                          <span>Processing Fee:</span>
                                          <span>${payment.processing_fee}</span>
                                        </div>
                                      )}
                                      {payment.platform_fee && (
                                        <div className="flex justify-between text-sm text-gray-600">
                                          <span>Platform Fee:</span>
                                          <span>${payment.platform_fee}</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between text-sm font-bold border-t pt-1 mt-1">
                                        <span>Total Paid:</span>
                                        <span>${payment.total_amount}</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="bg-gray-50 p-3 rounded">
                                    <div className="text-xs text-gray-500 mb-1">PAYMENT PURPOSE</div>
                                    <div className="text-sm text-gray-900">{payment.description || payment.memo || 'General Payment'}</div>
                                  </div>
                                  
                                  {payment.metadata && Object.keys(payment.metadata).length > 0 && (
                                    <div className="bg-gray-50 p-3 rounded">
                                      <div className="text-xs text-gray-500 mb-1">ADDITIONAL INFO</div>
                                      <div className="text-xs text-gray-700 space-y-1">
                                        {Object.entries(payment.metadata).map(([key, value]) => (
                                          <div key={key}><span className="font-semibold">{key}:</span> {value}</div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Refund Decision Helper */}
                              <div className="mt-4 bg-yellow-50 border border-yellow-300 rounded p-3">
                                <div className="text-xs font-semibold text-yellow-900 mb-2">⚠️ REFUND DECISION CHECKLIST:</div>
                                <div className="text-xs text-yellow-800 space-y-1">
                                  <div>• Can only refund if status is COMPLETED or PROCESSING</div>
                                  <div>• PENDING ACH payments cannot be refunded until cleared</div>
                                  <div>• Use "View in Stripe Dashboard" link to verify payment details</div>
                                  <div>• Check for duplicate payments before refunding</div>
                                  <div>• Refund will update ledger automatically</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Payments Message */}
        {viewMode === 'all' && allPayments.length === 0 && selectedHoa && !loading && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <div className="text-4xl mb-2">📭</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No Payments Found</h3>
            <p className="text-gray-700">No external payments for {selectedHoaData?.name} yet.</p>
          </div>
        )}

        {/* No Duplicates Message */}
        {viewMode === 'duplicates' && stats && stats.groups_found === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="text-4xl mb-2">✅</div>
            <h3 className="text-lg font-semibold text-green-900 mb-1">No Duplicates Found</h3>
            <p className="text-green-700">All payments look good for {selectedHoaData?.name}!</p>
          </div>
        )}

        {/* Duplicates Table */}
        {duplicates.length > 0 && (
          <div className="space-y-6">
            {duplicates.map((group, idx) => (
              <div key={idx} className="border border-orange-300 rounded-lg overflow-hidden">
                {/* Group Header */}
                <div className="bg-orange-50 px-4 py-3 border-b border-orange-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-orange-900">
                        Duplicate Group #{idx + 1}
                      </h3>
                      <div className="text-sm text-orange-700 mt-1 space-y-1">
                        <div>📧 Payer: <span className="font-mono">{group.payer_email}</span></div>
                        <div>🏠 Property: <span className="font-mono">{group.property_identifier}</span></div>
                        <div>💰 Amount: <span className="font-semibold">${group.primary_payment.amount}</span></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="px-3 py-1 bg-orange-600 text-white rounded-full text-sm font-semibold">
                        {group.duplicate_count + 1} Payments
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payments Table */}
                <div className="bg-white">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time Gap</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Primary Payment */}
                      <tr className="bg-green-50">
                        <td className="px-4 py-3 text-sm font-mono">{group.primary_payment.id.substring(0, 12)}...</td>
                        <td className="px-4 py-3 text-sm">{new Date(group.primary_payment.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            group.primary_payment.status === 'completed' ? 'bg-green-100 text-green-800' : 
                            group.primary_payment.status === 'refunded' ? 'bg-gray-100 text-gray-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {group.primary_payment.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{group.primary_payment.payment_method}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-semibold">
                            ✅ ORIGINAL
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">Keep</td>
                      </tr>

                      {/* Duplicate Payments */}
                      {group.duplicate_payments.map((dup, dupIdx) => (
                        <tr key={dupIdx} className="bg-red-50">
                          <td className="px-4 py-3 text-sm font-mono">{dup.id.substring(0, 12)}...</td>
                          <td className="px-4 py-3 text-sm">{new Date(dup.created_at).toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              dup.status === 'refunded' ? 'bg-gray-100 text-gray-800' :
                              dup.status === 'completed' ? 'bg-red-100 text-red-800' : 
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {dup.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">{dup.payment_method}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                              +{dup.minutes_apart} min
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full font-semibold">
                              ⚠️ DUPLICATE
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {dup.status === 'refunded' ? (
                              <span className="text-xs text-gray-500">Refunded ✓</span>
                            ) : (
                              <button
                                onClick={() => refundPayment(dup.id, {
                                  amount: group.primary_payment.amount,
                                  email: group.payer_email
                                })}
                                disabled={refunding[dup.id]}
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                              >
                                {refunding[dup.id] ? 'Refunding...' : '💳 Refund'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Action Footer */}
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      💡 <strong>Action Required:</strong> Refund duplicate payments in Stripe Dashboard
                    </p>
                    <a
                      href="https://dashboard.stripe.com/payments"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
                    >
                      Open Stripe Dashboard →
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// HOA Smart Pro Admin Tab Component
const AdminSmartProTab = () => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg leading-6 font-medium text-gray-900">HOA Smart Pro Administration</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Manage state statutes, question banks, and document indexing for HOA Smart Pro.
        </p>
      </div>
      <AdminSmartPro />
    </div>
  );
};

// Document Removal Requests Tab Component
const DocumentRemovalTab = () => {
  const [removalRequests, setRemovalRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [adminReason, setAdminReason] = useState('');

  useEffect(() => {
    loadRemovalRequests();
  }, []);

  const loadRemovalRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/platform-admin/document-removal-requests');
      setRemovalRequests(response.data.pending_requests || []);
    } catch (error) {
      console.error('Failed to load removal requests:', error);
      alert('❌ Failed to load document removal requests');
    } finally {
      setLoading(false);
    }
  };

  const approveRemoval = async () => {
    if (!adminReason || adminReason.trim().length < 10) {
      alert('Please provide an admin reason (minimum 10 characters)');
      return;
    }

    try {
      await axios.post('/api/platform-admin/document-removal-requests/approve', {
        document_id: selectedRequest.document_id,
        removal_reason: adminReason
      });
      
      alert(`✅ Document "${selectedRequest.document_name}" has been removed successfully.`);
      setShowApprovalModal(false);
      setSelectedRequest(null);
      setAdminReason('');
      loadRemovalRequests();
    } catch (error) {
      console.error('Failed to approve removal:', error);
      alert('❌ Failed to approve removal request: ' + (error.response?.data?.detail || error.message));
    }
  };

  const rejectRemoval = async (documentId) => {
    if (!window.confirm('Are you sure you want to reject this removal request?')) {
      return;
    }

    try {
      await axios.delete(`/api/platform-admin/document-removal-requests/${documentId}`);
      alert('✅ Removal request rejected');
      loadRemovalRequests();
    } catch (error) {
      console.error('Failed to reject removal:', error);
      alert('❌ Failed to reject removal request');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Document Removal Requests</h3>
              <p className="text-sm text-gray-500 mt-1">
                Review and approve document removal requests from HOAs
              </p>
            </div>
            <button
              onClick={loadRemovalRequests}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading requests...</p>
            </div>
          ) : removalRequests.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No pending document removal requests</p>
            </div>
          ) : (
            <div className="space-y-4">
              {removalRequests.map((request) => (
                <div key={request.document_id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                          PENDING REVIEW
                        </span>
                        <span className="text-sm text-gray-600">
                          {request.hoa_name} ({request.hoa_code})
                        </span>
                      </div>
                      
                      <h4 className="font-medium text-gray-900 text-lg">📄 {request.document_name}</h4>
                      
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="flex items-center text-gray-600">
                          <span className="font-semibold w-32">Document ID:</span>
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{request.document_id}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <span className="font-semibold w-32">Category:</span>
                          <span>{request.category || 'N/A'}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <span className="font-semibold w-32">Uploaded:</span>
                          <span>{new Date(request.uploaded_at).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <span className="font-semibold w-32">Requested By:</span>
                          <span>{request.requested_by || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <span className="font-semibold w-32">Requested:</span>
                          <span>{new Date(request.requested_at).toLocaleDateString()}</span>
                        </div>
                      </div>

                      <div className="mt-3 p-3 bg-gray-50 rounded">
                        <p className="text-sm font-semibold text-gray-700 mb-1">Removal Reason:</p>
                        <p className="text-sm text-gray-900">{request.removal_reason}</p>
                      </div>
                    </div>

                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowApprovalModal(true);
                        }}
                        className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => rejectRemoval(request.document_id)}
                        className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Approve Document Removal
            </h3>
            
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Document:</strong> {selectedRequest.document_name}
              </p>
              <p className="text-sm text-blue-900 mt-1">
                <strong>HOA:</strong> {selectedRequest.hoa_name} ({selectedRequest.hoa_code})
              </p>
              <p className="text-sm text-blue-900 mt-1">
                <strong>User Reason:</strong> {selectedRequest.removal_reason}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Administrator Notes / Reason for Approval *
              </label>
              <textarea
                value={adminReason}
                onChange={(e) => setAdminReason(e.target.value)}
                placeholder="Document administrative reason for approval (e.g., 'Uploaded in error', 'Contains sensitive information', 'Duplicate document', etc.)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="4"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                {adminReason.length} / 10 characters minimum
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg mb-4">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Warning:</strong> This action will mark the document as removed and hide it from the document list. The document record will be kept for audit purposes but will not be accessible to users.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedRequest(null);
                  setAdminReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={approveRemoval}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!adminReason || adminReason.trim().length < 10}
              >
                ✓ Approve Removal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Support Tickets Tab Component
const SupportTicketsTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [responseText, setResponseText] = useState('');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterSource !== 'all') params.append('source', filterSource);
      
      const response = await axios.get(`/api/tickets/admin/all?${params.toString()}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      alert('❌ Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await axios.put(`/api/tickets/${ticketId}/status`, {
        status: newStatus,
        response_text: responseText
      });
      alert(`✅ Ticket status updated to: ${newStatus}`);
      setShowDetailModal(false);
      setSelectedTicket(null);
      setResponseText('');
      loadTickets();
    } catch (error) {
      console.error('Failed to update ticket:', error);
      alert('❌ Failed to update ticket status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || badges.open;
  };

  const getSourceBadge = (source, isPublic) => {
    if (isPublic) return { label: 'Website', color: 'bg-purple-100 text-purple-800' };
    if (source === 'hoa_portal') return { label: 'HOA Portal', color: 'bg-blue-100 text-blue-800' };
    return { label: source, color: 'bg-gray-100 text-gray-800' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Support Tickets</h3>
              <p className="text-sm text-gray-500 mt-1">
                Manage support tickets from website contact form and HOA portals
              </p>
            </div>
            <button
              onClick={loadTickets}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              🔄 Refresh
            </button>
          </div>

          {/* Filters */}
          <div className="mt-4 flex gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); loadTickets(); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Source</label>
              <select
                value={filterSource}
                onChange={(e) => { setFilterSource(e.target.value); loadTickets(); }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Sources</option>
                <option value="website_contact_page">Website Contact</option>
                <option value="hoa_portal">HOA Portal</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading tickets...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No support tickets found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket) => {
                const sourceBadge = getSourceBadge(ticket.source, ticket.is_public_website);
                return (
                  <div key={ticket.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-mono text-gray-600">{ticket.ticket_number}</span>
                          <span className={`px-2 py-1 ${getStatusBadge(ticket.status)} text-xs font-medium rounded`}>
                            {ticket.status.toUpperCase()}
                          </span>
                          <span className={`px-2 py-1 ${sourceBadge.color} text-xs font-medium rounded`}>
                            {sourceBadge.label}
                          </span>
                        </div>
                        
                        <h4 className="font-medium text-gray-900 mb-1">{ticket.subject}</h4>
                        <p className="text-sm text-gray-600 mb-2">{ticket.description?.substring(0, 150)}...</p>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>👤 {ticket.user_name}</span>
                          <span>📧 {ticket.user_email}</span>
                          {ticket.hoa_code && <span>🏘️ {ticket.hoa_code}</span>}
                          <span>🕐 {new Date(ticket.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleViewTicket(ticket)}
                        className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Ticket Details</h3>
                <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Ticket Number:</label>
                  <p className="text-sm text-gray-900">{selectedTicket.ticket_number}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Subject:</label>
                  <p className="text-sm text-gray-900">{selectedTicket.subject}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Description:</label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">From:</label>
                    <p className="text-sm text-gray-900">{selectedTicket.user_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Email:</label>
                    <p className="text-sm text-gray-900">{selectedTicket.user_email}</p>
                  </div>
                </div>

                {selectedTicket.phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Phone:</label>
                    <p className="text-sm text-gray-900">{selectedTicket.phone}</p>
                  </div>
                )}

                {selectedTicket.hoa_code && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">HOA:</label>
                    <p className="text-sm text-gray-900">{selectedTicket.hoa_code}</p>
                  </div>
                )}

                {/* Previous Responses Section */}
                {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Response History:</label>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {selectedTicket.responses.map((response, index) => (
                        <div key={response.id || index} className="bg-gray-50 p-3 rounded-md">
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-xs font-medium text-gray-600">
                              {response.employee_name || 'Staff'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(response.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">{response.response_text}</p>
                          {response.is_internal && (
                            <span className="text-xs text-orange-600 mt-1 inline-block">
                              🔒 Internal Note
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-700">Add New Response (optional):</label>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows="4"
                    placeholder="Add a response to this ticket..."
                  />
                </div>

                <div className="flex gap-2">
                  {selectedTicket.status === 'open' && (
                    <button
                      onClick={() => handleUpdateStatus(selectedTicket.id, 'in_progress')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Mark In Progress
                    </button>
                  )}
                  {(selectedTicket.status === 'open' || selectedTicket.status === 'in_progress') && (
                    <button
                      onClick={() => handleUpdateStatus(selectedTicket.id, 'resolved')}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Mark Resolved
                    </button>
                  )}
                  {selectedTicket.status === 'resolved' && (
                    <button
                      onClick={() => handleUpdateStatus(selectedTicket.id, 'closed')}
                      className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                      Close Ticket
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// E-Voting Audit Tab Component
const EVotingAuditTab = ({ hoas }) => {
  const [selectedHoa, setSelectedHoa] = useState('');
  const [ballotId, setBallotId] = useState('');
  const [propertyHash, setPropertyHash] = useState('');
  const [auditResult, setAuditResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAudit = async (e) => {
    e.preventDefault();
    setError('');
    setAuditResult(null);
    setLoading(true);

    try {
      const response = await api.post('/api/vote/admin/audit-ballot', {
        hoa_id: selectedHoa,
        ballot_id: ballotId,
        property_hash: propertyHash
      });
      setAuditResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to retrieve ballot. Both ballot_id and property_hash must match exactly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">🗳️ E-Voting Ballot Audit</h2>
        <p className="text-sm text-gray-600 mb-6">
          Platform admin only: Retrieve and audit a specific ballot for compliance verification.
          Requires both ballot ID and property hash for security.
        </p>

        <form onSubmit={handleAudit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select HOA</label>
            <select
              value={selectedHoa}
              onChange={(e) => setSelectedHoa(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select HOA --</option>
              {hoas.map((hoa) => (
                <option key={hoa.id} value={hoa.id}>
                  {hoa.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ballot ID</label>
              <input
                type="text"
                value={ballotId}
                onChange={(e) => setBallotId(e.target.value)}
                placeholder="Enter ballot ID"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Hash</label>
              <input
                type="text"
                value={propertyHash}
                onChange={(e) => setPropertyHash(e.target.value)}
                placeholder="Enter property hash"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold"
          >
            {loading ? 'Retrieving Ballot...' : '🔍 Audit Ballot'}
          </button>
        </form>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">❌ {error}</p>
          </div>
        )}
      </div>

      {auditResult && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">✅ Ballot Retrieved Successfully</h3>

          {/* Ballot Metadata */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 mb-3">📋 Ballot Information</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="font-medium">Ballot ID:</span> {auditResult.ballot_id}</div>
              <div><span className="font-medium">Property Hash:</span> {auditResult.property_hash}</div>
              <div><span className="font-medium">Cast At:</span> {new Date(auditResult.cast_at).toLocaleString()}</div>
              <div><span className="font-medium">Election:</span> {auditResult.election.title}</div>
            </div>
          </div>

          {/* Voter Info */}
          {auditResult.voter_info && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-purple-900 mb-3">👤 Voter Information</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="font-medium">Name:</span> {auditResult.voter_info.name}</div>
                <div><span className="font-medium">Email:</span> {auditResult.voter_info.email}</div>
                <div><span className="font-medium">Property ID:</span> {auditResult.voter_info.property_identifier}</div>
                <div><span className="font-medium">Address:</span> {auditResult.voter_info.property_address}</div>
              </div>
            </div>
          )}

          {/* Proxy Info */}
          {auditResult.proxy_info && auditResult.proxy_info.is_proxy && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-orange-900 mb-3">🤝 Proxy Vote Information</h4>
              <p className="text-sm">
                This ballot was cast by <strong>{auditResult.proxy_info.proxy_holder}</strong> on behalf of the voter.
              </p>
            </div>
          )}

          {/* Candidate Votes */}
          {auditResult.candidate_votes.length > 0 && (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-green-900 mb-3">🏆 Candidate Votes</h4>
              <div className="space-y-2">
                {auditResult.candidate_votes.map((vote, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border border-green-200">
                    <div className="text-sm"><span className="font-medium">{vote.position}:</span> <span className="text-green-700 font-semibold">{vote.voted_for}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Topic Votes */}
          {auditResult.topic_votes.length > 0 && (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-yellow-900 mb-3">📋 Motion/Question Votes</h4>
              <div className="space-y-2">
                {auditResult.topic_votes.map((vote, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border border-yellow-200">
                    <div className="text-sm font-medium mb-1">{vote.question}</div>
                    <div className="text-sm text-yellow-700 font-semibold">Answer: {vote.answer}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Security Notice */}
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
            <p className="text-xs text-gray-700">
              🔒 <strong>Audit Security:</strong> This ballot was retrieved using both ballot_id and property_hash verification. 
              All votes remain anonymous to the public. This audit is logged for compliance purposes.
            </p>
          </div>
        </div>
      )}

      {/* Ballot Audit - Reopen Volunteer Access */}
      <div className="bg-white rounded-lg shadow p-6 border-2 border-orange-300">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <span>🔓</span> Reopen Volunteer Access
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          If a valid ballot correction ticket is received, temporarily reopen a volunteer's access 
          to update their counts. All HOA members will be notified via email.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Volunteer Page Link <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="https://app.com/poll-volunteer/abc-123-xyz"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              id="volunteer-link-reopen"
            />
            <p className="text-xs text-gray-600 mt-1">
              Paste the volunteer page URL (will extract access token)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reopen Duration
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              id="reopen-hours-select"
            >
              <option value="1">1 hour</option>
              <option value="2">2 hours</option>
              <option value="4">4 hours</option>
              <option value="8">8 hours</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Reopening <span className="text-red-500">*</span>
            </label>
            <textarea
              placeholder="E.g., Ballot recount request received with valid documentation from homeowner John Doe. Correcting ballot count discrepancy identified in audit."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 h-32"
              id="reopen-reason-text"
            />
          </div>

          <button
            onClick={async () => {
              const linkInput = document.getElementById('volunteer-link-reopen');
              const hoursSelect = document.getElementById('reopen-hours-select');
              const reasonInput = document.getElementById('reopen-reason-text');
              
              const link = linkInput.value.trim();
              const hours = parseInt(hoursSelect.value);
              const reason = reasonInput.value.trim();
              
              if (!link) {
                alert('Please enter volunteer page link');
                return;
              }
              
              if (!reason) {
                alert('Please provide a detailed reason for reopening');
                return;
              }
              
              // Extract access token from URL
              const match = link.match(/poll-volunteer\/([^/?]+)/);
              if (!match) {
                alert('Invalid volunteer page link. Could not extract access token.');
                return;
              }
              
              const accessToken = match[1];
              
              if (!window.confirm(
                `Reopen volunteer access for ${hours} hour(s)?\n\n` +
                `This will:\n` +
                `✅ Allow volunteer to edit ballot counts again\n` +
                `📧 Send email notification to ALL HOA members\n` +
                `📝 Record audit trail of this action\n` +
                `🔒 Auto-lock after ${hours} hour(s)\n\n` +
                `Reason: ${reason.substring(0, 100)}...\n\n` +
                `Continue?`
              )) {
                return;
              }
              
              try {
                const response = await api.post('/api/vote/platform-admin/reopen-volunteer-access', {
                  volunteer_id: accessToken,
                  reopen_hours: hours,
                  reason: reason
                });
                
                const expiryDate = new Date(response.data.new_expiry);
                alert(
                  `✅ Volunteer Access Reopened!\n\n` +
                  `📧 Notified: ${response.data.members_notified} HOA members\n` +
                  `⏰ Valid Until: ${expiryDate.toLocaleString()}\n` +
                  `🔓 Access Token: ${accessToken.substring(0, 8)}...`
                );
                
                // Clear form
                linkInput.value = '';
                reasonInput.value = '';
              } catch (error) {
                console.error('Error reopening access:', error);
                alert('❌ Failed to reopen access:\n\n' + (error.response?.data?.detail || error.message));
              }
            }}
            className="w-full bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
          >
            <span>🔓</span> Approve & Reopen Access (Notify HOA)
          </button>
        </div>

        <div className="mt-4 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
          <p className="text-sm text-yellow-900 mb-2">
            <strong>⚠️ Important - Use Only for Legitimate Corrections:</strong>
          </p>
          <ul className="text-xs text-yellow-800 space-y-1 ml-4">
            <li>• Requires valid documentation (ticket, email, signed request)</li>
            <li>• ALL HOA members will receive email notification with reason</li>
            <li>• Creates permanent audit trail in system logs</li>
            <li>• Volunteer's edits after reopening are tracked separately</li>
            <li>• Access automatically expires after specified duration</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminDashboard;